/**
 * Logic module for the Conference Room Designer.
 * This module contains functions for managing and resetting area states.
 * @module conferenceRoomDesignerLogic
 */
import initialState from "toolConstants/conferenceroomDesigner/initialState";
import constants from "./constants";
import { ceil, find, findIndex, has, isEmpty, indexOf, keys, max, mergeWith, omit, round, some } from "lodash";
import equipmentCatalog from "globalConstants/EquipmentCatalog";
import { current } from "@reduxjs/toolkit";
import { matchBundle, calculateAdditionalEquipment, optimizeBundleSelection } from "utilities/bundleMatching";
import { calcMaxTilesFit, closest, side, snapDownToIncrement } from "utilities/mathematic";
import { getOverlaps, calculateCoordinates } from "utilities/geometric";
import { maxStandaloneAmpPortsFor, evaluateAmpSwap, planCeilingAmplification, CEILING_CHAIN_MAX_MICS, TABLE_CHAIN_MAX_MICS, UCX_SPEAKER_CAPS, getUcxSpeakerGroup, expansionSpeakerCaps } from "toolConstants/conferenceroomDesigner/treeFrogPorts";
import { matchTreeFrogBundle, isTreeFrogAndroidUC, isDevioUcxDesign } from "utilities/treeFrogBundleMatching";
import { familyMicModelName } from "utilities/micFamily";
import { getBma360dAmpConfig, getBma360dWallSpeakerAmpConfig, ONBOARD_AMP_CAPACITY, AMP_450BP_CAPACITY } from "toolConstants/conferenceroomDesigner/bma360dAmp";
import { getBma360dProcessors, matchBma360dBundle, getCoverageBoxLongest, getAreaCeilingHeight } from "toolConstants/conferenceroomDesigner/bma360dDsp";

/**
 * Resets an area to its initial state, with the option to reset specific parameters.
 * 
 * @function
 * @param {Object} container - The container object holding all areas.
 * @param {string|null} [parameter=null] - Specific parameter to reset. If null, resets all parameters.
 * 
 * @description
 * This function resets the active area in the container to its initial state.
 * It preserves the current ceiling tile selection while resetting other ceiling tile properties.
 * Other area properties may be reset based on the implementation (not shown in the provided code).
 * 
 * @example
 * // Reset all parameters of the active area
 * resetArea(containerState);
 * 
 * // Reset only a specific parameter (e.g., 'table')
 * resetArea(containerState, 'table');
 */
export const resetArea = (container, parameter = null, value = null) => {
  const activeArea = container.areas[container.activeTab];
  // Early return if no active area
  if (!activeArea) {
    console.error('No active area found');
    return;
  }
  
  const initState = initialState.area();
  // Don't mutate initState - we'll create new objects as needed

  // Safely extract properties with defaults
  const {
    table = { shape: 'rectangle' },
    microphones = { model: 'tcm_x' },
    speakers = { model: 'ceilingspkr' },
    customized = { microphones: false, speakers: false, cameras: false },
    dimensions = activeArea.dimensions || initState.dimensions
  } = activeArea || {};

  const { shape = 'rectangle' } = table || {};
  // Extract models but replace empty strings with defaults
  const { model: extractedMicModel = 'tcm_x' } = microphones || {};
  const { model: extractedSpkrModel = 'ceilingspkr' } = speakers || {};
  // Ensure models are never empty strings
  const rawMicModel = (extractedMicModel && extractedMicModel !== '') ? extractedMicModel : 'tcm_x';
  const spkrModel = (extractedSpkrModel && extractedSpkrModel !== '') ? extractedSpkrModel : 'ceilingspkr';
  const { microphones: isMicCustom = false, speakers: isSpkrCustom = false, cameras: isCameraCustom = false } = customized;

  // Migrate legacy model keys to product-keyed config names
  const micConfigs = constants.equipment.microphones;
  const { BAR, CEILING_MOUNTED, PENDANT, TABLETOP, HAS_CAMERA, CEILING_TILE } = constants.equipment.micCategories;
  const migrateMicKey = (m, mName) => {
    if (!m) return m;
    if (micConfigs[m]) return m;
    const resolved = Object.keys(micConfigs).find(key => micConfigs[key].modelName === mName);
    if (!resolved) {
      console.warn(`[migrateMicKey] Could not resolve mic model "${m}" (modelName: "${mName}") — preserving original key`);
    }
    return resolved ?? m;
  };
  const micModel = migrateMicKey(rawMicModel, microphones?.modelName);

  // Safely get coverage functions with fallbacks
  const micCoverage = constants.equipment?.microphones?.[micModel]?.coverage || (() => ({ focused: false, full_room: true }));
  const spkrCoverage = constants.equipment?.speakers?.[spkrModel]?.coverage || (() => ({ focused: true, full_room: false }))

  // Replace arrays entirely instead of merging by index
  const customizer = (objValue, srcValue) => {
    if (Array.isArray(srcValue)) {
      return srcValue;
    }
  };

  // Reset devices helper function - now works with Immer properly
  const resetDevices = (resetMics = true, resetSpkrs = true, resetCameras = true, changes = {}, preserveLayouts = false, forceReset = false) => {
    // forceReset is used for dimension changes - clear all devices to force recalculation
    
    // Determine final models and modelNames before merging
    
    const rawFinalMicModel = resetMics ?
      ((changes.microphones?.model && changes.microphones.model !== '') ? changes.microphones.model :
       (activeArea.microphones?.model && activeArea.microphones.model !== '') ? activeArea.microphones.model :
       micModel || 'tcm_x') : null;
    const finalMicModel = rawFinalMicModel
      ? migrateMicKey(rawFinalMicModel, changes.microphones?.modelName ?? activeArea.microphones?.modelName)
      : null;

    const finalMicModelName = resetMics ?
      (changes.microphones?.modelName ||
       activeArea.microphones?.modelName ||
       micConfigs[finalMicModel]?.modelName ||
       'Parlé TCM-X') : null;
        
    
    const finalSpkrModel = resetSpkrs ?
      ((changes.speakers?.model && changes.speakers.model !== '') ? changes.speakers.model :
       (activeArea.speakers?.model && activeArea.speakers.model !== '') ? activeArea.speakers.model :
       spkrModel || 'ceilingspkr') : null;
    
    const finalSpkrModelName = resetSpkrs ?
      (changes.speakers?.modelName ||
       activeArea.speakers?.modelName ||
       (finalSpkrModel === 'ceilingspkr' ? 'Desono C-IC6' :
        finalSpkrModel === 'pendantspkr' ? 'Desono P6-SM' :
        finalSpkrModel === 'wallspkr' ? 'Desono DX-S5' :
        finalSpkrModel === 'ceilingspkr_lp' ? 'Desono C-IC6LP' : 'Desono C-IC6')) : null;
    
    // Update through container state to work with Immer properly
    const currentArea = container.areas[container.activeTab];
    
    if (resetMics) {
      const shouldResetMicLayout = ((!isMicCustom && resetMics) || forceReset) && !preserveLayouts;

      // Determine final qty and layout values
      // For dimension changes (forceReset), preserve the existing qty and let middleware recalculate
      // Only reset to 0 for actual device resets (not dimension changes)
      const hasExistingLayout = currentArea.microphones?.layout?.length > 0;
      const isDimensionReset = forceReset; // forceReset is true when called from dimension changes

      // Check if we're switching between different microphone types (ceiling <-> bar, etc.)
      // Use _oldModel from changes if available (passed from Redux reducer), otherwise fall back to current model
      const oldModelFromChanges = changes.microphones?._oldModel;
      const currentMicModelRaw = oldModelFromChanges || currentArea.microphones?.model || micModel;
      const currentMicKey = migrateMicKey(currentMicModelRaw, currentArea.microphones?.modelName);
      const newMicKey = finalMicModel; // already migrated above

      // Determine if the type is actually changing (e.g., ceiling -> bar, pendant -> ceiling)
      const isTypeChanging = currentMicKey !== newMicKey;

      // Don't preserve qty when switching between different microphone types
      const shouldPreserveQty = !isTypeChanging && (hasExistingLayout || isDimensionReset);

      // For dimension changes, preserve qty and clear layout to trigger recalculation
      // For other resets, use the normal reset logic
      // IMPORTANT: When type is changing, always reset qty to 0 to force recalculation
      const micQty = changes.microphones?.qty !== undefined ? changes.microphones.qty :
                     (isTypeChanging ? 0 :  // Force recalculation when switching mic types
                      (isDimensionReset ? (currentArea.microphones?.qty || 0) :
                       (shouldResetMicLayout && !shouldPreserveQty ? 0 : (currentArea.microphones?.qty || 0))));
      const micLayout = changes.microphones?.layout !== undefined ? changes.microphones.layout :
                        (shouldResetMicLayout ? [] : (currentArea.microphones?.layout || []));

      // Preserve plot data during reset if layout/qty haven't changed (optimization)
      const existingMicPlotData = currentArea.microphones?.plotData;
      const micLayoutChanged = micLayout !== currentArea.microphones?.layout;
      const shouldPreserveMicPlotData = existingMicPlotData && !isEmpty(existingMicPlotData) && !micLayoutChanged;

      // When switching microphone types, reset height to prevent stale values
      // This prevents bar microphone heights from being used with ceiling mics
      const shouldResetHeight = isTypeChanging && !changes.microphones?.height;

      currentArea.microphones = {
        ...currentArea.microphones,
        plotData: shouldPreserveMicPlotData ? existingMicPlotData : {},
        coverage: micCoverage(activeArea),
        // Apply changes
        ...changes.microphones,
        // Ensure model and modelName are always set
        model: finalMicModel,
        modelName: finalMicModelName,
        // Set qty and layout with final values
        qty: micQty,
        layout: micLayout,
        // Reset height when switching types (unless explicitly provided in changes)
        ...(shouldResetHeight && { height: undefined })
      };
      
    }
    
    if (resetSpkrs) {
      const shouldResetSpkrLayout = ((!isSpkrCustom && resetSpkrs) || forceReset) && !preserveLayouts;

      // For dimension changes (forceReset), preserve the existing qty and let middleware recalculate
      // Only reset to 0 for actual device resets (not dimension changes)
      const hasExistingSpkrLayout = currentArea.speakers?.layout?.length > 0;
      const isDimensionReset = forceReset; // forceReset is true when called from dimension changes

      // Check if we're switching between different speaker types (ceiling <-> bar, etc.)
      // Use _oldModel from changes if available (passed from Redux reducer), otherwise fall back to current model
      const oldSpkrModelFromChanges = changes.speakers?._oldModel;
      const currentSpkrModel = oldSpkrModelFromChanges || currentArea.speakers?.model || spkrModel;
      const currentSpkrType = constants.equipment?.speakers?.[currentSpkrModel] || {};
      const newSpkrType = constants.equipment?.speakers?.[finalSpkrModel] || {};

      // Determine if the type is actually changing (e.g., ceiling -> bar or bar -> ceiling)
      const isSpkrTypeChanging = (currentSpkrType.isBarSpkr !== newSpkrType.isBarSpkr);

      // Don't preserve qty when switching between different speaker types
      const shouldPreserveSpkrQty = !isSpkrTypeChanging && (hasExistingSpkrLayout || isDimensionReset);

      const spkrQty = changes.speakers?.qty !== undefined ? changes.speakers.qty :
                      (isDimensionReset ? (currentArea.speakers?.qty || 0) :
                       (shouldResetSpkrLayout && !shouldPreserveSpkrQty ? 0 : (currentArea.speakers?.qty || 0)));
      const spkrLayout = changes.speakers?.layout !== undefined ? changes.speakers.layout :
                         (shouldResetSpkrLayout ? [] : (currentArea.speakers?.layout || []));

      // Preserve HearSpace plot data during reset if it exists
      const existingSpeakerPlotData = currentArea.speakers?.plotData;
      const shouldPreservePlotData = existingSpeakerPlotData && existingSpeakerPlotData._fromHearSpace;

      currentArea.speakers = {
        ...currentArea.speakers,
        plotData: shouldPreservePlotData ? existingSpeakerPlotData : {},
        coverage: spkrCoverage(activeArea),
        // Apply changes
        ...changes.speakers,
        // Ensure model and modelName are always set
        model: finalSpkrModel,
        modelName: finalSpkrModelName,
        // Set qty and layout with final values
        qty: spkrQty,
        layout: spkrLayout
      };
      
    }
    
    if (resetCameras) {

      // Check if camera is enabled and valid
      const isCameraEnabled = changes.cameras?.hasCamera !== undefined ? changes.cameras.hasCamera : currentArea.cameras?.hasCamera;
      const cameraModel = changes.cameras?.model || currentArea.cameras?.model;
      const hasValidCamera = isCameraEnabled && cameraModel && cameraModel !== 'none';

      // Get camera equipment configuration to ensure we have correct coverage values
      const cameraEquipment = hasValidCamera ? constants.equipment?.cameras?.[cameraModel] : null;
      let coverageRadius = changes.cameras?.coverageRadius || currentArea.cameras?.coverageRadius;
      let coverageAngle = changes.cameras?.coverageAngle || currentArea.cameras?.coverageAngle;

      // If camera equipment exists and we don't have coverage values, get them from equipment config
      if (cameraEquipment && (!coverageRadius || !coverageAngle)) {
        if (cameraEquipment.dualImager) {
          // For dual-imager cameras, use wideImager as default
          coverageRadius = cameraEquipment.wideImager?.coverageRadius || coverageRadius;
          coverageAngle = cameraEquipment.wideImager?.coverageAngle || coverageAngle;
        } else {
          // For single-imager cameras, use top-level values
          coverageRadius = cameraEquipment.coverageRadius || coverageRadius;
          coverageAngle = cameraEquipment.coverageAngle || coverageAngle;
        }
      }

      // For enabled cameras being reset, use the layout from changes if provided
      // For disabled cameras, set qty:0 and layout:[]
      if (hasValidCamera && (forceReset || (!isCameraCustom && resetCameras))) {
        // Camera is enabled and being reset
        // When forceReset is true (dimension changes), keep qty but clear layout for recalculation
        // This ensures the middleware will recalculate the camera position for the new room dimensions
        currentArea.cameras = {
          // Preserve model and settings
          model: cameraModel,
          modelName: changes.cameras?.modelName || currentArea.cameras?.modelName || '',
          hasCamera: isCameraEnabled,
          // Set coverage properties from equipment config or preserved values
          coverageRadius: coverageRadius,
          coverageAngle: coverageAngle,
          // For dimension changes (forceReset), keep qty but clear layout to trigger recalculation
          qty: changes.cameras?.qty !== undefined ? changes.cameras.qty :
               (forceReset && !preserveLayouts ? 1 : 1),
          // Clear layout when forceReset to allow middleware to recalculate position
          layout: changes.cameras?.layout !== undefined ? changes.cameras.layout :
                  (forceReset && !preserveLayouts ? [] : []),
          // Only clear plotData if we're actually changing layout to force recalculation
          plotData: (forceReset && !preserveLayouts) ? {} : (currentArea.cameras?.plotData || {}),
          // Preserve plotVisibility if not being changed; default to wide_only for dual-imager cameras
          plotVisibility: changes.cameras?.plotVisibility || currentArea.cameras?.plotVisibility ||
            (cameraEquipment?.dualImager ? { wide_only: true, zoom_only: false } : undefined),
          // Apply any other changes but preserve plotData if not forcing reset
          ...(changes.cameras ? omit(changes.cameras, 'plotData') : {})
        };
      } else {
        // Camera is disabled or not being fully reset
        currentArea.cameras = {
          // Preserve existing values including plotVisibility
          ...currentArea.cameras,
          // Only clear plot data if camera is being disabled
          plotData: (!hasValidCamera) ? {} : (currentArea.cameras?.plotData || {}),
          // Set to empty if camera is disabled
          ...((!hasValidCamera) ? { qty: 0, layout: [] } : {}),

          // Apply changes but preserve plotData for enabled cameras
          ...(changes.cameras ? omit(changes.cameras, hasValidCamera ? 'plotData' : null) : {})
        };
      }
      
    }
    
    // Update customization flags - these should only be reset if the device was actually reset
    if (resetMics) currentArea.customized.microphones = false;
    if (resetSpkrs) currentArea.customized.speakers = false;
    if (resetCameras) currentArea.customized.cameras = false;
  };

  // Non device reseters 
  switch (parameter) {
    case 'customizations':
      // Only reset customization flags - DO NOT touch device layouts at all
      const currentArea = container.areas[container.activeTab];
      if (currentArea && currentArea.customized) {
        currentArea.customized = {
          microphones: false,
          speakers: false,
          cameras: false
        };
      }
      // Don't call resetDevices - we want to keep all current device layouts
      // The devices should remain exactly as they are, we're only clearing the "customized" flags
      break;
      
    case 'dimensions':
      // Reset ceiling tiles when room dimensions change but recalculate quantities
      const currentAreaDims = container.areas[container.activeTab];
      const { tile } = currentAreaDims.ceilingTiles;
      const ceilingTiles = initState.ceilingTiles;
      
      // If tiles were active, recalculate the quantity for the new room size
      if (tile && tile !== "no_ceiling_tiles") {
        const tileDimensions = constants.ceilingTiles[tile];
        if (tileDimensions && tileDimensions.x > 0 && tileDimensions.y > 0) {
          const { length, width } = currentAreaDims.dimensions;
          const newQtyX = calcMaxTilesFit(length, tileDimensions.x);
          const newQtyY = calcMaxTilesFit(width, tileDimensions.y);
          
          currentAreaDims.ceilingTiles = { 
            ...ceilingTiles, 
            tile: tile,
            qty: { x: newQtyX, y: newQtyY },
            transform: { x: 0, y: 0, a: 0 }, // Reset to centered position
            checked: [] // Clear any checked tiles as they won't align with new grid
          };
        } else {
          currentAreaDims.ceilingTiles = { ...ceilingTiles, tile: tile };
        }
      } else {
        currentAreaDims.ceilingTiles = { ...ceilingTiles, tile: tile };
      }

      // Reset ceiling microphone height when room dimensions change.
      // Skip table mics — their height is fixed to talkerHeight, not ceiling height.
      if (currentAreaDims.microphones) {
        const dimMicKey = migrateMicKey(currentAreaDims.microphones.model, currentAreaDims.microphones.modelName);
        if (!TABLETOP.has(dimMicKey)) {
          // CEILING_MOUNTED (tcm_x/dcm_x/CBC 2500 bars): use the model's own canonical
          // height() formula, same as the 'acoustics' case above — each model knows its own
          // rule (tcm_x/dcm_x flush unless acoustics-capped; CBC bars ceiling−0.5' clamped
          // 6'-12'; BMA360D capped at its 20' install max).
          if (CEILING_MOUNTED.has(dimMicKey)) {
            const dimMicConfig = constants.equipment.microphones[dimMicKey];
            currentAreaDims.microphones.height = dimMicConfig?.height
              ? dimMicConfig.height(currentAreaDims)
              : currentAreaDims.ceilingHeight.min;
          } else {
            // Other non-tabletop types (e.g. PENDANT) keep their prior behavior — out of
            // scope for this fix, which only targets the ceiling-mount clearance formula.
            currentAreaDims.microphones.height = currentAreaDims.ceilingHeight.min;
          }
        }
      }

      // clear all expansion mic state on dimension change. The previous
      // placement (gap-aware coords, custom click, or manual-opt-in grid) was tied to
      // the OLD room geometry — any of those layouts could land off-canvas or in
      // nonsensical positions after a resize. Reset to the initial-state shape so
      // the next recalc / auto-recommendation re-evaluates from scratch for the new room.
      // Skipped under ceilingHeight (the fall-through below) because vertical changes
      // don't invalidate horizontal placement.
      currentAreaDims.expansionMicType = null;
      currentAreaDims.expansionMicRecommended = false;
      currentAreaDims.expansionMicCoverage = null;
      currentAreaDims.expansionMicrophones = null;
      currentAreaDims.expansionMicManualOptIn = false;
      if (currentAreaDims.customized) currentAreaDims.customized.expansionMicrophones = false;

      // Same reasoning for expansion speakers — they're independent of expansion mics
      // (the auto-coupling was removed), but the dimension invalidation is symmetric.
      // `calculateBarSpeakerCoverageGap` re-fires on the setDimensions recalc trigger
      // and re-recommends if the new room still has a speaker gap.
      // NB: No `customized.expansionSpeakers` flag to reset — only expansion MICs have
      // a customised flag today (`customized.expansionMicrophones`). If a future change
      // introduces a customised flag for expansion speakers, mirror the mic cleanup above.
      currentAreaDims.expansionSpeakerType = null;
      currentAreaDims.expansionSpeakerRecommended = false;
      currentAreaDims.expansionSpeakerCoverage = null;
      currentAreaDims.expansionSpeakers = null;

    // Intentional fall through
    case 'ceilingHeight':
      // When dimensions or ceiling height change, reset microphones and speakers
      // For cameras, we need to force recalculation since room coordinates changed
      // Camera positions and aim points are calculated based on room dimensions
      resetDevices(true, true, true, {}, false, true);
      break;

    case 'table':
      if (Boolean(shape)) {
        // Reset devices but ensure models are preserved for recalculation
        const preserveMicModel = migrateMicKey(activeArea.microphones?.model, activeArea.microphones?.modelName);
        const preserveMicModelName = activeArea.microphones?.modelName ||
          micConfigs[preserveMicModel]?.modelName || 'Parlé TCM-X';
           
        const preserveSpkrModel = activeArea.speakers?.model || 'ceilingspkr';
        const preserveSpkrModelName = activeArea.speakers?.modelName || 
          (preserveSpkrModel === 'ceilingspkr' ? 'Desono C-IC6' : 
           preserveSpkrModel === 'ceilingspkr_lp' ? 'Desono C-IC6LP' : 'Desono C-IC6');
           
        // For table changes, also preserve cameras that have valid layout data
        // This handles the case where cameras were positioned but not explicitly customized
        const hasValidCameraLayout = activeArea.cameras?.layout?.length > 0;
        const shouldPreserveCameras = isCameraCustom || hasValidCameraLayout;
               
        resetDevices(!isMicCustom, !isSpkrCustom, !shouldPreserveCameras, {
          microphones: { model: preserveMicModel, modelName: preserveMicModelName },
          speakers: { model: preserveSpkrModel, modelName: preserveSpkrModelName }
        });
      } else if (!isSpkrCustom) {
        // PIG-290: removing the table (shape is now null) must revert auto speaker coverage to the
        // no-table default. Only the add branch above recomputed coverage; the removal path did
        // nothing, so speaker coverage stayed 'focused' from when the table was present — the focused
        // box is smaller than full-room, so the recalc placed ~2 fewer speakers ("missing speakers").
        // Mic coverage already reverts through its own path; mirror that for speakers here. The
        // speaker-model coverage fn derives purely from table presence, so with the table gone this
        // resolves to full-room, and the next recalc (setTable is a recalc trigger) repopulates the
        // full-room speaker count.
        activeArea.speakers.coverage = spkrCoverage(activeArea);
      }
      break;

    case 'ceilingTiles':
      resetDevices(!isMicCustom, !isSpkrCustom, isCameraCustom)
      break;
      
    case 'acoustics':
      // For acoustics changes, adjust ceiling mic height immediately in reducer
      // This ensures calculateDevicesMiddleware sees the correct height
      const currentMicModel = activeArea.microphones?.model;
      const currentMicModelKey = migrateMicKey(currentMicModel, activeArea.microphones?.modelName);

      // For ceiling microphones, update height immediately if needed.
      // Ceiling-TILE mics (BMA360D) are excluded from the corrected branch below: their own
      // height() (constants.js) is just `min(20' spec cap, ceiling)` — no acoustics-aware
      // lowering at all, by design (PIG-295/PIG-429: `calculateDevicesMiddleware.
      // maybeAutoLowerBma360d` owns BMA360D's acoustics-driven auto-lower with a spec-precise
      // target; validation.js's e1 is likewise exempted for BMA360D to avoid a second,
      // slightly-off auto-lower and duplicate toast). So BMA360D keeps its own literal-baseline
      // + poor-acoustics-only lowering here, unchanged, rather than routing through
      // micConfig.height() the way the true CEILING_MOUNTED puck mics (tcm_x) and CBC bars now do.
      const isCeilingTileMic = CEILING_TILE?.has(currentMicModelKey) ?? false;

      if (isCeilingTileMic) {
        const micConfig = constants.equipment.microphones[currentMicModelKey];
        if (micConfig && micConfig.micData) {
          const acousticsIndex = indexOf(constants.acoustics, activeArea.acoustics);
          const micDataValue = micConfig.micData[acousticsIndex];
          const talkerHeight = constants.area.talkerHeight;
          const optimalMax = round(side(micDataValue, 1) + talkerHeight, 4);
          const ceilingHeight = activeArea.ceilingHeight.min;

          const baselineHeight = ceilingHeight;
          activeArea.microphones.height = baselineHeight;
          activeArea.microphones.maxHeight = optimalMax;

          if (activeArea.acoustics === 'poor' && baselineHeight > optimalMax && ceilingHeight > optimalMax) {
            const poleIncrement = 0.1524; // 0.5 feet
            const snappedMax = snapDownToIncrement(optimalMax, poleIncrement);
            activeArea.microphones.height = Math.round(snappedMax * 10000) / 10000;
          }
        }
      } else if (CEILING_MOUNTED.has(currentMicModelKey)) {
        // Get mic configuration
        const micConfig = constants.equipment.microphones[currentMicModelKey];

        if (micConfig && micConfig.micData) {
          // Calculate optimal max height for new acoustics (still needed for
          // validation.js's maxHeight sync-check below, independent of the height itself)
          const acousticsIndex = indexOf(constants.acoustics, activeArea.acoustics);
          const micDataValue = micConfig.micData[acousticsIndex];
          const talkerHeight = constants.area.talkerHeight;
          const optimalMax = round(side(micDataValue, 1) + talkerHeight, 4);

          // PIG-536: re-derive the mount height from `micConfig.height(activeArea)` — the SAME
          // canonical formula setMicrophones (room.ts) and every other creation/reset path use —
          // on EVERY acoustics change, so the result is idempotent (Good → Poor → Good returns
          // to the same height) AND consistent with every other call site.
          //
          // Each model owns its own rule, so this branch must not hand-roll one:
          //   - tcm_x / dcm_x  → computeCeilingMicHeightInfo: flush with the ceiling, lowered
          //                      only when the acoustics-optimal cap requires it (and floored to
          //                      a 0.5' pole increment when it does). Applies to every acoustics
          //                      tier, so no separate poor-acoustics branch belongs here.
          //   - CBC 2500 bars  → calculateCBCHeight: ceiling − 0.5', clamped 6'-12'.
          // PENDANT and BAR mics in this same switch already call their own height() the same
          // way (see the PENDANT branch below, and defMic.height(area) in room.ts's
          // setMicrophones for BAR).
          if (typeof micConfig.height === 'function') {
            activeArea.microphones.height = micConfig.height(activeArea);
          }

          // Update maxHeight for validation
          activeArea.microphones.maxHeight = optimalMax;
        }
      } else if (PENDANT.has(currentMicModelKey)) {
        // PIG-290 (mirrors the PIG-536 ceiling-mic branch above for pendant): re-derive the pendant
        // hang height AND its acoustics-driven maxHeight from a clean baseline on EVERY acoustics
        // change, so the height is idempotent. Previously poor acoustics lowered the pendant (via the
        // validation maxHeight clamp) but improving acoustics never restored it — it stayed stuck low,
        // shrinking the coverage radius and dropping the mic count on Good → Poor → Good once a table
        // was present. Setting maxHeight here to the same value validation computes means validation
        // sees no change and does not re-dispatch a setMicrophones({maxHeight}) that would otherwise
        // re-clamp the restored height back down against the stale (lower) range.
        const pendantConfig = constants.equipment.microphones[currentMicModelKey];
        if (pendantConfig && pendantConfig.micData) {
          const acousticsIndex = indexOf(constants.acoustics, activeArea.acoustics);
          const micDataValue = pendantConfig.micData[acousticsIndex];
          const talkerHeight = constants.area.talkerHeight;
          const optimalMax = round(side(micDataValue, 1) + talkerHeight, 4);
          const baselineHeight = typeof pendantConfig.height === 'function'
            ? pendantConfig.height(activeArea)
            : activeArea.ceilingHeight.min;
          activeArea.microphones.height = baselineHeight;
          activeArea.microphones.maxHeight = optimalMax;

          // For poor acoustics, lower the hang height to the optimal max (snapped to the same 0.5 ft
          // pole increment the ceiling branch and setMicrophones reducer use) when the baseline hangs
          // above optimal — otherwise keep the full baseline height.
          if (activeArea.acoustics === 'poor' && baselineHeight > optimalMax) {
            // Snap DOWN — see the matching comment in the ceiling-mic branch above for why
            // a max constraint must never be rounded up. (For every pendant micData tier the
            // floor and nearest increment happen to coincide, so this is behaviour-neutral
            // today; it is flooring for consistency and to stay correct if micData changes.)
            const poleIncrement = 0.1524; // 0.5 feet
            const snappedMax = snapDownToIncrement(optimalMax, poleIncrement);
            activeArea.microphones.height = Math.round(snappedMax * 10000) / 10000;
          }
        }
      }

      // Ceiling EXPANSION mic (dcm_x): independent of the primary-mic branches above, since
      // expansion mics only exist alongside a BAR primary (never CEILING_MOUNTED/PENDANT) —
      // so the checks above never touch it. Mirrors the corrected primary ceiling-mic branch
      // above: derive height from `dcmConfig.height(activeArea)` — the same canonical formula
      // used everywhere else a dcm_x default height is set (setExpansionMicType,
      // placeExpansionMicAtLocation, the ceiling-height sync in setExpansionMicrophones) — so
      // this stays idempotent AND consistent. That formula mounts the mic flush with the ceiling
      // and applies the acoustics-optimal cap for every tier. Table expansion mics (ttm_x)
      // don't have an adjustable height, so this only applies when the ceiling type is active.
      if (activeArea.expansionMicrophones && activeArea.expansionMicrophones.model === 'dcm_x') {
        const dcmConfig = constants.equipment.microphones.dcm_x;
        if (dcmConfig && typeof dcmConfig.height === 'function') {
          const newExpansionHeight = dcmConfig.height(activeArea);

          activeArea.expansionMicrophones = {
            ...activeArea.expansionMicrophones,
            height: newExpansionHeight,
            layout: (activeArea.expansionMicrophones.layout ?? []).map((m) => ({
              ...m,
              location: { ...m.location, z: newExpansionHeight },
            })),
          };

          // Informational toast when the NEW acoustics tier (this branch only runs on an
          // acoustics change) is why this mic isn't mounted flush with the ceiling. The height
          // is correct from the moment it's set above, so there's no later validation.js
          // "adjusting height" correction to carry this information on for this specific case
          // (see `computeCeilingMicHeightInfo`, constants.js, and the matching comment in
          // room.ts's setExpansionMicType). `container` (not `activeArea`) is where the toast
          // middleware reads it from.
          if (typeof dcmConfig.isAcousticsLimited === 'function' && dcmConfig.isAcousticsLimited(activeArea)) {
            container._toastMessage = 'Ceiling expansion microphone is too high to pick up any talkers at given room acoustics. Adjusting microphone height to {height}.';
            container._toastHeightMeters = newExpansionHeight;
            container._toastSeverity = 'warning';
          }
        }
      }

      // Don't call resetDevices - calculateDevicesMiddleware will handle recalculation
      // with the updated height, preserving quantities and plot data
      break;

    case 'microphones':
      if (!value) {
        // When called from undo button with no value, reset microphones to defaults
        // Force reset to ensure devices are cleared and recalculated
        // Preserve model and modelName when resetting
        const preserveModel = migrateMicKey(activeArea.microphones?.model, activeArea.microphones?.modelName);
        const preserveModelName = activeArea.microphones?.modelName ||
          micConfigs[preserveModel]?.modelName || 'Parlé TCM-X';

        // Bar devices (VBC 2500/2800) are a single physical unit: mic + speaker + camera.
        // Undo should reset all three together so they land at the same recalculated position.
        const isBarMic = BAR.has(preserveModel);

        // A manual move/add/delete stashes the pre-edit `coverageDensity` on
        // `lastCoverageDensity` (see clearPrimaryCoverage in room.ts), because — unlike
        // `coverage` — the density slider's value genuinely gets overwritten by the very
        // next recalculation once the device is customised. Restoring it here (including
        // its `jumpPoints`) lets `resolveCeilingMicDensity` recognise the geometry hasn't
        // changed and honour the old slider pick instead of resetting to the default.
        const lastMicDensity = activeArea.microphones?.lastCoverageDensity;

        resetDevices(true, isBarMic, isBarMic, {
          microphones: {
            model: preserveModel,
            modelName: preserveModelName,
            qty: 0,
            // A manual move/add/delete sets `hideCoverageToggle` (see clearPrimaryCoverage
            // in room.ts) without touching `coverage` itself, so clearing the flag here is
            // all undo needs to do — the toggle reappears showing the value it already had.
            hideCoverageToggle: false,
            ...(lastMicDensity && { coverageDensity: lastMicDensity })
          },
          // Bar devices reset speakers together with the mic (resetSpkrs === isBarMic above).
          // Force-clear the speaker heatmap so it disappears immediately on undo instead of
          // showing the stale pre-undo SPL plot until HearSpace's debounced recalculation
          // finishes (see the matching clear in the 'speakers' undo branch below).
          ...(isBarMic && { speakers: { plotData: {} } })
        }, false, true);

        // Undo in microphones view also clears expansion mic customisation so the
        // next recalc repopulates the expansion layout from the algorithm. Mirrors
        // the primary reset's semantics — preserves expansionMicType / coverage /
        // manualOptIn (the user's intentional choices), wipes only the manual edits.
        const undoMicArea = container.areas[container.activeTab];
        if (undoMicArea.customized) undoMicArea.customized.expansionMicrophones = false;
        if (undoMicArea.expansionMicrophones) {
          undoMicArea.expansionMicrophones.layout = [];
          undoMicArea.expansionMicrophones.qty = 0;
        }
        // Restore the expansion "Focus/Full" toggle the same way, if a manual
        // move/add/delete had hidden it.
        if (undoMicArea.expansionMicCoverageLastKnown != null) {
          undoMicArea.expansionMicCoverage = undoMicArea.expansionMicCoverageLastKnown;
          undoMicArea.expansionMicCoverageLastKnown = undefined;
        }
        if (undoMicArea.microphones) undoMicArea.microphones.lastCoverageDensity = undefined;

        break;
      }
      let { model = null, modelName = null } = value;
      // modelName-only dispatch (bar sub-model switch): resolve product key so model switch logic runs
      if (!model && modelName) {
        model = Object.keys(micConfigs).find(k => micConfigs[k].modelName === modelName) ?? null;
      }
      if (model) {
        const changes = {};
        const modelKey = migrateMicKey(model, modelName);
        const isCeilingMic = CEILING_MOUNTED.has(modelKey);
        const isPendantMic = PENDANT.has(modelKey);
        const isBarMic     = BAR.has(modelKey);
        const isVideoBar   = HAS_CAMERA.has(modelKey);

        // Detect if we're switching between different microphone types
        let isTypeSwitching = false;
        let oldModelKey = null;
        if (value._oldModel) {
          oldModelKey = value._oldModel;
          isTypeSwitching = oldModelKey !== modelKey;
        }

        // When switching to ceiling mics, default to ceiling-mounted height (at ceiling, no poles)
        // For pendant/table/bar mics, use the calculated default height based on acoustics
        // If explicit height provided, use that instead
        // NOTE: CBC 2500 CM/PM are also CEILING_MOUNTED (isCeilingMic), but unlike TCM-X/DCM-X
        // they are NOT flush-mounted at the ceiling — they require ceiling - 0.5' clearance,
        // clamped to 6'-12' (see calculateCBCHeight). Excluding isBarMic here ensures CBC bars
        // always go through their own height() function instead of being pinned to raw
        // ceilingHeight.min.
        let defaultHeight = value.height !== undefined
          ? value.height
          : (isTypeSwitching && isCeilingMic && !isPendantMic && !isBarMic)
            ? (activeArea.ceilingHeight.min || activeArea.ceilingHeight)  // Ceiling-mounted: at ceiling height
            : constants.equipment.microphones[modelKey]?.height?.(activeArea) ?? activeArea.ceilingHeight.min;

        // If switching to ceiling mic with poor acoustics, apply height adjustment
        if (isTypeSwitching && isCeilingMic && !isPendantMic && activeArea.acoustics === 'poor') {
          const micConfig = constants.equipment.microphones[modelKey];
          if (micConfig && micConfig.micData) {
            const acousticsIndex = indexOf(constants.acoustics, activeArea.acoustics);
            const micDataValue = micConfig.micData[acousticsIndex];
            const talkerHeight = constants.area.talkerHeight;
            const optimalMax = round(side(micDataValue, 1) + talkerHeight, 4);
            const ceilingHeight = activeArea.ceilingHeight.min || activeArea.ceilingHeight;

            // Only lower if ceiling height exceeds optimal for poor acoustics
            if (ceilingHeight > optimalMax) {
              // Snap DOWN — see the matching comment in the 'acoustics' resetArea case above
              // for why a max constraint must never be rounded up.
              const poleIncrement = 0.1524; // 0.5 feet
              const snappedMax = snapDownToIncrement(optimalMax, poleIncrement);
              // Fix floating-point precision
              defaultHeight = Math.round(snappedMax * 10000) / 10000;
            }
          }
        }

        changes.microphones = {
          model: model,  // CRITICAL: Include the new model so resetDevices can detect type changes
          height: defaultHeight,
          heightRange: constants.equipment.microphones[modelKey]?.heightRange?.(activeArea),
          coverage: constants.equipment.microphones[modelKey]?.coverage?.(activeArea),
          // IMPORTANT: Preserve modelName if provided to prevent equipment list failures
          // When switching TO bar with no explicit modelName, default to first item in dropdown
          // Always set modelName from config when switching types so stale names don't carry over.
          // If an explicit modelName was passed (bar sub-model switch), use that.
          modelName: modelName || constants.equipment.microphones[modelKey].modelName,
          // Preserve _oldModel from Redux reducer so resetDevices can detect type changes
          ...(value._oldModel && { _oldModel: value._oldModel })
        }

        // Current speaker
        const speakerModel = activeArea.speakers.model;
        const { speakers: { [speakerModel]: { isBarSpkr = false } } } = constants.equipment;
        // Bug fix: only force a speaker migration when the mic is actually entering or
        // leaving bar territory. Wall and pendant speakers are valid standalone choices
        // for ceiling/pendant/tabletop mics — the old condition below also matched
        // `isWallSpkr || isPendantSpkr`, so switching e.g. ceiling -> pendant mic (or any
        // non-bar -> non-bar change) silently reset a deliberately-chosen wall or pendant
        // speaker back to the ceiling default. Use the real previous mic model
        // (oldModelKey, from _oldModel) to detect a genuine bar departure; fall back to
        // the speaker's own isBarSpkr flag only when the old model isn't known (e.g. a
        // direct resetArea call without _oldModel), since 'barspkr' is only ever valid
        // alongside a bar mic and is always safe to migrate away from.
        const wasBarMic = oldModelKey ? BAR.has(oldModelKey) : isBarSpkr;
        const newSpeakerModel = (isBarMic && !isBarSpkr) ? 'barspkr' : (!isBarMic && wasBarMic) ? 'ceilingspkr' : speakerModel;
        const newSpeakerEquipment = constants.equipment.speakers[newSpeakerModel];
        const deviceHeight = newSpeakerEquipment.height(activeArea);
        const deviceCoverage = newSpeakerEquipment.coverage(activeArea);
        if (newSpeakerModel !== speakerModel || (isSpkrCustom && isBarSpkr)) {
          changes.speakers = {
            model: newSpeakerModel,
            modelName: newSpeakerEquipment.modelName,
            mountingType: newSpeakerEquipment.mountingType,
            height: deviceHeight,
            coverage: deviceCoverage,
            _oldModel: speakerModel  // Capture old speaker model for type change detection
          };
        } else if (isTypeSwitching) {
          // Mic type changed but speaker model stayed the same — still recalculate coverage
          // so speaker count uses the correct coverage box (focused vs full_room)
          changes.speakers = { coverage: deviceCoverage };
        };
        // current camera
        const cameraModel = activeArea.cameras.model;
        const { cameras: { [cameraModel]: { isBarCamera } = { isBarCamera: false } } } = constants.equipment;
        // Determine which bar camera to use based on mic config's cameraKey
        let newCameraModel = cameraModel;
        if (isVideoBar && !isBarCamera) {
          // Switching to a video bar - use the cameraKey from mic config
          newCameraModel = micConfigs[modelKey]?.cameraKey ?? 'barCamera';
        } else if (isBarMic && !isVideoBar && !['vidi250', 'vidi280'].includes(cameraModel)) {
          // Switching to a non-VBC bar: default to vidi280 unless a standalone camera is already selected
          newCameraModel = 'vidi280';
        }
        if (newCameraModel !== cameraModel) {
          changes.cameras = { model: newCameraModel, hasCamera: true };
        };

        resetDevices(true, 'speakers' in changes, 'cameras' in changes, changes)

      } else if (has(value, 'coverage') && !has(value, 'height')) {
        // Reset devices when coverage changes to recalculate microphone placement
        // IMPORTANT: Pass the new coverage value in changes so it's applied before selector runs
        // If the mic is custom, preserve the layout — getDeviceLayouts will use the existing positions
        if (!isMicCustom) {
          resetDevices(true, false, false, { microphones: { coverage: value.coverage } }, false, true);
        }
      } else if (has(value, 'height')) {
        // Height changes are already handled by setMicrophones reducer
        // Don't reset devices - just let the height update flow through
        // The reducer updates height in existing layout
      };
      break;

    case 'speakers':
      if (!value) {
        // When called from undo button with no value, reset speakers to defaults
        // Force reset to ensure devices are cleared and recalculated

        // Check if we're dealing with bar speakers (they're combined with microphones)
        const spkrModel = activeArea.speakers?.model || 'ceilingspkr';
        const { speakers: { [spkrModel]: { isBarSpkr = false } = {} } = {} } = constants.equipment;

        if (isBarSpkr) {
          // For bar speakers, reset both microphones and speakers together
          container.areas[container.activeTab].customized.microphones = false;
          container.areas[container.activeTab].customized.speakers = false;

          // For video bars (VBC 2500a, VBC 2800), camera is part of the bar unit — reset it too
          const currentBarMicKey = migrateMicKey(activeArea.microphones?.model, activeArea.microphones?.modelName);
          const isVideoBarMic = HAS_CAMERA.has(currentBarMicKey);
          if (isVideoBarMic) {
            container.areas[container.activeTab].customized.cameras = false;
          }

          // Force-clear the speaker heatmap so it disappears immediately on undo instead
          // of showing the stale pre-undo SPL plot until HearSpace's debounced
          // recalculation finishes (resetDevices otherwise preserves HearSpace-sourced
          // plotData across resets — see shouldPreservePlotData below).
          resetDevices(true, true, isVideoBarMic, { speakers: { plotData: {} } }, false, true);
        } else {
          // For non-bar speakers, reset only speakers
          // Preserve model and modelName when resetting
          const preserveSpkrModel = activeArea.speakers?.model || 'ceilingspkr';
          const preserveSpkrModelName = activeArea.speakers?.modelName ||
            (preserveSpkrModel === 'ceilingspkr' ? 'Desono C-IC6' :
             preserveSpkrModel === 'ceilingspkr_lp' ? 'Desono C-IC6LP' : 'Desono C-IC6');

          resetDevices(false, true, false, {
            speakers: {
              model: preserveSpkrModel,
              modelName: preserveSpkrModelName,
              qty: 0,
              // A manual move/add/delete sets `hideCoverageToggle` (see clearPrimaryCoverage
              // in room.ts) without touching `coverage` itself, so clearing the flag here is
              // all undo needs to do — the toggle reappears showing the value it already had.
              hideCoverageToggle: false,
              // Force-clear the heatmap on undo — see comment in the isBarSpkr branch above.
              plotData: {}
            }
          }, false, true);
        }

        // Undo in speakers view also clears expansion speaker customisation so the
        // next recalc repopulates the expansion layout from the algorithm. Mirrors
        // the primary reset's semantics — preserves expansionSpeakerType / coverage,
        // wipes only the manual edits. Runs for both bar and non-bar branches above.
        const undoSpkrArea = container.areas[container.activeTab];
        if (undoSpkrArea.customized) undoSpkrArea.customized.expansionSpeakers = false;
        if (undoSpkrArea.expansionSpeakers) {
          undoSpkrArea.expansionSpeakers.layout = [];
          undoSpkrArea.expansionSpeakers.qty = 0;
        }
        // Restore the expansion "Focus/Full" toggle the same way, if a manual
        // move/add/delete had hidden it.
        if (undoSpkrArea.expansionSpeakerCoverageLastKnown != null) {
          undoSpkrArea.expansionSpeakerCoverage = undoSpkrArea.expansionSpeakerCoverageLastKnown;
          undoSpkrArea.expansionSpeakerCoverageLastKnown = undefined;
        }
      } else {
        const { model: speakerModel = null, mountingType = null, modelName = null } = value;
        
        // Handle mounting type change
        if (mountingType) {
          const changes = { speakers: { mountingType } };
          
          // Set appropriate default model based on mounting type
          if (mountingType === 'ceiling') {
            changes.speakers.model = 'ceilingspkr';
            changes.speakers.modelName = 'Desono C-IC6 (units)';
          } else if (mountingType === 'pendant') {
            changes.speakers.model = 'pendantspkr';
            changes.speakers.modelName = 'Parlé P6-SM';
          } else if (mountingType === 'wall') {
            // Set wall speaker model to trigger automatic wall speaker generation
            changes.speakers.model = 'wallspkr';
            changes.speakers.modelName = 'Desono DX-S5';
          }
          
          // Get speaker properties for the model
          const speakerEquipment = constants.equipment.speakers[changes.speakers.model];
          if (speakerEquipment) {
            changes.speakers.height = speakerEquipment.height(activeArea);
            if (speakerEquipment.heightRange) {
              changes.speakers.heightRange = speakerEquipment.heightRange(activeArea);
            }
            changes.speakers.coverage = speakerEquipment.coverage(activeArea);
          }
          
          resetDevices(false, true, false, changes);
        }
        // Handle model name change (for ceiling speakers)
        else if (modelName) {
          const changes = { 
            speakers: { 
              modelName,
              // Keep the same model type for calculation purposes
              model: activeArea.speakers?.model || 'ceilingspkr'
            } 
          };
          
          // Trigger recalculation with new model name
          resetDevices(false, true, false, changes);
        }
        // Handle legacy model change
        else if (speakerModel) {
          const changes = {};
          // Calculate new speaker properties based on model change
          const deviceHeight = constants.equipment.speakers[speakerModel].height(activeArea);
          const deviceHeightRange = constants.equipment.speakers[speakerModel].heightRange ? constants.equipment.speakers[speakerModel].heightRange(activeArea) : undefined;
          const deviceCoverage = constants.equipment.speakers[speakerModel].coverage(activeArea);
          
          changes.speakers = {
            model: speakerModel,
            height: deviceHeight,
            ...(deviceHeightRange && { heightRange: deviceHeightRange }),
            coverage: deviceCoverage
          };
          
          resetDevices(false, true, false, changes);
        } else if (has(value, 'coverage')) {
          // Reset devices when coverage changes to recalculate speaker placement
          // Use forceReset=true to ensure recalculation even if speakers are customized
          // IMPORTANT: Pass the new coverage value in changes so it's applied before selector runs
          resetDevices(false, true, false, { speakers: { coverage: value.coverage } }, false, true);
        } else if (has(value, 'height')) {
          // Height changes are already handled by setSpeakers reducer
          // Don't reset devices - just let the height update flow through
          // The reducer updates height in existing layout and recalculates aiming angles
        }
      }
      break;

    case 'cameras':
      // Handle camera reset from undo button
      if (!value) {
        // When called from undo button with no value, reset cameras to defaults
        // Get current area reference
        const currentAreaCam = container.areas[container.activeTab];
        
        // Preserve camera model and settings if camera is enabled
        const preserveCameraModel = activeArea.cameras?.model || 'none';
        const preserveCameraModelName = activeArea.cameras?.modelName || '';
        const preserveHasCamera = activeArea.cameras?.hasCamera || false;

        const isBarCamera = constants.equipment?.cameras?.[preserveCameraModel]?.isBarCamera || false;

        if (isBarCamera) {
          // Bar devices (VBC 2500/2800) are a single physical unit: mic + speaker + camera.
          // Undo should reset all three together so they land at the same recalculated position.
          const preserveMicModel = migrateMicKey(activeArea.microphones?.model, activeArea.microphones?.modelName);
          const preserveMicModelName = activeArea.microphones?.modelName ||
            micConfigs[preserveMicModel]?.modelName || 'Parlé TCM-X';
          currentAreaCam.customized.microphones = false;
          currentAreaCam.customized.speakers = false;
          currentAreaCam.customized.cameras = false;
          resetDevices(true, true, true, {
            microphones: { model: preserveMicModel, modelName: preserveMicModelName }
          }, false, true);
        } else if (preserveHasCamera && preserveCameraModel !== 'none') {
          // Standalone camera: clear customization and place at room center for middleware to recalculate
          currentAreaCam.customized.cameras = false;
          const dimensions = activeArea.dimensions || { length: 10, width: 10 };
          const defaultCamera = {
            location: { x: dimensions.length / 2, y: dimensions.width / 2 },
            modelName: preserveCameraModelName
          };
          resetDevices(false, false, true, {
            cameras: {
              model: preserveCameraModel,
              modelName: preserveCameraModelName,
              hasCamera: preserveHasCamera,
              qty: 1,
              layout: [defaultCamera],
              plotData: undefined
            }
          }, false, false);
        } else {
          // Camera is disabled, clear everything
          currentAreaCam.customized.cameras = false;
          resetDevices(false, false, true, {
            cameras: {
              model: preserveCameraModel,
              modelName: preserveCameraModelName,
              hasCamera: preserveHasCamera
            }
          }, false, false);
        }
      } else {
        // Handle specific camera property changes
        const changes = { cameras: value };
        
        // Check if only plotVisibility is changing - if so, don't reset camera position
        const isOnlyPlotVisibilityChange = Object.keys(value).length === 1 && 'plotVisibility' in value;
        
        if (isOnlyPlotVisibilityChange) {
          // Only update plotVisibility without resetting camera layout
          const currentArea = container.areas[container.activeTab];
          currentArea.cameras = {
            ...currentArea.cameras,
            plotVisibility: value.plotVisibility
          };
        } else {
          // Other camera changes should trigger reset
          resetDevices(false, false, true, changes);
        }
      }
      break;

    default:
      // Handle full reset when parameter is true (from Reset button)
      if (parameter === true) {
        // Complete reset - replace everything with initState, but preserve id and name
        // so that dict lookups via area.id (e.g. reportDocument closures) stay valid.
        container.areas[container.activeTab] = { ...initState, id: activeArea.id, name: activeArea.name };
      }
      // Check if parameter is a device type (called from undo button for specific device)
      // Note: cameras are now handled in their own case above
      else if (parameter === 'microphones' || parameter === 'speakers') {
        // Only reset the specific device type that was passed
        // This happens when undo is clicked from a device-specific view

          container.areas[container.activeTab].customized[parameter] = false;
        
        // Reset only the specified device type
        if (parameter === 'microphones' && isMicCustom) {
          resetDevices(true, false, false);
        } else if (parameter === 'speakers' && isSpkrCustom) {
          resetDevices(false, true, false);
        }
      } else if (value && typeof value !== 'string') {
        // Check if we're dealing with bar devices that need to be reset together
        const defaultCaseMicKey = migrateMicKey(activeArea.microphones?.model, activeArea.microphones?.modelName);
        const spkrModel = activeArea.speakers?.model || 'ceilingspkr';
        const cameraModel = activeArea.cameras?.model || 'none';

        const isBarMic = BAR.has(defaultCaseMicKey);
        const { speakers: { [spkrModel]: { isBarSpkr = false } = {} } = {} } = constants.equipment;
        const { cameras: { [cameraModel || 'dummy']: { isBarCamera = false } = {} } = {} } = constants.equipment;

        // For bar devices, reset all related components together
        // Exception: ABC 2500a uses standalone Vidi camera which should be independent
        const isABCWithStandaloneCamera = defaultCaseMicKey === 'abc_2500a' && cameraModel === 'vidi250';

        if ((isBarMic || isBarSpkr || isBarCamera) && !isABCWithStandaloneCamera) {
          // When changing bar device configurations, preserve existing layouts if they exist
          resetDevices(true, true, true, {}, true);
        } else if (isABCWithStandaloneCamera && value === 'cameras') {
          resetDevices(false, false, true, {}, true);
        } else {
          resetDevices(value === 'microphones', value === 'speakers', value === 'cameras', {}, true);
        }
      } else if (parameter === 'full') {
        // When doing a full reset (undo button), only reset customizations
        // Preserve all current settings including devices, furniture, dimensions
        container.areas[container.activeTab].customized = {
          microphones: false,
          speakers: false,
          cameras: false
        };
        // Only reset added devices if they were customized
        if (isMicCustom) {
          resetDevices(true, false, false);
        }
        if (isSpkrCustom) {
          resetDevices(false, true, false);
        }
        if (isCameraCustom) {
          resetDevices(false, false, true);
        }
      } else if (value === null && parameter !== 'microphones' && parameter !== 'speakers' && parameter !== 'cameras') {
        // Complete reset - replace everything with initState
        // Only do this if parameter is not a device type
        container.areas[container.activeTab] = initState;
      }
      // If value is a string (device type indicator), do nothing - don't reset devices
  };

};

/**
 * Calculates required TCM-X mounting accessories.
 * @function
 * @param {number} mountingDistance - Distance between ceiling and microphone in meters
 * @param {number} micCount - Number of TCM-X microphones requiring mounting accessories
 * @returns {Array<Object>} Array of mounting accessories with quantities
 *
 * @description
 * Calculates TCM-X mounting accessories based on the following rules:
 * - Minimum mounting height: 0.5ft (0.15m)
 * - Distance <= 3'5" (1.042m): Add TCM-X-PM per microphone
 * - Distance > 3'5" (1.042m): Add TCM-X-PM per microphone + PMA-2000-PE extensions as needed
 * - PMA-2000-PE count = ceil((distance - 1.042m) / 0.9144m) per microphone
 */
const calculateTCMXAccessories = (mountingDistance, micCount = 1) => {
  // Minimum mounting height check (0.5ft / 0.15m)
  const MIN_MOUNTING_HEIGHT = 0.15; // 0.5 feet in meters
  if (mountingDistance < MIN_MOUNTING_HEIGHT || micCount <= 0) {
    return [];
  }

  const poleAccessories = equipmentCatalog.PoleAccessories || [];
  const accessories = [];

  // Get TCM-X base pole and extensions
  const tcmxBasePole = poleAccessories.find(acc => acc.type === 'tcm_x_base_pole');
  const tcmxExtension = poleAccessories.find(acc => acc.type === 'tcm_x_extension');

  if (!tcmxBasePole) {
    console.warn('TCM-X base pole (TCM-X-PM) not found in equipment catalog');
    return [];
  }

  const BASE_MAX_DISTANCE = 1.042; // 3'5" in meters
  const EXTENSION_LENGTH = 0.9144; // 3' in meters

  // Always add TCM-X-PM base mounting kit (one per microphone)
  accessories.push({
    modelName: tcmxBasePole.modelName,
    partNumber: tcmxBasePole.partNumber,
    description: tcmxBasePole.description,
    quantity: micCount
  });

  // If distance exceeds base pole maximum, add extensions
  if (mountingDistance > BASE_MAX_DISTANCE) {
    if (!tcmxExtension) {
      console.warn('TCM-X extension (PMA-2000-PE) not found in equipment catalog');
    } else {
      const remainingDistance = mountingDistance - BASE_MAX_DISTANCE;
      const extensionsPerMic = Math.ceil(remainingDistance / EXTENSION_LENGTH);
      const totalExtensions = extensionsPerMic * micCount;

      if (totalExtensions > 0) {
        accessories.push({
          modelName: tcmxExtension.modelName,
          partNumber: tcmxExtension.partNumber,
          description: tcmxExtension.description,
          quantity: totalExtensions
        });
      }
    }
  }

  return accessories;
};

/**
 * Calculates required pole accessories for ceiling microphone mounting.
 * @function
 * @param {number} mountingDistance - Distance between ceiling and microphone in meters
 * @param {number} micCount - Number of microphones requiring pole accessories
 * @returns {Array<Object>} Array of pole accessories with quantities
 *
 * @description
 * Calculates the optimal combination of pole sections to achieve the desired mounting distance.
 * Prioritizes fewer longer sections as specified in requirements.
 * Returns accessories with modelName, partNumber, description, and quantity.
 */
const calculatePoleAccessories = (mountingDistance, micCount = 1) => {
  if (mountingDistance <= 0.01 || micCount <= 0) {
    return [];
  }

  const poleAccessories = equipmentCatalog.PoleAccessories || [];
  const accessories = [];

  // Get base pole and extensions, sorted by length (descending for optimal combinations)
  const basePole = poleAccessories.find(acc => acc.type === 'base_pole');
  const extensions = poleAccessories
    .filter(acc => acc.type === 'extension')
    .sort((a, b) => b.length - a.length); // Sort descending to prefer longer sections

  let remainingDistance = mountingDistance;

  // Always start with base pole (minimum 0.5ft required)
  if (basePole && remainingDistance >= basePole.length) {
    accessories.push({
      modelName: basePole.modelName,
      partNumber: basePole.partNumber,
      description: basePole.description,
      quantity: micCount
    });
    remainingDistance -= basePole.length;
  }

  // Add extensions in descending size order for optimal combination
  for (const extension of extensions) {
    const neededQuantity = Math.floor(remainingDistance / extension.length);
    if (neededQuantity > 0) {
      accessories.push({
        modelName: extension.modelName,
        partNumber: extension.partNumber,
        description: extension.description,
        quantity: neededQuantity * micCount
      });
      remainingDistance -= neededQuantity * extension.length;
    }
  }

  // Handle remaining distance that's smaller than smallest extension by using multiple base poles
  if (remainingDistance > 0.01 && basePole) {
    const additionalBasePoles = Math.ceil(remainingDistance / basePole.length);

    // Check if we already have base poles in accessories, if so update quantity
    const existingBasePole = accessories.find(acc => acc.modelName === basePole.modelName);
    if (existingBasePole) {
      existingBasePole.quantity += additionalBasePoles * micCount;
    } else {
      accessories.push({
        modelName: basePole.modelName,
        partNumber: basePole.partNumber,
        description: basePole.description,
        quantity: additionalBasePoles * micCount
      });
    }
  }

  return accessories;
};

/**
* @function calculateEquipmentTable
* @description Calculates comprehensive equipment list for conference room system.
* 
* @param {Array<Object>} areas - Array of area objects (currently uses first area)
* @returns {Array<Object>} Array of equipment entries with quantities and details
* 
* @description
* This function calculates equipment needs based on room configuration:
* 
* 1. Conferencing Bar Systems:
*    - Handles all-in-one bar systems
*    - Includes associated accessories
* 
* 2. DSP-Based Systems:
*    - Calculates DSP requirements
*    - Determines microphone configurations:
*      * Base units and expanders
*      * Ceiling, pendant, and tabletop variations
*    - Handles speaker requirements:
*      * Amplification needs
*      * Speaker distribution
*    - Network infrastructure:
*      * TC-5 or AVB switch requirements
*      * Port calculations
*    - Camera integration
*    - Accessories and mounting solutions
* 
* 3. Cabling Requirements:
*    - Calculates different cable lengths needed
*    - Accounts for included cables in bundles
*    - Optimizes cable distribution
* 
* @example
* const equipmentList = calculateEquipmentTable([{
*   hasBar: false,
*   microphones: { modelName: 'TCM-X', layout: { length: 4 }},
*   speakers: { modelName: 'PM4-IC', layout: { length: 6 }},
*   cameras: { modelName: 'CAM-USB', layout: { length: 1 }},
*   productFamily: 'Parlé'
* }]);
* 
* @note
* - Currently processes single area (first in array)
* - Uses equipment catalog for product specifications
* - Handles both all-in-one and component-based systems
* - Includes sophisticated port and cable calculations
* - Accounts for different mounting and installation scenarios
*/
export const calculateEquipmentTable = (areas) => {
  const area = areas[0]
  if (!area) {
    console.warn('calculateEquipmentTable: No area data provided');
    return {};
  }
  
  const {
    hasBar = false,
    microphones = {},
    speakers = {},
    cameras = {},
    productFamily = ''
  } = area;

  // Tree Frog (Devio) design — single source of truth for branches that select
  // the DPA-450BP amplifier and apply the UCC peripheral port budget. Hoisted to
  // function scope so each amp-related branch reads the same value.
  const isTreeFrog = productFamily === 'Devio';

  // Debug: Log the actual microphones object received
  
  // Safely extract properties with defaults
  const { modelName: micModel = '', layout: micLayout = [], qty: micQtyProp = 0, height: micHeight, model: micModelType } = microphones;
  const { modelName: spkrModel = '', layout: spkrLayout = [], qty: spkrQtyProp = 0 } = speakers;
  const { modelName: cameraModel = '', layout: cameraLayout = [], qty: cameraQtyProp = 0 } = cameras;
  
  // Get quantities - use layout length if available (most accurate), otherwise use qty property
  // Layout length is the actual deployed devices, qty might be the target/calculated value
  // If qty is 0 but coverage is set, use a default quantity for estimation
  let micQty = Array.isArray(micLayout) && micLayout.length > 0 ? micLayout.length : micQtyProp;
  let spkrQty = Array.isArray(spkrLayout) && spkrLayout.length > 0 ? spkrLayout.length : spkrQtyProp;
  let cameraQty = Array.isArray(cameraLayout) && cameraLayout.length > 0 ? cameraLayout.length : cameraQtyProp;
  
  // If no devices are configured but coverage is set, estimate default quantities
  // This handles the case where a room is configured but devices haven't been calculated yet
  if (micQty === 0 && microphones.coverage && (microphones.coverage.full_room || microphones.coverage.focused)) {
    // Estimate based on room size and coverage - minimum 1 microphone for a configured room
    micQty = 1;
  }
  if (spkrQty === 0 && speakers.coverage && (speakers.coverage.full_room || speakers.coverage.focused)) {
    // Estimate based on room size and coverage - minimum 2 speakers for a configured room
    spkrQty = 2;
  }
  if (cameraQty === 0 && cameras.hasCamera && cameras.model !== 'none') {
    // If camera is enabled but not placed, assume 1 camera
    cameraQty = 1;
  }
  
  
  // If micModel is empty but we have microphones with a model, extract from layout
  let finalMicModel = micModel;
  if (!finalMicModel && micLayout && micLayout.length > 0) {
    finalMicModel = micLayout[0].modelName || '';
  }
  
  // Validate required fields - use defaults if missing to prevent equipment list failure
  // Only use defaults if we truly have no model name (preserve custom settings like ABC 2500a)
  if (!finalMicModel) {
    // Default to pendant mic for poor acoustics, ceiling mic otherwise
    finalMicModel = area.acoustics === 'poor' ? 'Parlé TCM-1' : 'Parlé TCM-X';
    console.warn('calculateEquipmentTable: Using default microphone model', {
      defaultModel: finalMicModel,
      acoustics: area.acoustics,
      originalMicModel: micModel
    });
  }

  // Resolve the mic to its correct product family ONCE, here, so every downstream
  // consumer (bundle matching, the à-la-carte and bundle-overflow mic lines, catalog
  // lookup, mounting accessories) reads one consistent value instead of re-deriving it
  // inside product-family/platform branches. UCX-only Devio DCM-X / DTM-X names apply
  // to a genuine Tree Frog Android design only (isDevioUcxDesign); Devio SCX, Tesira,
  // and the WDT-90 Tesira overflow fallback all use the Parlé TCM-X / TTM-X names. The
  // mic modelName in state can lag the current family/platform (e.g. "Parlé DCM-X" left
  // over from picking Devio), so re-derive it from the stable model key rather than
  // trusting it verbatim. Reuses the same familyMicModelName the state layer uses
  // (utilities/micFamily), so all surfaces agree (PIG-594). Non-ceiling/table models
  // have no family twin and fall through to finalMicModel unchanged.
  finalMicModel = familyMicModelName(micModelType, isDevioUcxDesign(area), finalMicModel);

  // Handle speaker model defaults
  let finalSpkrModel = spkrModel;
  if (!finalSpkrModel && spkrLayout && spkrLayout.length > 0) {
    finalSpkrModel = spkrLayout[0].modelName || '';
  }
  if (!finalSpkrModel) {
    finalSpkrModel = 'Desono C-IC6'; // Default speaker model
  }

  // Note: It's valid to have no speakers configured initially
  // The equipment list will be generated with available devices

  // PIG-451 (WDT-140): speaker-type detection, hoisted to function scope so both
  // the bundle+overflow path and the individual/à-la-carte path apply the same
  // amplifier-capacity, cable, and CCA rules regardless of which path is taken.
  const isWallSpeakerConfig = speakers?.mountingType === 'wall'
    || constants.equipment?.speakers?.[speakers?.model]?.isWallSpkr === true;
  const isPendantSpeakerConfig = constants.equipment?.speakers?.[speakers?.model]?.isPendantSpkr === true
    || (speakers?.modelName || '').includes('P6-SM');

  const equipmentList = {};

  // Helper function to add devices to equipment list
  const addToEquipmentList = (input, defaultQty = 1) => {
    // Normalize input to array format
    const items = Array.isArray(input) ? input : [[input, defaultQty]];
    items.forEach(([product, qty]) => {
      if (product) {
        const tableObject = makeTableObject(product, qty || defaultQty);
        if (tableObject) {
          const [key, value] = tableObject;
          equipmentList[key] = value;
        };
      };
    });
  };

  const cables = equipmentCatalog.Cables.reduce((acc, curr) => {
    acc[curr.length] = 0;
    return acc
  }, {});
  // Helper to add cables
  const addCables = (arr) => {
    keys(cables).forEach((length, index) => cables[length] += arr[index]);
  };

  // PIG-452 (WDT-141): à-la-carte cable reference table.
  //
  // Baseline cables added for ANY à-la-carte config (independent of mic/speaker type):
  //   • 1 × 10' cable  laptop → DSP            (emitted by the DSP block: addCables([dspCount-1, 1, 0]))
  //   • 1 × 25' cable  DSP → mic head unit     (emitted per base unit by the mic block)
  //   • 1 × 3' cable   per ADDITIONAL DSP unit (dspCount-1, emitted by the DSP block)
  //
  // Per mic/speaker configuration (ch = total amp channels = TCM-XA units×2 + AMP-450BP units×4):
  //   Mic            | Speaker                     | 25' DSP→AMP | 3' DSP→AMP | 10' mic→elem | 10'/exp | 10'/spkr | CCA-1                     | BPAK
  //   Ceiling TCM-XA | C-IC6                       |     —       |     —      |      1       |  1/exp  |  1/spkr  | 0                        | 1
  //   Ceiling TCM-XA | Pendant P6-SM               |     —       |     —      |      1       |  1/exp  |  1/spkr  | ccaDaisyChainQty(spkr,ch)| 0
  //   Ceiling TCM-XA | P6-SM + overflow AMP-450BP  |     1       |     —      |      1       |  1/exp  |  1/spkr  | ccaDaisyChainQty(spkr,ch)| 0
  //   Ceiling TCM-X  | Wall                        |     —       |     1      |      1       |  1/exp  |  1/spkr  | 1/spkr                   | 0
  //   Tabletop TTM-X | C-IC6                       |     1       |     —      |      —       |    —    |  1/spkr  | 0                        | 0
  //   Tabletop TTM-X | Wall                        |     —       |     1      |      —       |    —    |  1/spkr  | 1/spkr                   | 0
  //   Tabletop TTM-X | Pendant P6-SM               |     1       |     —      |      —       |    —    |  1/spkr  | ccaDaisyChainQty(spkr,ch)| 0
  //   Pendant TCM-1A | C-IC6                       |     —       |     —      |      —       |  1/exp  |  1/spkr  | 2/head (PIG-453)                  | 0
  //   Pendant TCM-1A | C-IC6 + overflow AMP-450BP  |     1       |     —      |      —       |  1/exp  |  1/spkr  | 2/head (PIG-453)                  | 0
  //   Pendant TCM-1A | Wall                        |     —       |     1      |      —       |  1/exp  |  1/spkr  | 1/spkr                            | 0
  //   Pendant TCM-1A | Pendant P6-SM               |     —       |     —      |      —       |  1/exp  |  1/spkr  | 2/head (PIG-453) + ccaDaisyChainQty(spkr,ch) | 0
  //   Pendant TCM-1A | P6-SM + overflow AMP-450BP  |     1       |     —      |      —       |  1/exp  |  1/spkr  | 2/head (PIG-453) + ccaDaisyChainQty(spkr,ch) | 0
  //   BMA360D (tile) | Pendant P6-SM               |     —       |     —      |      —       |    —    |  1/spkr  | §5 Case B (see below)    | 0
  // NOTE: "2/head" = 2 × ampBaseUnits — TCM-1A has no RJ-45, so each amplified pendant
  // head unit needs 2 CCA for its own connectivity, independent of/additional to any
  // CCA needed for the downstream speaker wiring (P6-SM daisy-chain formula above).
  //
  // The 25'/3' DSP→AMP and speaker-jumper (-10'/+25') cables are emitted by the amp block
  // (addCables([0, -ampQty, ampQty])). The 10' mic→element / per-expander cables are emitted
  // by the mic block. This helper block adds the CCA / BPAK accessory lines that the columns
  // above describe; commits PIG-453 (TCM-1A) and PIG-450 (P6-SM daisy-chain) fill the CCA cells.
  // BMA360D + P6-SM is a separate rule (PIG-591 §5 Case B): onboard-amp-driven speakers take
  // 2 CCA each (both Euroblock ends, no daisy chaining); only speakers overflowing to an
  // external AMP-450BP use the daisy-chain formula. See the à-la-carte speaker block below.

  // PIG-454/450/453 (WDT-138/139/142): CCA adapters are added from several places (wall
  // speakers, P6-SM pendant speakers, TCM-1A amp outputs) and can co-occur in one config.
  // addToEquipmentList overwrites by model key, so accumulate the running total here and
  // re-add the summed quantity instead of clobbering a prior CCA line.
  const addCCA = (qty) => {
    if (!qty || qty <= 0) return;
    const cca = find(equipmentCatalog.Accesories, acc => acc.modelName === 'CCA');
    if (!cca) {
      console.warn('calculateEquipmentTable: CCA adapter not found in catalog');
      return;
    }
    const existingQty = equipmentList['CCA']?.qty || 0;
    addToEquipmentList(cca, existingQty + qty);
  };

  // PIG-450 (WDT-139): daisy-chain CCA quantity for P6-SM pendant speakers driven off a
  // TCM-XA / TCM-1A head amp or an external AMP-450BP. Reconciles the "min(spkr,ch)+(spkr-ch)×2"
  // cell in the PIG-452 table with the PIG-450 AC:
  //   • speakers ≤ ch → all connect directly → CCA = total speakers
  //   • speakers > ch → excess are daisy-chained → CCA = ch (direct) + (spkr-ch)×2 (chained)
  // NOTE: this is NOT used for BMA360D onboard-amp-driven P6-SM — those take a flat 2 CCA per
  // speaker per PIG-591 §5 Case B (only their AMP-450BP overflow uses this daisy-chain formula).
  const ccaDaisyChainQty = (spkrs, ch) => {
    if (!spkrs || spkrs <= 0) return 0;
    const direct = Math.min(spkrs, ch);
    const chained = Math.max(0, spkrs - ch);
    return direct + chained * 2;
  };

  // Check if room has a bar - check both hasBar flag and microphone model name
  const isBarDevice = hasBar || (finalMicModel && (finalMicModel.includes('ABC 2500a') || finalMicModel.includes('VBC 2500a') || finalMicModel.includes('VBC 2800') || finalMicModel.includes('CBC 2500')));

  // Tree Frog (Devio Android UC) bundle match — hoisted so both the bar branch
  // and the non-bar `bundleResult` branch below can reuse the same result
  // without refiltering the catalog. Null when not applicable.
  //
  // A 'room' bundle bundles Devio-specific mic/speaker/bar SKUs (e.g.
  // MRB-UCX2000T-VBC2800-DCMX) — only valid when the design actually resolved
  // to productFamily 'Devio'. A 'compute' bundle is just the UCX hub + touch
  // console (UCC-UCX1000-T/2000-T/3000-T), with no mic-specific content, and
  // is legitimately added ALONGSIDE a Tesira DSP during the port-overflow
  // fallback (platform stays 'Android' — Windows greyed out — but
  // productFamily flips to 'Tesira') per WDT-90 §5.4: "Add compute bundle
  // UCC-UCX1000-T... it does not replace the DSP." So only 'room' matches need
  // the extra `isTreeFrog` gate here; `matchTreeFrogBundle` itself only checks
  // platform (`isTreeFrogAndroidUC`), which is correct for 'compute' but would
  // wrongly keep matching a 'room' bundle (and adding its Devio mic/speaker
  // SKUs) even once productFamily has resolved away from 'Devio' — that's the
  // original "mixed Tesira + Devio" bug: the equipment list must be driven by
  // what the mic/speaker configuration actually resolved to, not by the
  // platform toggle alone.
  const treeFrogMatchRaw = matchTreeFrogBundle(area);
  const treeFrogMatch = (treeFrogMatchRaw?.type === 'room' && !isTreeFrog) ? null : treeFrogMatchRaw;

  if (isBarDevice) {
    // Special handling for VBC 2800 with UC Compute
    const isVBC2800 = finalMicModel && finalMicModel.includes('VBC 2800');
    const isVBC2500a = finalMicModel && finalMicModel.includes('VBC 2500a');
    const hasUCCompute = area.ucCompute && area.ucCompute !== 'None';

    // Tree Frog (Devio Android UC) bundle path. When productFamily === 'Devio'
    // AND UC compute is selected, route through the Tree Frog matcher first.
    // A room bundle covers the bar (and optionally expansion mics/speakers) as
    // a single line item — the bar add and existing TSC2DIP-replacement logic
    // are both skipped. A compute bundle only covers the hub + Apprimo Touch;
    // the bar is still added separately à la carte. When no Tree Frog bundle
    // matches, fall through to the existing Tesira / Parlé UC bundle logic.
    if (treeFrogMatch) {
      addToEquipmentList(treeFrogMatch.bundle);
      if (treeFrogMatch.type === 'compute') {
        // Compute bundle only includes the hub + touch console; still add the bar.
        const bar = find(equipmentCatalog.ConferencingBars, b => b.modelName === finalMicModel);
        if (bar) addToEquipmentList(bar);
      }
      // Room bundle path covers the bar — nothing else to add here. Expansion
      // mic/speaker excess is handled below by the existing expansion add block,
      // which reads counts from `area.expansion*` directly (the bundle's
      // includes.micQty / spkrQty drive how many we still need to add — see
      // the expansion mic count adjustment below).
    } else if ((isVBC2800 || isVBC2500a) && hasUCCompute) {
      // Handle UC bundles for both VBC 2800 and VBC 2500a without duplicating logic
      const suffix = area.ucCompute === 'Microsoft Teams' ? 'T' : 'Z';
      const expectedModelName = isVBC2800
        ? `Biamp MRB-VBC 2800-TSC2DIP-${suffix}`
        : `Biamp MRB-VBC 2500a-TSC2DIP-${suffix}`;

      const ucBundle = find(equipmentCatalog.Bundles, bundle =>
        bundle.modelName === expectedModelName
      );

      if (ucBundle) {
      addToEquipmentList(ucBundle);
      } else {
      // Fallback to regular bar if UC bundle not found
      const bar = find(equipmentCatalog.ConferencingBars, bar => bar.modelName === finalMicModel);
      if (bar) {
        addToEquipmentList(bar);
      }
      }
    } else {
      // Regular bar device handling (ABC 2500a, VBC 2500a, VBC 2800, CBC 2500 CM/PM without UC Compute)
      const bar = find(equipmentCatalog.ConferencingBars, bar => bar.modelName === finalMicModel);
      if (bar) {
        addToEquipmentList(bar);

        // Add recommended accessories for CBC 2500 bars
        if (finalMicModel.includes('CBC 2500')) {
          // Calculate distance from microphone to ceiling
          const ceilingHeight = area.ceilingHeight?.min || 2.7432; // Default 9ft ceiling
          const mountingHeight = micHeight || 2.4384; // Default 8ft mounting height
          const distanceToCeiling = ceilingHeight - mountingHeight;

          if (finalMicModel.includes('CBC 2500 PM')) {
            // CBC-2500 PM: Pole Mount version
            // If distance <= 3'5" (2.13m), accessory included in PM version
            // If distance > 3'5" (2.13m), add PMA-2000-PE (3' pole extensions)
            const includedPoleLength = 2.13; // 3'5" in meters (included with PM version)

            if (distanceToCeiling > includedPoleLength) {
              // Calculate number of 3' extensions needed
              const additionalDistance = distanceToCeiling - includedPoleLength;
              const poleExtension = find(equipmentCatalog.Accesories, acc => acc.modelName === 'PMA-2000-PE');

              if (poleExtension) {
                const extensionLength = 0.9144; // 3' in meters
                const extensionCount = Math.ceil(additionalDistance / extensionLength);
                addToEquipmentList(poleExtension, extensionCount);
              }
            }
          } else if (finalMicModel.includes('CBC 2500 CM')) {
            // CBC-2500 CM: Cable Mount version
            // If distance <= 39" (1m), 1m sleeves included with product
            // If distance > 39" (1m), add DCC-Kit (Decorative Cover Kit)
            const includedSleeveLength = 1.0; // 39" (approx 1m) included with CM version

            if (distanceToCeiling > includedSleeveLength) {
              const dccKit = find(equipmentCatalog.Accesories, acc => acc.modelName === 'DCC-Kit');
              if (dccKit) {
                addToEquipmentList(dccKit, 1);
              }
            }
          }
        }
      } else {
        console.warn('calculateEquipmentTable: Bar device not found in catalog', { finalMicModel, equipmentCatalog: equipmentCatalog.ConferencingBars });
      }
    }

    // Append expansion mics + speakers from bar + expansion configurations. The bar
    // branch is the only path where these can exist — expansion state is only populated
    // when the primary mic is a bar (see room.ts setExpansionMicType guards). Counts
    // come from the placed layouts so manual adds/deletes are reflected in real time.
    //
    // Catalog lookup notes:
    //   - Non-Devio falls back to the Parlé TCM-X / TTM-X entries (the existing
    //     placeholder behavior — Devio-specific DCM-X / DTM-X entries land later).
    //   - Devio ceiling expansion picks DCM-X by default; when `evaluateAmpSwap`
    //     reports the swap is safe (DCM-XA channels cover the speakers), the
    //     amplified head (DCM-XA) is used instead and the standalone amp is dropped
    //     further down. Net effect: one peripheral port freed.
    const ampSwap = isTreeFrog ? evaluateAmpSwap(area) : null;
    // Devio UCX bar + expansion ceiling mics + expansion speakers: amplification per
    // port accounting spec §4.1 — head amps (DCM-XA) when they cover the speakers,
    // a DPA-450BP alone for 5–8 C-IC6 on one chain, and DPA-450BP + port-1 DCM-XA
    // for 9–12. `evaluateAmpSwap` only knows "all DCM-XA" or "all DPA", so it
    // can't express the 9–12 combination; this plan drives both the head-unit
    // promotion below and the DPA-450BP count further down.
    const barExpansionSpkrCount = area.expansionSpeakers?.layout?.length ?? 0;
    const barCeilingAmpPlan = (isDevioUcxDesign(area) && area.expansionMicType === 'ceiling'
      && (area.expansionMicrophones?.layout?.length ?? 0) > 0 && barExpansionSpkrCount > 0)
      ? planCeilingAmplification({
          baseUnits: Math.ceil((area.expansionMicrophones?.layout?.length ?? 0) / CEILING_CHAIN_MAX_MICS),
          speakers: barExpansionSpkrCount,
          freePorts: maxStandaloneAmpPortsFor(area),
          headAmpCapacity: expansionSpeakerCaps(area).headAmp,
          dpaCapacity: expansionSpeakerCaps(area).dpa,
        })
      : null;
    // When a Tree Frog ROOM bundle covers some/all expansion devices, only the
    // excess gets added à la carte (per the spec: "no individual peripheral line
    // items are added for devices covered by the bundle"). Compute bundles
    // (UCC-UCX*-T) cover only the hub + touch console; expansion devices stay
    // full-count and route through the existing à la carte adds below.
    const rawExpansionMicCount = area.expansionMicrophones?.layout?.length ?? 0;
    const expansionMicCount = treeFrogMatch?.type === 'room'
      ? treeFrogMatch.excessMics
      : rawExpansionMicCount;
    if (expansionMicCount > 0) {
      // Each UCC Ethernet peripheral chain is 1 head unit + at most 1 optional expander
      // (PIG-651 / port accounting spec §2.2 — same 2-mic cap for ceiling and table
      // chains). Splitting the placed count into head + expander lines here — instead
      // of one lumped SKU line for the whole count — is what makes the BOM match what's
      // actually wireable: 3 placed table mics are 2 chains (2 ports), i.e. 2× DTM-X
      // (one head per chain/port) + 1× DTM-XEX (only the first chain's 2nd mic is an
      // expander; the 2nd chain's lone mic is itself a head unit, not an expander).
      const chainMax = area.expansionMicType === 'table' ? TABLE_CHAIN_MAX_MICS : CEILING_CHAIN_MAX_MICS;

      // When a room bundle already includes a mic (e.g. MRB-UCX2000T-VBC2800-DTMX:
      // micQty 1), its head unit isn't itemized here — but it DOES occupy a slot in
      // chain 1, so `expansionMicCount` (the excess on top of the bundle) doesn't start
      // a fresh chain from zero. `bundleFillSlot` is how many more mics the bundle's own
      // chain can still take as a plain expander before the excess needs a NEW head unit.
      // E.g. bundle micQty=1, chainMax=2 → bundleFillSlot=1: the first excess mic
      // completes the bundle's chain (expander), any remainder starts new chains.
      // Without this, a bundle covering 1 mic + 1 excess mic would wrongly emit a new
      // DTM-X head instead of the DTM-XEX that actually completes the bundled chain.
      const bundleMicQty = treeFrogMatch?.type === 'room' ? (treeFrogMatch.bundle?.includes?.micQty ?? 0) : 0;
      const bundleFillSlot = bundleMicQty > 0 ? (chainMax - (bundleMicQty % chainMax)) % chainMax : 0;
      const bundleFillCount = Math.min(expansionMicCount, bundleFillSlot);
      const newChainMicCount = expansionMicCount - bundleFillCount;

      const expansionHeadCount = Math.ceil(newChainMicCount / chainMax);
      const expansionExpanderCount = bundleFillCount + (newChainMicCount - expansionHeadCount);

      // expansionHeadCount / expansionExpanderCount can each be 0 (e.g. the excess
      // mic(s) only complete the room bundle's already-included chain) — skip the
      // line entirely rather than adding a stray 0-qty entry.
      const addMicLine = (modelName, qty) => {
        if (qty <= 0) return;
        const entry = find(equipmentCatalog.Microphones, mic => mic.modelName === modelName);
        if (entry) {
          addToEquipmentList(entry, qty);
        } else {
          console.warn('calculateEquipmentTable: expansion mic placeholder not found in catalog', { modelName });
        }
      };

      if (area.expansionMicType === 'table') {
        // Family-aware base name: Devio UCX → DTM-X, Tesira / Devio SCX → TTM-X.
        // `familyMicModelName` mirrors the same resolution `applyDevioMicModelName`
        // already applies to the placed canvas mics. Table expansion mics have
        // no amplified equivalent (no DTM-XA hardware) — speakers on a
        // table-mic design always route through the standalone DPA-450BP, so
        // `ampSwap` (DCM-XA only) never applies here.
        const tableHeadName = familyMicModelName('ttm_x', isDevioUcxDesign(area), constants.expansionMicPlaceholders.table);
        const tableExpanderName = `${tableHeadName}EX`;
        addMicLine(tableHeadName, expansionHeadCount);
        addMicLine(tableExpanderName, expansionExpanderCount);
      } else if (barCeilingAmpPlan) {
        // Devio UCX: promote exactly the heads the §4.1 plan needs to DCM-XA.
        const xaHeadCount = Math.min(expansionHeadCount, barCeilingAmpPlan.xaBaseUnits);
        addMicLine('Parlé DCM-XA', xaHeadCount);
        addMicLine(constants.expansionMicPlaceholders.ceiling, expansionHeadCount - xaHeadCount);
        addMicLine(`${constants.expansionMicPlaceholders.ceiling}EX`, expansionExpanderCount);
      } else if (isTreeFrog && ampSwap?.swapValid) {
        // Devio only: promote only as many of the NEW head units to the
        // integrated-amp DCM-XA as `dcmxaChainsNeeded` (cost optimization, spec
        // §4.1) — any chain beyond that stays plain DCM-X, since its amp capacity
        // isn't needed. E.g. 2 chains + 4 speakers → 1 DCM-XA (covers all 4) + 1
        // plain DCM-X, not 2 DCM-XA. The expander is always plain DCM-XEX
        // regardless of whether its chain's head is amplified.
        // NOTE: `dcmxaChainsNeeded` is computed from the area's total placed mic
        // count (evaluateAmpSwap), not the bundle-adjusted excess used here — a
        // room bundle stacked with a partial amp swap isn't exercised today, so
        // this may over/under-promote in that narrow combination.
        const xaHeadCount = Math.min(expansionHeadCount, ampSwap.dcmxaChainsNeeded ?? 0);
        const plainHeadCount = expansionHeadCount - xaHeadCount;
        addMicLine('Parlé DCM-XA', xaHeadCount);
        addMicLine(constants.expansionMicPlaceholders.ceiling, plainHeadCount);
        addMicLine(`${constants.expansionMicPlaceholders.ceiling}EX`, expansionExpanderCount);
      } else {
        addMicLine(constants.expansionMicPlaceholders.ceiling, expansionHeadCount);
        addMicLine(`${constants.expansionMicPlaceholders.ceiling}EX`, expansionExpanderCount);
      }

      // Ceiling expansion mic pole-mount hardware. Per the "Bar + Expansion Ceiling Mic
      // Placement — Algorithm Specification" (Confluence, confirmed with the spec owner
      // 2026-08 — see Documentation/specs/expansion-ceiling-mic-pole-mount-amendment.md), the
      // Tree Frog Ethernet Ceiling Mic (DCM-X/DCM-XA) is treated as equivalent to the Parlé
      // TCM-X for ALL calculation purposes, including mounting hardware — so every placed
      // expansion ceiling mic gets the same TCM-X-PM base kit (+ PMA-2000-PE extensions beyond
      // 3'5"/1.042m) the primary TCM-X ceiling mic gets below. Sized off the EXPANSION mic's
      // OWN configured height (which can differ from the primary bar mic's height/mountingDistance
      // computed further down) — same fallback chain computeExpansionCeilingMicCoords uses
      // (roomCalculations.ts) so the mounting math matches the placement math.
      // `expansionMicCount` (not rawExpansionMicCount) so a room-bundle-covered mic isn't
      // double-mounted — its hardware, including any mount, is already in the bundle SKU.
      if (area.expansionMicType === 'ceiling' && expansionMicCount > 0 && area.ceilingHeight) {
        const expansionCeilingHeight = area.ceilingHeight.min || area.ceilingHeight;
        const expansionMicHeight = area.expansionMicrophones?.height ?? expansionCeilingHeight ?? 2.8956;
        const expansionMountingDistance = expansionCeilingHeight - expansionMicHeight;
        if (Math.abs(expansionMountingDistance) > 0.01) {
          const expansionPoleAccessories = calculateTCMXAccessories(expansionMountingDistance, expansionMicCount);
          expansionPoleAccessories.forEach(accessory => {
            const { quantity, ...accessoryData } = accessory;
            addToEquipmentList(accessoryData, quantity);
          });
        }
      }
    }

    // Expansion speakers use real catalog models — `area.expansionSpeakers.modelName`
    // is a Desono entry (e.g. "Desono C-IC6") populated by `setExpansionSpeakerModel`.
    // Counts read from the placed layout (`raw` count), not from `treeFrogMatch.excessSpeakers`
    // which mixed primary + expansion and over-counted in the bar branch.
    const expansionSpkrCount = area.expansionSpeakers?.layout?.length ?? 0;
    const expansionSpkrModelName = area.expansionSpeakers?.modelName;
    if (expansionSpkrCount > 0 && expansionSpkrModelName) {
      const expansionSpkrEntry = find(
        equipmentCatalog.Speakers,
        spkr => spkr.modelName && spkr.modelName.includes(expansionSpkrModelName),
      );
      if (expansionSpkrEntry) {
        addToEquipmentList(expansionSpkrEntry, expansionSpkrCount);
      } else {
        console.warn('calculateEquipmentTable: expansion speaker not found in catalog', { expansionSpkrModelName });
      }

      // Tree Frog (Devio) bar + expansion speakers: add DPA-450BP amps to drive the
      // expansion speakers, capped at the UCC peripheral port budget. Bar-only
      // designs leave both ports available; expansion mic chains consume one port
      // each, leaving the remainder for amps. Non-Devio bar designs are unchanged
      // — the existing equipment list shape doesn't add separate amps here
      // (legacy behavior preserved).
      //
      // Capacity is 8 speakers/amp for ceiling C-IC6 (2/channel × 4 channels) but
      // only 4 for pendant P6-SM (1/channel — spec §4.3 Case C, same distinction
      // the non-Devio CLM baseline amp-sizing already applies via
      // `isPendantSpeakerConfig`). Using 8 unconditionally under-provisioned
      // pendant expansion speaker designs (e.g. 6 pendant speakers reported as
      // fitting 1 amp when only 4 actually fit).
      //
      // When the amp-swap is valid (`ampSwap?.swapValid`), the DCM-XA expansion
      // mic head units handle amplification themselves, so no standalone amp is
      // added — the swap above already promoted the catalog lookup to DCM-XA.
      if (isTreeFrog && (barCeilingAmpPlan || !ampSwap?.swapValid)) {
        // Per-amp capacity from UCX_SPEAKER_CAPS (C-IC6 8 / P6-SM 4 — the same
        // numbers apply to the AMP-450 used on Devio SCX).
        let barAmpQty = barCeilingAmpPlan
          ? barCeilingAmpPlan.dpaQty
          : ceil(expansionSpkrCount / expansionSpeakerCaps(area).dpa);
        barAmpQty = Math.min(barAmpQty, maxStandaloneAmpPortsFor(area));
        if (barAmpQty > 0) {
          // Devio UCX (Tree Frog / Android) routes to the Devio DPA-450BP; Devio
          // SCX (Windows — e.g. the bar+expansion port-overflow fallback, which
          // has no Tesira "alongside" equivalent and moves the whole design to
          // Windows) has no UCX hub/peripheral ports, so it must route to the
          // Tesira AMP-450 family instead (PIG-594) — same distinction the
          // non-bar DSP branch below already applies via isDevioUcxDesign.
          const ampMatch = isDevioUcxDesign(area) ? 'DPA-450BP' : 'AMP-450';
          const amp = find(equipmentCatalog.Amplifiers, a => a.modelName && a.modelName.includes(ampMatch));
          if (amp) {
            addToEquipmentList(amp, barAmpQty);
            addCables([0, -barAmpQty, barAmpQty]);
          }
        }
      }
    }
  } else {
    // Calculate DSP based system with enhanced bundle matching
    let micCount = micQty;
    let spkrCount = spkrQty;
    let dspCount = 0;

    // Check if UC Compute is selected
    const hasUCCompute = area.ucCompute && area.ucCompute !== 'None';

    // Get microphone type information early to determine if bundle matching should be used
    const micInfo = find(equipmentCatalog.Microphones, mic => mic.modelName && finalMicModel && mic.modelName.includes(finalMicModel)) || {};
    const isPendant = micInfo.isPendant || false;
    // ceiling-tile mics (BMA360D family) are Dante-based with their own on-board
    // DSP — they must NOT match the TCM-X equipment bundles (e.g. MRB-…-C-CIC6). They are
    // handled à la carte below (BMA360D model + AMP-450BP + EasyConnect POE-90W).
    const isCeilingTileMic = micInfo.isCeilingTile || false;

    // PIG-516 (§3/§4): inputs for BMA360D DSP selection and bundle matching. The
    // coverage box (longest dimension) and ceiling height drive the slant-distance
    // DSP sizing; both are only needed for ceiling-tile (BMA360D) designs.
    const bmaCeilingHeight = getAreaCeilingHeight(area);
    const bmaCoverageLongest = isCeilingTileMic ? getCoverageBoxLongest(area, constants) : 0;

    // Pendant mics skip bundle matching (à la carte). Ceiling-tile (BMA360D) mics
    // now attempt a BMA360D bundle first (PIG-516); only TCM-X bundles remain off
    // limits for them.
    let bundleResult = null;
    if (!isPendant && !isCeilingTileMic) {
      // Tree Frog (Devio Android UC) bundle path runs first. Reuses the
      // `treeFrogMatch` hoisted above so we don't refilter the catalog twice.
      if (treeFrogMatch) {
        bundleResult = treeFrogMatch;
      } else {
        // Spec §4.3 Case C: P6-SM pendant speakers are 1 speaker/amp-channel (not
        // 2/channel like C-IC6), so a bundle's built-in TCM-XA head (2 channels)
        // only covers 2 speakers directly — anything beyond that needs an
        // external AMP-450BP regardless of whether a bundle is used. That AMP-450BP
        // is still cheaper/simpler to size around the bundle's head amp than to
        // replace it, since the bundle's own catalog data (e.g. the Large P6-SM
        // bundle: 1 TCM-XA + 4 P6-SM, no AMP-450BP) already relies on CCA
        // daisy-chaining to exceed the raw 1/channel wiring limit. So the bundle is
        // always attempted here; the individual/à-la-carte amp-sizing branch below
        // (`isPendantSpeakerConfig`) carries the actual Case C optimization check
        // for when a bundle isn't used at all.

        // Try to match bundle with standard equipment (UC Compute will be handled later via bundle replacement)
        bundleResult = matchBundle({
          productFamily,
          micModel: finalMicModel,
          micQty: micCount,
          spkrModel: finalSpkrModel,
          spkrQty: spkrCount,
          requiresUCCompute: false
        });
      }
    } else if (isCeilingTileMic) {
      // PIG-516 (§4): match a BMA360D bundle before resolving à la carte. A match
      // requires a single BMA360D unit, C-IC6 speakers, and a strict speaker-qty
      // match (2 → X800/medium, 4 → X1600/large). The bundle covers the mic, DSP,
      // speakers, on-board-amp power (POE-90W) and cabling as one line item, so
      // there are no excess items in the matchable cases. Anything else → null →
      // the à la carte path below (with §3 DSP sizing).
      const bmaBundle = matchBma360dBundle({
        micModel: finalMicModel,
        unitCount: micCount,
        spkrModel: finalSpkrModel,
        spkrQty: spkrCount,
        coverageBoxLongest: bmaCoverageLongest,
        ceilingHeight: bmaCeilingHeight,
      });
      if (bmaBundle) {
        // Support report: a user can add speakers on top of an already-matched
        // bundle (e.g. 2 default + 2 more = 4, still an X800/medium room). The
        // bundle must be RETAINED — matchBma360dBundle now matches on "at least"
        // the bundle's own speaker count rather than exact equality — and the
        // extra speakers flow through as excess, handled by the isCeilingTileMic
        // branch below (CCA/AMP-450BP/BPAK/cabling aware of the bundle's own
        // already-consumed on-board capacity), not the generic bundle-overflow
        // path built for TCM-X (which has no idea about BMA360D's accessory rules).
        const excessSpeakers = Math.max(0, spkrCount - (bmaBundle.includes?.spkrQty || 0));
        bundleResult = { bundle: bmaBundle, fallbackToDSP: false, excessMics: 0, excessSpeakers };
      }
    }

    if (bundleResult && bundleResult.bundle && !bundleResult.fallbackToDSP) {
      // Bundle found - add to equipment list
      const bundle = bundleResult.bundle;
      addToEquipmentList(bundle);

      // Handle UC Compute bundle replacement
      // If UC Compute is selected, replace certain bundles with their TSC2DIP equivalents
      if (hasUCCompute) {
        const ucSuffix = area.ucCompute === 'Microsoft Teams' ? 'T' : 'Z';

        // Check if MRB-L-X400-C-CIC6 bundle was added
        if (bundle.modelName === 'Biamp MRB-L-X400-C-CIC6') {
          // Remove the standard bundle
          delete equipmentList['Biamp MRB-L-X400-C-CIC6'];

          // Add the TSC2DIP replacement bundle
          const tsc2ipBundleName = `Biamp MRB-L-X400-C-TSC2DIP-${ucSuffix}`;
          const tsc2ipBundle = find(equipmentCatalog.Bundles, b => b.modelName === tsc2ipBundleName);

          if (tsc2ipBundle) {
            addToEquipmentList(tsc2ipBundle);
          }
        }
      }

      // Adjust counts based on bundle contents
      micCount = bundleResult.excessMics || 0;
      spkrCount = bundleResult.excessSpeakers || 0;
      dspCount += 1;

      // WDT-90 port-overflow fallback: when platform=Android but productFamily
      // flipped to Tesira (peripherals route through a Tesira DSP via USB to
      // UCC-UCX1000-T), add the Tesira DSP sized to the mic count. Normal
      // Tree Frog designs (productFamily='Devio') route directly to the UCX
      // hub and don't need a separate DSP.
      if (isTreeFrogAndroidUC(area) && area.productFamily === 'Tesira' && micCount > 0) {
        const processors = getProcessors('Tesira', micCount);
        addToEquipmentList(processors);
        dspCount += processors.reduce((acc, p) => acc + p[1], 0);
      }

      // Add additional equipment for excess devices
      if (micCount > 0 || spkrCount > 0) {
        // PIG-595 (spec §4.1): for Tree Frog (Devio) ceiling designs the excess
        // speakers are amplified by promoting excess ceiling mic base units to the
        // integrated-amp DCM-XA head and/or adding standalone DPA-450BP amps —
        // whichever the peripheral-port budget allows. When both ports are consumed
        // by mic chains (3–4 mics), a standalone amp has no port, so the base units
        // MUST become DCM-XA or the speakers are left unamplified (the bug). The
        // plan is null for non-Devio / non-ceiling designs, which keep the legacy
        // standalone-amp path below unchanged.
        //
        // Speaker groups: on Devio UCX (Android) the plan covers every speaker
        // group with that group's capacities (`ucxSpeakerCaps`, port accounting
        // spec §2.3) — C-IC6 family 4 per DCM-XA / 8 per DPA-450BP, P6-SM 2 / 4,
        // and wall 0 / 2 (a DCM-XA never powers wall speakers, so wall is
        // DPA-450BP only). On Devio SCX, wall and P6-SM speakers are excluded and
        // keep their existing external-amp math (PIG-451 / PIG-534).
        const isCeilingMic = !isPendant && !isCeilingTileMic && !(micInfo.isTableTop || false);
        // Tree Frog UC Platform §5.3a partial match: a Devio UCX room bundle
        // (MRB-UCX3000T-VIDI280-*) is only matched when the extras fit the devices
        // the bundle already contains (`getRoomBundleCapacity`) — the bundle's
        // DCM-XA / DPA-450BP drives the extra C-IC6 speakers and the extra mic is
        // the DCM-XEX / DTM-XEX that completes the bundle's own chain. So the
        // extras never add an amp, an amp head promotion, or a new head unit.
        // Without this, the extras were sized as if the bundle had no spare
        // capacity: TC-22 / TC-23 got an extra DPA-450BP, and a leftover mic
        // started a new chain (a third head unit on a two-port hub).
        const isUcxRoomBundle = treeFrogMatch?.type === 'room' && bundleResult === treeFrogMatch;
        // Devio UCX (Android) speaker capacities by speaker group — port accounting
        // spec §2.3: C-IC6 family 4 per DCM-XA / 8 per DPA-450BP, P6-SM 2 / 4, wall
        // 0 / 2 (DPA-450BP only). Every speaker model is allowed on the non-bar
        // Devio UCX path (UC Platform spec §5.3). Null off the UCX path, where the
        // legacy amp math below is unchanged (wall / P6-SM keep PIG-451 / PIG-534).
        const ucxSpeakerCaps = isDevioUcxDesign(area)
          ? UCX_SPEAKER_CAPS[getUcxSpeakerGroup(speakers) ?? 'ceiling']
          : null;
        const ceilingAmpPlan = (isTreeFrog && isCeilingMic && spkrCount > 0 && !isUcxRoomBundle
          && (ucxSpeakerCaps || (!isWallSpeakerConfig && !isPendantSpeakerConfig)))
          ? planCeilingAmplification({
              baseUnits: ceil(micCount / 2),
              speakers: spkrCount,
              freePorts: maxStandaloneAmpPortsFor(area),
              headAmpCapacity: ucxSpeakerCaps?.headAmp,
              dpaCapacity: ucxSpeakerCaps?.dpa,
            })
          : null;

        // Handle excess microphones with the standard à la carte logic so the
        // correct base unit (e.g. TCM-X, not the amplified TCM-XA) and the
        // required EasyConnect cables are included (PIG-433).
        if (micCount > 0) {
          const isTableTopMic = micInfo.isTableTop || false;
          // UCX room bundle: excess mics complete the bundle's chain (expanders only).
          const baseUnits = isUcxRoomBundle ? 0 : ceil(micCount / (isPendant ? 3 : 2));
          const expanderUnits = micCount - baseUnits;
          // PIG-595: promote base units to the integrated-amp DCM-XA head when the
          // plan calls for it. getMicrophones resolves the amp-unit slot to DCM-XA
          // (its `isAmpUnit` entry substring-matches "Parlé DCM-X").
          const xaBaseUnits = ceilingAmpPlan?.xaBaseUnits ?? 0;
          // finalMicModel is already resolved to the correct product family upstream, so
          // the excess mic line uses it directly (UCX → Devio DCM-X / DTM-X; SCX / Tesira
          // / overflow fallback → Parlé TCM-X / TTM-X).
          const microphones = getMicrophones(finalMicModel, xaBaseUnits, baseUnits - xaBaseUnits, expanderUnits);
          addToEquipmentList(microphones);

          // 10' cable per expander unit and between each base unit and its mic
          // (non-tabletop); 25' cable per base unit.
          const tenFootQty = isTableTopMic ? 0 : expanderUnits + (isPendant ? 0 : baseUnits);
          addCables([0, tenFootQty, baseUnits]);
        }

        // Add amplifiers and cables for excess speakers
        if (spkrCount > 0 && isCeilingTileMic) {
          // BMA360D bundle + excess ceiling C-IC6 speakers (support report: user
          // adds speakers on top of an already-matched bundle). The bundle is a
          // single tile (matchBma360dBundle requires unitCount === 1) and always
          // ships with its own speaker count entirely on the on-board amp — bundle
          // sizes are 2/medium or 4/large, both ≤ ONBOARD_AMP_CAPACITY (4) — so the
          // bundle's own POE-90W, tile home-run cable, and CCA are already inside
          // the SKU (confirmed by the existing "no separate CCA/cable line" bundle
          // tests). None of the generic bundle-overflow logic above applies here
          // (it's built for TCM-X and knows nothing about on-board capacity/CCA/25'
          // tile cabling), so this excess is handled with its own self-contained
          // calc: fill whatever on-board capacity the bundle's tile has left before
          // any external AMP-450BP, then apply the same CCA/BPAK/cabling rules as
          // the full à la carte isCeilingTileMic path.
          const bundleSpkrQty = bundle.includes?.spkrQty || 0;
          const onboardCapacity = ONBOARD_AMP_CAPACITY; // bundle is always a single tile
          const remainingOnboard = Math.max(0, onboardCapacity - bundleSpkrQty);
          const excessOnboardDriven = Math.min(spkrCount, remainingOnboard);
          const excessOverflow = spkrCount - excessOnboardDriven;
          const excessAmpQty = Math.ceil(excessOverflow / AMP_450BP_CAPACITY);

          const speaker = find(equipmentCatalog.Speakers, s =>
            s.modelName && finalSpkrModel && s.modelName.includes(finalSpkrModel)
          );
          if (speaker) {
            addToEquipmentList(speaker, spkrCount);
          } else {
            console.warn('calculateEquipmentTable: à-la-carte speaker not found in catalog — no speaker/cable line added', { finalSpkrModel });
          }

          // PIG-481 (WDT-78): the bundle's own catalog entry already includes 1×
          // EasyConnect POE-90W (confirmed against the BMA360D Equipment Selection
          // Logic page) — the injector powers the tile's on-board amp itself, not a
          // per-speaker accessory, so the single tile only ever needs the one
          // injector the bundle ships with, regardless of how many additional
          // speakers (on-board or overflow) are added on top. No separate line item
          // is added here.

          // CCA for the excess speakers still riding the bundle tile's on-board amp
          // (BMA360D-Equipment-Selection-Logic.md §5 Case A) — overflow speakers on
          // an external AMP-450BP connect directly, no CCA needed.
          if (excessOnboardDriven > 0) {
            addCCA(excessOnboardDriven);
          }

          // External AMP-450BP overflow: one bracket (BPAK) per unit for driving
          // ceiling C-IC6 speakers (confirmed with the spec owner — see
          // Documentation/specs/bma360d-onboard-amp-and-bpak-amendment.md), plus its
          // own 25' DSP→amp run (the tile's own 25' home-run cable is already in the
          // bundle SKU, so only the amp run is added here).
          let excessBpakQty = 0;
          if (excessAmpQty > 0) {
            const amplifier = find(equipmentCatalog.Amplifiers, amp => amp.modelName && amp.modelName.includes('AMP-450BP'));
            if (amplifier) {
              addToEquipmentList(amplifier, excessAmpQty);
              addCables([0, 0, excessAmpQty]);
              excessBpakQty = excessAmpQty;
              const bpak = find(equipmentCatalog.Accesories, acc => acc.modelName && acc.modelName.includes('BPAK'));
              if (bpak) addToEquipmentList(bpak, excessBpakQty);
            } else {
              console.warn('AMP-450BP not found in catalog');
            }
          }

          // 10' cable per excess speaker, minus any covered by a BPAK's own short
          // adapter cable (same accounting as the full à la carte BPAK/cable calc).
          addCables([0, Math.max(0, spkrCount - excessBpakQty), 0]);
        } else if (spkrCount > 0) {
          // `calculateAdditionalEquipment` reads `bundle.includes.spkrModel`
          // which is undefined on compute bundles (UCC-UCX*-T) — fall back to
          // the design's actual speaker model so Tree Frog compute and
          // overflow-fallback paths still output speaker line items.
          let additionalSpeakers = calculateAdditionalEquipment(bundle, 0, spkrCount);
          if (additionalSpeakers.length === 0 && finalSpkrModel) {
            const speaker = find(equipmentCatalog.Speakers, s =>
              s.modelName && s.modelName.includes(finalSpkrModel)
            );
            if (speaker) additionalSpeakers = [{ ...speaker, qty: spkrCount }];
          }
          additionalSpeakers.forEach(item => {
            addToEquipmentList(item, item.qty);
          });

          // Calculate amplifier needs for excess speakers. For Tree Frog ceiling
          // C-IC6 designs the standalone-amp count comes from the §4.1 plan, which
          // has already offloaded speakers onto DCM-XA heads (PIG-595). Otherwise
          // fall back to the port-budget-capped standalone count (no-op for
          // non-Devio — returns Infinity): C-IC6 and wall are 2 speakers/channel
          // (8/AMP-450BP); P6-SM (Case C) is 1 speaker/channel on the raw external
          // amp (4/AMP-450BP) — the bundle's own head amp supplies P6-SM's extra
          // daisy-chained capacity (see the CCA calc below), not this overflow amp.
          let excessAmpQty;
          if (isUcxRoomBundle) {
            // The bundle's own DCM-XA / DPA-450BP covers the extras (§5.3a).
            excessAmpQty = 0;
          } else if (ceilingAmpPlan) {
            excessAmpQty = ceilingAmpPlan.dpaQty;
          } else {
            excessAmpQty = ceil(spkrCount / (ucxSpeakerCaps?.dpa ?? (isPendantSpeakerConfig ? 4 : 8)));
            if (isTreeFrog) {
              excessAmpQty = Math.min(excessAmpQty, maxStandaloneAmpPortsFor(area));
            }
          }
          if (excessAmpQty > 0) {
            // Devio UCX (Tree Frog) routes to the Devio DPA-450BP — a PoE amp that
            // connects to a UCX hub peripheral port. Everything else, INCLUDING Devio
            // SCX (a DSP with no UCX hub / peripheral ports), routes to the Tesira
            // AMP-450 family (Tree Frog UC Platform §4.5 names DPA-450BP only in the
            // UCX Android paths). Gate on the shared isDevioUcxDesign classifier, not
            // isTreeFrog (= any Devio), so SCX gets AMP-450, not an unconnectable
            // DPA-450BP (PIG-594). Match on the family substring so partNumber changes
            // don't break the lookup.
            const ampMatch = isDevioUcxDesign(area) ? 'DPA-450BP' : 'AMP-450';
            const amplifier = find(equipmentCatalog.Amplifiers, amp => amp.modelName.includes(ampMatch));
            if (amplifier) {
              addToEquipmentList(amplifier, excessAmpQty);
              // PIG-452 (WDT-141): DSP→AMP run length depends on where the amp lives —
              // wall-speaker amps stay at rack level (3' jumper); ceiling/pendant overflow
              // amps sit up with the speakers (25' run). Previously always used 25' here,
              // even for wall-speaker overflow.
              if (isWallSpeakerConfig) {
                addCables([excessAmpQty, -excessAmpQty, 0]);
              } else {
                addCables([0, -excessAmpQty, excessAmpQty]);
              }
            }
          }

          // PIG-452 (WDT-141): the excess-speaker cable-table row ("… + overflow
          // AMP-450BP") requires a 10' cable per excess speaker, and — for P6-SM —
          // CCA adapters sized by daisy-chain topology against the overflow
          // AMP-450BP's own channels (the bundle's own included speakers/wiring/CCA
          // ship inside the bundle SKU and are not re-added here). Wall speakers need
          // 1 CCA per excess speaker regardless of amp channel count. Neither was
          // being added before this fix, even though this excess-speaker block is the
          // only place that can add them for a bundle+overflow design. (P6-SM bundles
          // are TCM-XA-based, never BMA360D, so the §5 Case B onboard rule does not
          // apply here — the overflow always uses the daisy-chain formula.)
          // Devio UCX: C-IC6-family speakers driven by a DCM-XA head take one BPAK per
          // DCM-XA (UC Platform spec §5.3 / baseline §4.6, DCM-XA for TCM-XA). The
          // BPAK ships with a short adapter cable, so one 10' speaker run fewer.
          const ucxXaBpakQty = (ucxSpeakerCaps && ceilingAmpPlan && !isWallSpeakerConfig && !isPendantSpeakerConfig)
            ? Math.min(ceilingAmpPlan.xaBaseUnits, spkrCount)
            : 0;
          if (ucxXaBpakQty > 0) {
            const bpak = find(equipmentCatalog.Accesories, acc => acc.modelName && acc.modelName.includes('BPAK'));
            if (bpak) addToEquipmentList(bpak, ucxXaBpakQty);
          }
          addCables([0, spkrCount - ucxXaBpakQty, 0]);
          if (isWallSpeakerConfig) {
            addCCA(spkrCount);
          } else if (isPendantSpeakerConfig) {
            // Daisy-chain channels: 4 per amp + 2 per DCM-XA head driving pendants
            // (baseline: ch = XA units × 2 + amp units × 4).
            const overflowChannels = excessAmpQty * 4 + (ceilingAmpPlan?.xaBaseUnits ?? 0) * 2;
            addCCA(ccaDaisyChainQty(spkrCount, overflowChannels));
          }
        }

        // Flush accumulated cables now, since zeroing the counts below skips
        // the à la carte cable flush further down.
        const cablesToAdd = equipmentCatalog.Cables
          .filter(cable => cables[cable.length] > 0)
          .map(cable => [cable, cables[cable.length]]);
        addToEquipmentList(cablesToAdd);

        // Update counts after adding additional equipment
        micCount = 0; // All mics handled by bundle + additional
        spkrCount = 0; // All speakers handled by bundle + additional
      }
    } else {
      // Fallback to traditional DSP calculation if no bundle match
      // Use legacy getBundle for backward compatibility.
      //
      // Ceiling-tile mics (BMA360D) are excluded from this fallback as a defensive
      // backstop. `matchBma360dBundle` above is the sole source of truth for BMA360D
      // bundle matching (now matching "at least" the bundle's own speaker count, with
      // any extra handled as excess on top of the retained bundle — see the
      // isCeilingTileMic excess branch a few lines up), returning null only for
      // genuinely unbundleable configs (multi-tile rooms, or fewer speakers than the
      // bundle itself ships with). This legacy `getBundle` matches via a looser
      // threshold check built for TCM-X and knows nothing about BMA360D's on-board-amp/
      // CCA/25'-tile-cable rules, so it must never be allowed to re-match a BMA360D
      // bundle on its own — anything matchBma360dBundle rejects should fall straight to
      // full à la carte below instead.
      const legacyBundle = !isCeilingTileMic
        ? getBundle(productFamily, finalMicModel, micCount, finalSpkrModel, spkrCount)
        : null;
      if (legacyBundle) {
        addToEquipmentList(legacyBundle);
        micCount -= legacyBundle.includes.micQty;
        spkrCount -= legacyBundle.includes.spkrQty;
        dspCount += 1;
      } else {
        // find dsps - ensure at least one DSP for the system even if no mics configured yet
        const minMicCount = Math.max(micCount, 1); // At least 1 to get a base DSP

        let processors;
        if (isCeilingTileMic) {
          // PIG-516 (§3): BMA360D DSP is sized by total AEC channels (slant
          // distance for one unit, 8 beams/unit aggregated for two-or-more),
          // not the generic capacity-based getProcessors. Replaces the prior
          // TesiraFORTÉ X 400 placeholder (PIG-487).
          processors = getBma360dProcessors(micCount, bmaCoverageLongest, bmaCeilingHeight);
          if (!processors || processors.length === 0) {
            processors = getProcessors(productFamily, minMicCount);
          }
        } else {
          // DSP sizing is by AEC channel count (= mic count), independent of mic mounting.
          // Pendant systems previously hardcoded a single X400 / SCX 400, which under-sized the
          // DSP for larger counts (e.g. poor acoustics needing 10 mics needs an X1600, not an
          // X400). getProcessors picks the smallest fitting DSP by capacity for every family.
          processors = getProcessors(productFamily, minMicCount);
        }

        addToEquipmentList(processors);
        dspCount += processors.reduce((acc, curr) => acc += curr[1], 0);
        // Add short cables between DSP and one long cable to switch/laptop
        addCables([dspCount - 1, 1, 0]);
      }
    };

    // Variables needed for downstream logic
    let baseUnits = 0;
    let ampBaseUnits = 0;
    let noAmpUnits = 0;
    let ampQty = 0;
    let isTableTop = false;
    let isCeiling = true;
    // PIG-297 (WDT-78): BMA360D escalation outputs consumed below to add the PoE
    // injector accessory. Both default false for every other mic type.
    let onboardAmpUsed = false;
    let poeInjectorNeeded = false;

    // Get microphone type information. `isCeilingTileMic` (BMA360D family) is defined
    // earlier — it gates bundle matching off and routes these mics à la carte.
    isTableTop = micInfo.isTableTop || false;
    isCeiling = !isPendant && !isTableTop;

    // Only process individual devices if not fully handled by bundle
    if (micCount > 0 || spkrCount > 0) {
      // Calculate mic base units. Ceiling-tile mics map 1:1 to placed units; TCM-X
      // family splits into base + expander pairs (or triples for pendants).
      baseUnits = micCount > 0
        ? (isCeilingTileMic ? micCount : ceil(micCount / (isPendant ? 3 : 2)))
        : 0;

      // Tree Frog (Devio) uses the standalone PoE amp (DPA-450BP) by default;
      // the integrated-amp option (DCM-XA) is selected by the auto-swap when the
      // port budget would otherwise overflow. Until that swap runs, force the
      // standalone branch so the amp count is port-aware from the start.
      // `isTreeFrog` is hoisted to the function scope above.

      // Calculate amplification. Spec §3.1.1: `useHeadAmp` is "the KEY COST
      // OPTIMIZATION logic for ceiling and pendant speakers (C-IC6 family,
      // P6-SM)" — a general rule, not scoped to the bundle-purchase decision
      // alone. So even once bundle matching has failed and we're building an à
      // la carte system, the plain ceiling-mic (Case B) branch below still gates
      // head-amp usage on `useHeadAmp`: for 5-8/13-16/... speakers, 2+ amplified
      // head units cost more than a single standalone AMP-450BP, even when
      // enough base units already exist to cover the speakers "for free" — see
      // the final branch below.
      const useHeadAmp = !isTableTop && !isCeilingTileMic && !isTreeFrog && (spkrCount % 8 > 0 && spkrCount % 8 <= 4);

      // PIG-451 (WDT-140): for à-la-carte ceiling mics the head-unit choice is driven by
      // speaker type, not just the cost heuristic below. Wall speakers → plain TCM-X head
      // + external AMP-450BP; P6-SM pendant speakers → integrated-amp TCM-XA head unit.
      // (isWallSpeakerConfig / isPendantSpeakerConfig are hoisted to function scope above.)

      if (spkrCount === 0) {
        // No speakers, no amplification needed
        ampBaseUnits = 0;
        noAmpUnits = baseUnits;
        ampQty = 0;
      } else if (isCeilingTileMic) {
        // PIG-297 (WDT-78): BMA360D amplifier configuration.
        // Wall speakers (PIG-479) are a fixed special case — the on-board amp runs
        // in bridge mode driving up to 2 speakers, with no external AMP-450BP and a
        // mandatory PoE injector; the escalation matrix does not apply. An area
        // carries a single speaker mounting type, so wall + ceiling/pendant
        // coexistence is not representable (unsupported in MVP per the AC).
        // Otherwise the escalation matrix interleaves the on-board amp (≤4 speakers)
        // with external AMP-450BP units (≤8 each), using the on-board amp only when
        // it is cheaper than adding another AMP-450BP (a 1–4 speaker remainder).
        // `ampQty` is the external AMP-450BP count; the on-board-amp / PoE-injector
        // outputs are consumed by the equipment-list assembly (PIG-481).
        // isWallSpeakerConfig is computed once at the top of this block (PIG-451).
        ampBaseUnits = 0;
        noAmpUnits = baseUnits;
        if (isPendantSpeakerConfig) {
          // PIG-591 (§5 Case B): BMA360D + P6-SM. The onboard amp drives 1 P6-SM per
          // channel = 4 per BMA360D unit, and — per §5 — each unit's onboard amp must be
          // used before any external amp. So onboard capacity = 4 × unit count; only the
          // excess overflows to AMP-450BP (P6-SM is Case C: 4 speakers / AMP-450BP).
          // getBma360dAmpConfig is NOT used here: it's built around the 8-speaker/
          // AMP-450BP C-IC6 rate and its cost-optimization remainder logic, whereas
          // P6-SM (Case C) overflows at 4 speakers/AMP-450BP with no such
          // optimization — onboard capacity is used unconditionally, in full,
          // before any overflow amp is added.
          const onboardCapacity = ONBOARD_AMP_CAPACITY * baseUnits;
          const overflowSpkrs = Math.max(0, spkrCount - onboardCapacity);
          ampQty = overflowSpkrs > 0 ? ceil(overflowSpkrs / 4) : 0;
          onboardAmpUsed = spkrCount > 0;
          poeInjectorNeeded = onboardAmpUsed;
        } else {
          // PIG-297 fix: getBma360dAmpConfig's on-board capacity scales with tile
          // count (baseUnits) — pass it explicitly so multi-unit rooms get credit
          // for every tile's on-board amp, not just one.
          const ampConfig = isWallSpeakerConfig
            ? getBma360dWallSpeakerAmpConfig()
            : getBma360dAmpConfig(spkrCount, baseUnits);
          ampQty = ampConfig.amp450bpQty;
          onboardAmpUsed = ampConfig.onboardAmpUsed;
          poeInjectorNeeded = ampConfig.poeInjectorNeeded;
        }
      } else if (isTableTop) {
        // Tabletop mics don't use head amps — they have their own audio path.
        // Speakers go through external amps. Spec §4.3: C-IC6/wall are 2
        // speakers/channel (8/AMP-450BP); P6-SM (Case C) is 1 speaker/channel
        // (4/AMP-450BP) — pendant speakers hit the external amp's raw wiring
        // limit sooner since there's no bundle head amp here to daisy-chain off.
        ampBaseUnits = 0;
        noAmpUnits = baseUnits;
        ampQty = ceil(spkrCount / (isPendantSpeakerConfig ? 4 : 8));
      } else if (isWallSpeakerConfig) {
        // PIG-451 (WDT-140): wall speakers always use AMP-450BP — no head-amp
        // optimization applies, regardless of mic type (ceiling: TCM-X non-amplified
        // head unit; pendant: TCM-1 non-amplified head unit). One AMP-450BP per 8 speakers.
        ampBaseUnits = 0;
        noAmpUnits = baseUnits;
        ampQty = ceil(spkrCount / 8);
      } else if (isPendantSpeakerConfig) {
        // Spec §4.3 Case C: ceiling or pendant mic + P6-SM pendant speakers →
        // integrated-amp head unit (TCM-XA or TCM-1A). P6-SM is 1 speaker/channel
        // (6Ω), so each 2-channel head covers 2 speakers directly — half the C-IC6
        // (8Ω, 2/channel) rate. Cap ampBaseUnits at what's actually needed
        // (Math.min(requiredAmpHeads, baseUnits)) rather than amplifying every base
        // unit, and overflow any excess P6-SM to external AMP-450BP units (4 each,
        // also 1/channel for Case C).
        const requiredAmpHeads = ceil(spkrCount / 2);
        ampBaseUnits = Math.min(requiredAmpHeads, baseUnits);
        noAmpUnits = baseUnits - ampBaseUnits;
        const overflowSpkrs = spkrCount - ampBaseUnits * 2;
        ampQty = overflowSpkrs > 0 ? ceil(overflowSpkrs / 4) : 0;
        // Optimization check (spec §4.3): if the AMP-450BP(s) already sized for the
        // overflow could ALSO cover every speaker without any amplified head units at
        // all, the head amp is redundant — convert it back to non-amplified.
        //
        // This reversion only applies to ceiling mics (TCM-XA bundle overflow, where
        // baseUnits for the à-la-carte remainder is already 0 in practice, making this
        // a no-op — see bundleOverflowPendantSpeakers.test.js). For pendant mics, the
        // business rule is unconditional: P6-SM pendant speakers + pendant mic always
        // select TCM-1A as the head unit, even when a standalone AMP-450BP sized for
        // the overflow could technically cover every speaker alone. Reverting to the
        // non-amplified TCM-1 here would violate that rule, so pendant mics are
        // excluded from this optimization.
        if (ampQty > 0 && !isPendant) {
          const coveredSpeakers = ampQty * 4;
          if (spkrCount <= coveredSpeakers && ampBaseUnits > 0) {
            noAmpUnits += ampBaseUnits;
            ampBaseUnits = 0;
          }
        }
      } else if (isPendant && !isWallSpeakerConfig && !isPendantSpeakerConfig) {
        // Pendant mic + ceiling C-IC6 speakers: the TCM-1A head unit has an integrated
        // 2-channel amp driving up to 4 C-IC6 (2 per channel), so prefer it and only
        // overflow to an external amp (8 C-IC6 each). Unlike the ceiling-mic head-amp
        // path below, this is NOT gated by the `useHeadAmp` cost heuristic (which is
        // disabled for Devio) — the pendant head unit is the correct choice for every
        // family, so Devio designs use TCM-1A here rather than a standalone DPA-450BP.
        const requiredAmps = ceil(spkrCount / 4);
        ampBaseUnits = Math.min(requiredAmps, baseUnits);
        noAmpUnits = baseUnits - ampBaseUnits;
        const remainingSpkrs = spkrCount - ampBaseUnits * 4;
        ampQty = remainingSpkrs > 0 ? ceil(remainingSpkrs / 8) : 0;
        // Optimization check (spec §4.3): same redundant-head-amp reversion as above.
        if (ampQty > 0) {
          const coveredSpeakers = ampQty * 8;
          if (spkrCount <= coveredSpeakers && ampBaseUnits > 0) {
            noAmpUnits += ampBaseUnits;
            ampBaseUnits = 0;
          }
        }
      } else if (isTreeFrog) {
        // Tree Frog (Devio) ceiling mics default to the standalone DPA-450BP amp
        // here, unlike the Parlé Case B formula below — the integrated-amp option
        // (DCM-XA) is only selected by the separate port-budget-aware auto-swap
        // (evaluateAmpSwap / planCeilingAmplification), which needs the amp count
        // to start from the standalone branch to reason about freed UCC peripheral
        // ports. Applying the unconditional head-amp-first formula here would
        // promote DCM-XA before that swap has a chance to run.
        ampBaseUnits = 0;
        noAmpUnits = baseUnits;
        ampQty = ceil(spkrCount / 8);
      } else if (useHeadAmp && baseUnits >= ceil(spkrCount / 4)) {
        // Plain ceiling mic (TCM-X/TCM-XA) + C-IC6 speakers — spec §4.3 Case B,
        // gated by the §3.1.1 `useHeadAmp` cost heuristic (per spec, that
        // heuristic is the general ceiling/pendant-speaker cost rule, not just
        // the bundle-purchase decision). Option 1: the speaker count sits in the
        // head-amp-efficient 1-4/9-12/... range AND enough base units exist to
        // cover it outright — amplify exactly what's needed, no AMP-450BP.
        ampBaseUnits = ceil(spkrCount / 4);
        noAmpUnits = baseUnits - ampBaseUnits;
        ampQty = 0;
      } else {
        // Option 2: either the speaker count is in the 5-8/13-16/... range
        // (2+ head amps would cost more than one AMP-450BP) or there aren't
        // enough base units to cover the required head amps outright — route
        // everything through a standalone AMP-450BP instead.
        ampBaseUnits = 0;
        noAmpUnits = baseUnits;
        ampQty = ceil(spkrCount / 8);
      }

      // Tree Frog port-budget cap. Each standalone amp consumes one UCC peripheral
      // port; mic chains consume the rest. `maxStandaloneAmpPortsFor` returns the
      // remaining ports (2 - micChains). For non-Devio designs it returns Infinity,
      // making this clamp a no-op. Excess amps that get dropped here are reported
      // separately by the overflow toast so the user knows to reduce mic chains
      // or swap to DCM-XA.
      // wall speakers use a rack-level Tesira AMP-450BP (not a Devio DPA-450BP on the
      // UCX peripheral bus), so they don't consume a UCC port — exclude them from the cap or the
      // amp gets wrongly dropped for Devio wall-speaker configs.
      if (isTreeFrog && ampQty > 0 && !isWallSpeakerConfig) {
        ampQty = Math.min(ampQty, maxStandaloneAmpPortsFor(area));
      }

      if (micCount > 0) {
        // Ceiling-tile mics have no expander unit type — each placed unit is its
        // own base. expanderUnits = 0 for BMA360D.
        const expanderUnits = isCeilingTileMic ? 0 : micCount - baseUnits;
        // Add extra microphones. finalMicModel is already family-resolved upstream, so a
        // Devio SCX (non-UCX) design emits Parlé TCM-X / TTM-X and a real UCX Android
        // design emits Devio DCM-X / DTM-X (PIG-594).
        const microphones = getMicrophones(finalMicModel, ampBaseUnits, noAmpUnits, expanderUnits);
        addToEquipmentList(microphones);
        // TCM-X cabling: 10' between base and mic for each expander + base; 25' from each base to DSP.
        if (!isCeilingTileMic) {
          addCables([0, (isTableTop ? 0 : expanderUnits + (isPendant ? 0 : baseUnits)), baseUnits])
        } else {
          // BMA360D: each tile gets its own 25' home-run cable to the DSP — 1 per
          // tile (`baseUnits`), confirmed with the spec owner. This is independent of
          // whether an AMP-450BP is also needed for speaker overflow (that's a
          // separate DSP→AMP-450BP run, added below by the amp block via
          // `addCables([0, -ampQty, ampQty])`). Previously this branch added nothing
          // at all — the removed comment here claimed BMA360D cabling was "handled
          // separately" via a Cat 6 daisy-chain, but no such handling exists anywhere
          // in this function, so on-board-only configs (ampQty = 0) were silently
          // missing their DSP→tile cable entirely; configs that happened to also need
          // an AMP-450BP got exactly 1 such cable by coincidence, regardless of tile
          // count, because the amp block's cable was covering for a different purpose.
          addCables([0, 0, baseUnits]);
        }

        // PIG-453 (WDT-142): TCM-1A is an exception in the head-unit lineup — unlike
        // TCM-XA/DCM-XA, it has no RJ-45 port, so each amplified pendant head unit
        // (TCM-1A, selected as the à-la-carte `ampBaseUnits` for pendant mics) always
        // needs 2 extra CCA adapters for its own connectivity, on top of whatever CCA
        // the downstream speaker wiring requires. Non-amplified pendant bases (TCM-1,
        // counted in `noAmpUnits`) have no amp outputs and get no CCA here.
        //
        // This applies regardless of speaker type: for P6-SM pendant speakers this is
        // IN ADDITION TO (not a substitute for) the PIG-450 daisy-chain CCA count in the
        // speaker block below — the two counts cover distinct connections (the head
        // unit's own missing RJ-45 vs. the amp-channel-to-speaker wiring) and both
        // accumulate via the shared addCCA helper.
        if (isPendant && ampBaseUnits > 0) {
          addCCA(2 * ampBaseUnits);
        }
      }

      // Add extra speakers
      if (spkrCount > 0) {
        const speakers = find(equipmentCatalog.Speakers, spkr => spkr.modelName && finalSpkrModel && spkr.modelName.includes(finalSpkrModel));
        // PIG-123: guard the speaker line so an unknown à-la-carte model doesn't silently
        // drop the speaker (and its cabling). Surfacing it prevents an à-la-carte config from
        // quietly producing a zero-speaker / zero-cable equipment list. Per-speaker cable
        // quantities are defined in the WDT stories (PIG-450/452/454); this guard only makes
        // the fall-through visible.
        if (speakers) {
          addToEquipmentList(speakers, spkrCount);
        } else {
          console.warn('calculateEquipmentTable: à-la-carte speaker not found in catalog — no speaker/cable line added', { finalSpkrModel });
        }

        // PIG-454 (WDT-138): wall speakers each require a CCA adapter (Euroblock→Cat5e).
        // Quantity = total number of wall speakers, regardless of the AMP-450BP count.
        // Routed through the accumulating addCCA helper (PIG-452) so it sums with any
        // TCM-1A / P6-SM CCA lines instead of overwriting them.
        if (isWallSpeakerConfig) {
          addCCA(spkrCount);
        }
        else if (isPendantSpeakerConfig && isCeilingTileMic) {
          // PIG-591 (§5 Case B): BMA360D + P6-SM. P6-SM driven by the BMA360D onboard amp
          // take 2 CCA each — one at the amp/phoenix end and one at the speaker/bare-wire
          // end, no daisy chaining (each connects directly to its own onboard channel).
          // Only P6-SM that overflow to an external AMP-450BP use the WDT-139 daisy-chain
          // formula, sized against that amp's own channels (4 for P6-SM). onboard capacity
          // = 4 × BMA360D unit count (matches the amp block above).
          const onboardCapacity = ONBOARD_AMP_CAPACITY * baseUnits;
          const onboardDriven = Math.min(spkrCount, onboardCapacity);
          const overflowSpkrs = spkrCount - onboardDriven;
          addCCA(onboardDriven * 2 + ccaDaisyChainQty(overflowSpkrs, ampQty * 4));
        }
        // PIG-450 (WDT-139): P6-SM on a TCM-XA / TCM-1A head amp or tabletop-mic external
        // amp use daisy-chain CCA sizing. ch = total available amp channels = amplified head
        // units × 2 (TCM-XA / TCM-1A, both 2-channel, counted by `ampBaseUnits`) + external
        // AMP-450BP × 4 (counted by `ampQty`). Speakers within ch connect directly (1 CCA
        // each); excess speakers are daisy-chained (2 CCA each).
        else if (isPendantSpeakerConfig) {
          const ch = ampBaseUnits * 2 + ampQty * 4;
          addCCA(ccaDaisyChainQty(spkrCount, ch));
        }
        // BMA360D-Equipment-Selection-Logic.md §5 Case A: C-IC6 speakers driven by the
        // BMA360D on-board amp need 1× CCA each (BMA phoenix output → CCA → Cat5e →
        // C-IC6 RJ-45) — the on-board amp's terminal-block output needs the adapter,
        // unlike TCM-XA/AMP-450BP which connect to C-IC6 directly (baseline rule: "CCA
        // ... none needed for C-IC6"). Only the on-board-driven speakers need it; any
        // overflow driven by an external AMP-450BP follows that no-CCA rule same as the
        // plain à la carte C-IC6 path above. `onboardCapacity` mirrors the amp block
        // above (`ONBOARD_AMP_CAPACITY * baseUnits`) and the analogous P6-SM+BMA360D
        // branch two cases up.
        else if (isCeilingTileMic) {
          const onboardCapacity = ONBOARD_AMP_CAPACITY * baseUnits;
          const onboardDriven = Math.min(spkrCount, onboardCapacity);
          addCCA(onboardDriven);
        }
      };
    }

    // Add extra amps if required
    if (ampQty > 0) {
      // Wall speakers always use the Tesira AMP-450BP for amplification, for EVERY family
      // (rack-level amp, not a Devio UCX peripheral) — so Devio wall-speaker configs list the
      // AMP-450BP too (PIG-600). Otherwise: Devio UCX (Tree Frog) → Devio DPA-450BP (a PoE amp
      // on a UCX hub port); pendant / BMA360D-tile non-UCX → Tesira AMP-450BP; everything else —
      // including Devio SCX, which has no UCX hub to host a DPA-450BP — → AMP-450 family. Gate
      // the DPA-450BP branch on isDevioUcxDesign (Devio family + Android), not isTreeFrog
      // (= any Devio), so SCX is not handed an unconnectable DPA-450BP (PIG-594; Tree Frog UC
      // Platform §4.5 names DPA-450BP only in the UCX Android paths).
      let amplifiers;
      if (isWallSpeakerConfig) {
        amplifiers = find(equipmentCatalog.Amplifiers, amp => amp.modelName && amp.modelName.includes('AMP-450BP'));
        if (!amplifiers) {
          console.warn('AMP-450BP not found in catalog');
        }
      } else if (isDevioUcxDesign(area)) {
        amplifiers = find(equipmentCatalog.Amplifiers, amp => amp.modelName && amp.modelName.includes('DPA-450BP'));
        if (!amplifiers) {
          console.warn('Tree Frog amplifier (DPA-450BP) not found in catalog');
        }
      } else if (isPendant || isCeilingTileMic) {
        // Pendant systems and BMA360D ceiling-tile mics (PIG-481/WDT-78) use the
        // Tesira AMP-450BP for external amplification.
        amplifiers = find(equipmentCatalog.Amplifiers, amp => amp.modelName && amp.modelName.includes('AMP-450BP'));
        if (!amplifiers) {
          console.warn('AMP-450BP not found, using AMP-450');
          amplifiers = find(equipmentCatalog.Amplifiers, amp => amp.modelName.includes('AMP-450'));
        }
      } else {
        amplifiers = find(equipmentCatalog.Amplifiers, amp => amp.modelName.includes('AMP-450'));
      }
      addToEquipmentList(amplifiers, ampQty);
      // AMP-450BP comes with a short jumper for the speaker it's attached to.
      // PIG-452 (WDT-141): DSP→AMP run length depends on where the amp lives — wall
      // speakers keep their amp at rack level (3' jumper, per the cable reference
      // table); ceiling/pendant overflow amps sit up with the speakers (25' run).
      // Previously always used 25' here, even for wall-speaker configs.
      if (isWallSpeakerConfig) {
        addCables([ampQty, -ampQty, 0]);
      } else {
        addCables([0, -ampQty, ampQty]);
      }
    };

    // PIG-481 (WDT-78): the BMA360D on-board amplifier is powered by a PoE injector.
    // Add it as an accessory line item only when the on-board amp is actively driving
    // speakers (escalation remainder 1–4, or any wall-speaker bridge config). When
    // AMP-450BP units alone absorb all speakers, the on-board amp is idle and no
    // injector is added.
    if (poeInjectorNeeded) {
      const poeInjector = find(equipmentCatalog.Accesories, acc => acc.isPoeInjector);
      if (poeInjector) {
        addToEquipmentList(poeInjector, 1);
      } else {
        console.warn('PoE injector (910.3200.209) not found in catalog');
      }
    }

    // Add TC-5 or AVB switches
    const totalDevicesNeedingPorts = (dspCount - 1) + ampBaseUnits + noAmpUnits + ampQty;  // all devices requiring connection
    const totalAvailablePorts = 4 * dspCount;  // all ports provided by DSPs
    const requiredExtraPorts = totalDevicesNeedingPorts - totalAvailablePorts;

    if (requiredExtraPorts <= 13) {
      const tcUnits = max([0, ceil(requiredExtraPorts / 3)]);
      if (tcUnits > 0) {
        const TC5 = find(equipmentCatalog.Switches, swch => swch.modelName.includes('TC-5'));
        addToEquipmentList(TC5, tcUnits);
        addCables([tcUnits, 0, 0]);
      }
    } else {
      const switches = getSwitches(requiredExtraPorts);
      addToEquipmentList(switches);
      const switchQty = switches.reduce((acc, curr) => acc += curr[1], 0)
      addCables([switchQty, 0, 0]);
    };

    // Add accessories

    // Add ceiling microphone pole accessories if needed.
    // PIG-420: ceiling-tile mics (BMA360D family) don't use TCM-X pole accessories —
    // Biamp doesn't sell aftermarket mounting hardware for below-ceiling BMA360D
    // installs; that case is surfaced as a note on the equipment list (PIG-399).
    if (isCeiling && !isCeilingTileMic && micHeight && area.ceilingHeight) {
      const ceilingHeight = area.ceilingHeight.min || area.ceilingHeight;
      const mountingDistance = ceilingHeight - micHeight;

      // Only add pole accessories if microphone is not at ceiling height
      if (Math.abs(mountingDistance) > 0.01) { // Small tolerance for floating point comparison
        // Check if this is a TCM-X microphone to use appropriate mounting accessories
        const isTCMX = finalMicModel && finalMicModel.toUpperCase().includes('TCM-X');

        const poleAccessories = isTCMX
          ? calculateTCMXAccessories(mountingDistance, micQty)
          : calculatePoleAccessories(mountingDistance, micQty);

        poleAccessories.forEach(accessory => {
          // Pass the accessory object without the quantity field since makeTableObject expects it separately
          const { quantity, ...accessoryData } = accessory;
          addToEquipmentList(accessoryData, quantity);
        });
      }
    }

    // Calculate Speaker cables
    // Pendant mics don't have RJ-45 so we only need 8 * AMP-450 (others use speaker wire). Otherwise all speakers are wired with Cat5
    let spkrCables = isPendant ? 8 * ampQty : spkrCount;

    // Calculate BPAK
    // BPAK mounts on a ceiling speaker, so it only applies when the speakers are ceiling-mounted.
    // `isCeiling` is derived from the microphone (ceiling/tile mics), so on its own it wrongly
    // included BPAK for à-la-carte configs pairing a ceiling(-tile) mic with pendant or wall
    // speakers. Gate on the speaker mounting as well so BPAK follows the speaker, not the mic.
    //
    // Two independent sources of BPAK for ceiling C-IC6 speakers, confirmed with the spec
    // owner (see Documentation/specs/bma360d-onboard-amp-and-bpak-amendment.md §2 —
    // supersedes the prior "TCM-XA only" reading of Spec §4.6):
    //  1. TCM-XA / amplified-base-unit brackets — one per amplified head unit
    //     (`ampBaseUnits`), gated on `isCeiling` (mic) since only a ceiling amplified
    //     head unit has a module to bracket.
    //  2. AMP-450BP brackets — one per external AMP-450BP unit (`ampQty`) that drives
    //     ceiling C-IC6 speakers, regardless of mic type (TCM-X, TCM-XA, or BMA360D).
    //     Each AMP-450BP mounts via BPAK on one of the ceiling speakers it drives.
    // These two sources add together rather than being mutually exclusive — a config
    // could in principle have both amplified head units and overflow AMP-450BP units at
    // once. The total is capped at `spkrCount` since a BPAK can't be mounted on more
    // speakers than exist.
    const isCeilingSpeakerConfig = speakers?.mountingType === 'ceiling'
      || constants.equipment?.speakers?.[speakers?.model]?.isCeilingSpkr === true;
    const tcmXaBpakQty = (isCeiling && isCeilingSpeakerConfig) ? ampBaseUnits : 0;
    const amp450bpBpakQty = isCeilingSpeakerConfig ? ampQty : 0;
    const bpakQty = Math.min(tcmXaBpakQty + amp450bpBpakQty, spkrCount);


    // Adjust speaker cables - BPAK comes with a short adapter cable for the speaker it's mouted on.
    // Only subtract for the speakers actually getting a BPAK (bpakQty, already capped at spkrCount).
    spkrCables -= bpakQty;
    addCables([0, spkrCables, 0]);

    // Add cables 
    const cablesToAdd = equipmentCatalog.Cables.filter(cable => cables[cable.length] > 0).map(cable => [cable, cables[cable.length]]);
    addToEquipmentList(cablesToAdd);

    // Add BPAK
    if (bpakQty > 0) {
      const bpak = find(equipmentCatalog.Accesories, acc => acc.modelName.includes('BPAK'));
      addToEquipmentList(bpak, bpakQty);
    };

  }

  // Add camera (applies to both bar and DSP systems). Skip when a Tree Frog
  // room bundle (MRB-UCX*) already covers the camera via its `includes.camera`
  // field — otherwise the Vidi 280 surfaces twice (once in the bundle SKU and
  // once as a separate line).
  const treeFrogBundleCoversCamera = treeFrogMatch?.type === 'room'
    && Boolean(treeFrogMatch.bundle?.includes?.camera);
  if (cameraModel && cameraQty > 0 && !treeFrogBundleCoversCamera) {
    const camera = find(equipmentCatalog.Cameras, cam => cam.modelName === cameraModel);
    if (camera) {
      addToEquipmentList(camera, cameraQty);
    }
  }

  // Add UC Compute devices based on ucCompute selection
  // Only add external UC Compute device if not using any TSC2DIP bundle
  // (All TSC2DIP bundles have integrated UC Compute - either via VBC bars or TesiraSCIP speakers)
  if (area.ucCompute && area.ucCompute !== 'None') {
    // Check if we're using any TSC2DIP bundle (which has integrated UC Compute)
    const hasTSC2DIPBundle = Object.keys(equipmentList).some(key =>
      key.includes('TSC2DIP')
    );
    // Tree Frog (Android) ships UCC-UCX*-T as its compute — adding UCC-Lenovo
    // on top would surface two UC compute lines for the same design.
    // equipmentList is keyed by the display `modelName`, which (PIG-546) now
    // carries a "Biamp " prefix for these bundles — match with or without it.
    const hasUcxBundle = Object.keys(equipmentList).some(key =>
      key.startsWith('UCC-UCX') || key.startsWith('MRB-UCX') ||
      key.startsWith('Biamp UCC-UCX') || key.startsWith('Biamp MRB-UCX')
    );

    // Add external UC Compute device only if NOT using any TSC2DIP bundle
    if (!hasTSC2DIPBundle && !hasUcxBundle && equipmentCatalog.UCCompute) {
      let ucComputeDevice = null;

      if (area.ucCompute === 'Microsoft Teams') {
        ucComputeDevice = find(equipmentCatalog.UCCompute, device =>
          device.platform === 'Microsoft Teams'
        );
      } else if (area.ucCompute === 'Zoom Rooms') {
        ucComputeDevice = find(equipmentCatalog.UCCompute, device =>
          device.platform === 'Zoom Rooms'
        );
      }

      if (ucComputeDevice) {
        addToEquipmentList(ucComputeDevice, 1);
      }
    }
  }

  // Add PMA 2000-DM display mount for WALL-MOUNTED conferencing bars only
  // Exclude ceiling-mounted bars like CBC 2500 CM/PM
  const isCBCBar = finalMicModel && finalMicModel.includes('CBC 2500');
  const isWallMountedBar = isBarDevice && !isCBCBar;

  const hasWallMountedBar = isWallMountedBar || Object.keys(equipmentList).some(key => {
    // Exclude CBC bars (ceiling-mounted)
    if (key.includes('CBC 2500')) {
      return false;
    }
    // Check for wall-mounted standalone bars (with spaces)
    if (key.includes('ABC 2500a') || key.includes('VBC 2500a') || key.includes('VBC 2800')) {
      return true;
    }
    // Check for bundles containing wall-mounted bars (without spaces in bundle names)
    // Handles: MRB-VBC 2500a-TSC2DIP-T/Z and MRB-VBC 2800-TSC2DIP-T/Z
    if (key.includes('MRB-VBC 2500a') || key.includes('MRB-VBC 2800')) {
      return true;
    }
    return false;
  });

  if (hasWallMountedBar) {
    const displayMount = find(equipmentCatalog.Accesories, acc => acc.modelName === 'PMA 2000-DM');
    if (displayMount) {
      addToEquipmentList(displayMount, 1);
    }
  }

  return Object.values(equipmentList)
}

// HELPER Functions
/**
* @function makeTableObject
* @description Converts equipment data into standardized table entry format.
* 
* @param {Object} equipment - Equipment data object
* @param {string} equipment.modelName - Model name of the equipment
* @param {string} [equipment.partNumber] - Part number for the equipment
* @param {string} equipment.description - Description of the equipment
* @param {number} [qty=1] - Quantity of the equipment
* @returns {Array|null} Array containing [model, tableData] or null if equipment is empty
* 
* @description
* Creates table entry by:
* 1. Validating equipment data
* 2. Generating product link if part number exists
* 3. Formatting data into standard table structure
* 
* @example
* const result = makeTableObject({
*   modelName: 'TCM-X',
*   partNumber: '123456',
*   description: 'Ceiling microphone'
* }, 2);
* 
* // Returns [
* //   'TCM-X',
* //   {
* //     qty: 2,
* //     product: {
* //       name: 'TCM-X',
* //       link: 'https://products.biamp.com/product-details/-/o/ecom-item/123456'
* //     },
* //     description: 'Ceiling microphone'
* //   }
* // ]
* 
* @note
* - Returns null if equipment array is empty
* - Link is only generated if part number is available
* - Uses Biamp product URL structure
*/
const makeTableObject = (equipment = {}, qty = 1) => {
  if (!equipment || !equipment.modelName) return null;
  const model = equipment.modelName;
  const partNumber = equipment?.partNumber ?? null;
  const link = partNumber ? `https://products.biamp.com/product-details/-/o/ecom-item/${partNumber}` : null;
  const description = equipment.description;
  return [model, { qty, product: { name: model, link }, description }]
};

/**
* @function getBundle
* @description Determines appropriate equipment bundle based on room requirements.
*
* @param {string} family - Product family (e.g., 'Devio')
* @param {string} micModel - Microphone model
* @param {number} micQty - Number of microphones needed
* @param {string} spkrModel - Speaker model
* @param {number} spkrQty - Number of speakers needed
* @returns {Object|null} Bundle object if suitable match found, null otherwise
*
* @description
* Selects appropriate bundle by:
* 1. Validating quantity limits (max 4 mics, 16 speakers)
* 2. Determining microphone type and requirements:
*    - Tabletop: Medium bundle for 1 mic/≤2 speakers, Large otherwise
*    - Ceiling: Bundle selection based on speaker amplification needs
* 3. Evaluating speaker amplification strategy:
*    - Uses head amp for speaker counts 1-4, 9-12
*    - Defers to AMP-450BP for counts 5-8, 13-16 (more cost-effective)
*
* @note
* - Returns null if quantities exceed limits
* - Bundle selection considers cost efficiency
* - Different logic for tabletop vs ceiling microphones
* - Optimizes for speaker amplification needs
*/
const getBundle = (family, micModel, micQty, spkrModel, spkrQty) => {
  // Validate inputs
  if (!micModel || !spkrModel || !family) {
    console.warn('getBundle: Missing required parameters', { family, micModel, spkrModel });
    return null;
  }

  // Devio bundles only support standard CIC6 speakers (not LP or TAA variants)
  if (family === 'Devio') {
    const isStandardCIC6 = spkrModel.includes('C-IC6') &&
                          !spkrModel.includes('LP') &&
                          !spkrModel.includes('TAA');
    if (!isStandardCIC6) {
      return null;
    }
  }

  if (micQty > 4 || spkrQty > 16) return null;
  const isTableTop = find(equipmentCatalog.Microphones, mic => mic.modelName && mic.modelName.includes(micModel))?.isTableTop ?? false;
  // useHeadAmp logic: returns true for speaker counts 1~4, 9~12, 17~20, etc. Either bundle has a single amp which supports <=4 speakers. From 4~8 (and 13~16) it's more cost effective to use an AMP-450BP whicch is not in a bundle
  const useHeadAmp = spkrQty % 8 > 0 && spkrQty % 8 <= 4;
  const [searchMicQty, searchSpkrQty] = isTableTop ?
    (micQty === 1 ? [1, 2] : [2, 4]) : // tabletop mic logic: Medium for 1 mic or ≤2 speakers, Large otherwise
    (!useHeadAmp ? [] : (micQty === 1 ? [1, 2] : [2, 4])); // Ceiling mic logic: Requires useHead to be true

  const bundle = find(equipmentCatalog.Bundles, bundle =>
    bundle && bundle.productFamily === family &&
    bundle.includes && bundle.includes.micModel && micModel.includes(bundle.includes.micModel) &&
    searchMicQty <= bundle.includes.micQty &&
    bundle.includes.spkrModel && (spkrModel === bundle.includes.spkrModel || spkrModel.endsWith(bundle.includes.spkrModel)) &&
    searchSpkrQty <= bundle.includes.spkrQty
  );
  return bundle
};

/**
* @function getProcessors
* @description Calculates minimum required DSP processors based on microphone count.
* 
* @param {string} family - Product family name
* @param {number} micCount - Total number of microphones to support
* @returns {Array<Array>} Array of processor pairs [processor, quantity]
* 
* @description
* Determines optimal DSP configuration by:
* 1. Starting with total microphone count
* 2. Iteratively selecting processors:
*    - Finds processor with closest matching capacity
*    - Defaults to 16-channel processor if no exact match
*    - Aggregates same processor models
* 3. Continues until all microphones are supported
* 
* @example
* const processors = getProcessors('Parlé', 20);
* // For 20 microphones might return:
* // [
* //   [{ model: 'TesiraFORTÉ X 400', capacity: 16 }, 1],
* //   [{ model: 'TesiraFORTÉ X 100', capacity: 8 }, 1]
* // ]
* 
* @note
* - Optimizes for minimum number of processors
* - Considers processor capacity limits
* - Groups identical processor models
* - Maintains product family compatibility
*/
const getProcessors = (family, micCount) => {
  let counter = micCount;
  const processors = [];
  
  // Default to Tesira if no family specified
  const productFamily = family || 'Tesira';
  
  while (counter > 0) {
    const dsp = find(equipmentCatalog.Processors, dsp => dsp.productFamily === productFamily && (counter <= dsp.capacity || dsp.capacity === 16));
    
    // If no DSP found, try to find any DSP as fallback
    const fallbackDsp = dsp || find(equipmentCatalog.Processors, p => p.capacity >= 4);
    
    if (!fallbackDsp) {
      console.warn('No suitable DSP found for', productFamily, 'with', counter, 'mics');
      break;
    }
    
    const index = findIndex(processors, processor => processor[0] === fallbackDsp);
    index >= 0 ? processors[index][1] += 1 : processors.push([fallbackDsp, 1]);
    counter -= fallbackDsp.capacity;
  };
  return processors
};

/**
* @function getMicrophones
* @description Generates list of required microphone units based on system configuration.
* 
* @param {string} micModel - Base microphone model
* @param {number} ampBaseUnits - Number of amplified base units needed
* @param {number} noAmpUnits - Number of non-amplified base units needed
* @param {number} expanderUnits - Number of expander units needed
* @returns {Array<Array>} Array of microphone pairs [microphone, quantity]
* 
* @description
* Builds microphone list by:
* 1. Processing three unit types:
*    - Amplified base units (with built-in amplification)
*    - Non-amplified base units (standard processing)
*    - Expander units (additional coverage)
* 2. Finding matching microphone models from catalog
* 3. Creating quantity pairs for each type
* 
* @example
* const mics = getMicrophones('TCM-X', 2, 1, 3);
* // Returns:
* // [
* //   [{ modelName: 'TCM-XA', isAmpUnit: true, ... }, 2],
* //   [{ modelName: 'TCM-X', isBaseUnit: true, ... }, 1],
* //   [{ modelName: 'TCM-XEX', isExpanderUnit: true, ... }, 3]
* // ]
* 
* @note
* - Only includes units with non-zero quantities
* - Matches microphones based on model and unit type
* - Uses equipment catalog for model lookup
* - Maintains product family consistency
*/
const getMicrophones = (micModel, ampBaseUnits, noAmpUnits, expanderUnits) => {
  // Tree Frog's DCM-XA carries BOTH `isAmpUnit` and `isBaseUnit` (it serves
  // dual purpose as chain base + integrated amp). A plain `isBaseUnit` search
  // would match DCM-XA first and surface the amplified head when the caller
  // asked for the plain base unit. Predicate the base-unit search on
  // `!isAmpUnit` so DCM-X / TCM-X / DTM-X / TTM-X land correctly.
  const predicates = [
    (m) => m.isAmpUnit,
    (m) => m.isBaseUnit && !m.isAmpUnit,
    (m) => m.isExpanderUnit,
  ];
  // Variants are resolved by substring against the base model name. That works for the
  // TCM-X/DCM-X/TTM-X families whose base name is a prefix of their amp/expander names,
  // but the pendant base is catalogued as "Parlé TCM-1 pendant" — the " pendant" suffix is
  // absent from "Parlé TCM-1A"/"Parlé TCM-1EX", so it would fail to match them. Strip it so
  // all three pendant variants resolve from the shared "Parlé TCM-1" stem.
  const searchModel = typeof micModel === 'string'
    ? micModel.replace(/\s*pendant$/i, '')
    : micModel;
  return [ampBaseUnits, noAmpUnits, expanderUnits].reduce((mics, qty, index) => {
    if (qty > 0) {
      const mic = find(equipmentCatalog.Microphones, m => m.modelName && searchModel && m.modelName.includes(searchModel) && predicates[index](m))
      if (mic) mics.push([mic, qty])
    }
    return mics
  }, []);
};

/**
* @function getSwitches
* @description Calculates minimum required network switches based on AVB port requirements.
* 
* @param {number} AVBPorts - Total number of AVB ports needed
* @returns {Array<Array>} Array of switch pairs [switch, quantity]
* 
* @description
* Determines optimal switch configuration by:
* 1. Starting with total port requirement
* 2. Iteratively selecting switches:
*    - Finds switch with closest matching port count
*    - Defaults to 24-port switch if no exact match
*    - Aggregates same switch models
* 3. Continues until all port requirements are met
* 
* @example
* const switches = getSwitches(30);
* // For 30 ports might return:
* // [
* //   [{ modelName: 'AVB-SW-24', ports: 24 }, 1],
* //   [{ modelName: 'AVB-SW-8', ports: 8 }, 1]
* // ]
* 
* @note
* - Optimizes for minimum number of switches
* - Considers switch port capacity limits
* - Groups identical switch models
* - Similar logic to getProcessors but for network infrastructure
*/
const getSwitches = (AVBPorts) => {
  let counter = AVBPorts;
  const switches = [];
  while (counter > 0) {
    const swch = find(equipmentCatalog.Switches, swch => counter <= swch.ports || swch.ports === 24);
    const index = findIndex(switches, sw => sw[0] === swch);
    index >= 0 ? switches[index][1] += 1 : switches.push([swch, 1]);
    counter -= swch.ports;
  };
  return switches
};

/**
 * Shared Steps 3–8 of the bar + expansion device placement algorithm, used by BOTH the
 * expansion ceiling mic and expansion speaker placements (the spec defines them as the same
 * routine with substitutions). Steps 1 (barReach) and 2 (coverage radius) are device-specific
 * and computed by each caller, then handed in here.
 *
 * Differences between the two callers are expressed purely as parameters:
 *   - `overlapMap`        : the device's defBar*Overlap map ({ focused, full_room }); drives both
 *                           the Step-5 pull-back distance and the Step-7 grid spacing.
 *   - `pullBackEnabled`   : pull the expansion box back into the bar zone by radius × overlap,
 *                           so the first device's coverage meets the bar's coverage boundary.
 *                           Both speakers (spec 8.3.2) and ceiling mics (ceiling-mic spec Step 4)
 *                           specify this; it is on for both.
 *   - `maxCount`          : platform cap (maxExpansionMics vs maxExpansionSpeakers).
 *   - `model` / `modelName` and `manualAdd` (mic-only residual bypass).
 *
 * Step 7 derives the grid directly from the target overlap (NOT getOverlaps/closest, whose
 * band-edge snapping mis-picked the count — spec 8.6.1 expects 4, the old pick returned 2),
 * then applies the platform cap via a near-square grid.
 *
 * @returns {{ coords: [], qty: 0, reason: 'full-coverage' | 'below-min-residual' }}
 *   No devices placed. `reason` discriminates the two causes, which look identical in
 *   `coords`/`qty` but mean opposite things to a caller deciding whether the expansion config is
 *   still wanted:
 *     - `'full-coverage'`     — edge case A: the bar covers the entire base box, so there is no
 *                               residual at all and expansion is genuinely not required.
 *     - `'below-min-residual'` — edge case B: a real gap exists but is shallower than
 *                               radius × √2, i.e. too small to be worth a device. The expansion
 *                               config is still meaningful; only this particular gap isn't.
 *   Conflating them is how the table path's KNOWN SPEC DIVERGENCE arose — see
 *   `computeTableExpansionViaPath` in calculateDevicesMiddleware.ts.
 * @returns {{ coords, qty, expansionBox }}          - placement computed
 */
const calcBarExpansionPlacement = ({
  room,
  baseBox,
  isFocused,
  radius,
  barReach,
  overlapMap,
  pullBackEnabled,
  maxCount,
  model,
  modelName,
  manualAdd = false,
  layout = 'centered',
  enforcePairing = false,
  nearbyMics = [],
  minRowsFocused = 1,
  farClampMargin = 0,
}) => {
  const barCoverageDepth = room.left + barReach;


  // Step 3 (edge case A): bar covers the entire expansion area
  if (barCoverageDepth >= baseBox.left + baseBox.length) {
    return { coords: [], qty: 0, reason: 'full-coverage' };
  }

  const coverageMode = isFocused ? 'focused' : 'full_room';
  const targetOverlap = overlapMap[coverageMode];
  const pullBack = pullBackEnabled ? radius * targetOverlap : 0;

  // Step 4 / Step 5: expansion box. Focused mode with the bar not reaching the box → full box.
  // Otherwise start at the bar's coverage depth, pulled back by `pullBack` (speakers overlap the
  // bar zone for a seamless transition; mics start exactly at barReach with pullBack = 0).
  let expansionBox;
  if (isFocused && barCoverageDepth <= baseBox.left) {
    expansionBox = { ...baseBox };
  } else {
    const expansionLeft = Math.max(baseBox.left, barCoverageDepth - pullBack);
    expansionBox = { ...baseBox, left: expansionLeft, length: (baseBox.left + baseBox.length) - expansionLeft };
  }

  // Step 6 (edge case B): the area the bar genuinely leaves uncovered is too shallow to be worth
  // a device. Suppresses the AUTO recommendation only; an explicit manual "+ Add" (manualAdd)
  // bypasses it.
  //
  // Measured on the TRUE uncovered depth, NOT on expansionBox.length. The Step 4 pull-back
  // deliberately extends the box BACKWARDS into the bar's already-covered zone so the first
  // device's coverage circle meets the bar's boundary — it is a positioning device, not a
  // statement about how much area needs covering. Gating on the inflated box conflated the two:
  // because the pull-back distance (radius × overlap) exceeds this threshold (radius × √2) at the
  // full_room overlap of 1.6, edge case B became unreachable in full_room mode, so ANY room whose
  // far wall the bar missed — even by a centimetre — auto-recommended expansion. That is how the
  // default 20'×14' no-furniture room came to recommend a mic for a 0.496 m gap.
  // See Documentation/specs/bar-expansion-minimum-residual-amendment.md.
  //
  // The min() against baseBox.length covers edge case C (bar stops short of the box): there the
  // whole box is uncovered, and (far edge − barCoverageDepth) would overstate the gap by the
  // distance the bar never reached.
  const uncoveredDepth = Math.min(
    baseBox.length,
    (baseBox.left + baseBox.length) - barCoverageDepth
  );
  const minResidualDepth = radius * Math.SQRT2;
  if (!manualAdd && uncoveredDepth < minResidualDepth) {
    return { coords: [], qty: 0, reason: 'below-min-residual' };
  }

  // Step 7/8 — grid placement. Two strategies:
  //
  //  'anchored' (speakers): lay speakers out FROM the bar. The first column is anchored to the
  //    bar's coverage edge (overlapping it by the same amount neighbours overlap each other),
  //    then columns march outward at a fixed pitch until the far edge is covered, and rows fill
  //    the width. Count is by `ceil` (minus a sliver tolerance, see below) so coverage reaches the
  //    far edge/width rather than being a side effect of centering a grid in a box.
  //
  //  'centered' (ceiling mics, default): the legacy behaviour — derive the grid from the target
  //    overlap and centre it in the expansion box. Preserved byte-for-byte for the mic path.
  //
  // NOTE: expansion SPEAKER placement no longer calls this with 'anchored' — it now goes through
  // the shared `calculateSpeakerPlacement` core (centered grid + shape dispatch), same as the
  // primary ceiling/pendant speaker. This branch is unreachable in production today (no caller
  // passes layout: 'anchored') but is left in place rather than deleted, since it's still exercised
  // directly by tests and isn't otherwise causing harm.
  const pitch = radius * targetOverlap;

  if (layout === 'anchored') {
    const farEdge = baseBox.left + baseBox.length;
    // Anchor: place the first column at the bar's coverage edge.
    // Ceiling speakers keep the +radius push-out so their coverage ring just MEETS the bar edge.
    // Pendant speakers DROP the push-out (first column sits on the bar edge) so their coverage
    // OVERLAPS the bar zone — matching how the ceiling coverage overlaps the bar in the layout.
    // If the bar doesn't reach the box (focused), start at the box's left edge instead.
    const barReachesBox = barCoverageDepth > baseBox.left;
    const isPendant = constants.equipment.speakers[model]?.isPendantSpkr === true;
    let x0 = (barReachesBox ? barCoverageDepth : baseBox.left) + (isPendant ? 0 : radius);
    x0 = Math.min(x0, farEdge - radius); // keep the first column inside the far wall

    // Sliver tolerance: don't add a whole extra column/row to cover less than
    // COVERAGE_SLIVER_TOL of a pitch. Without it, `ceil` over-provisions when the last unit
    // would barely contribute — e.g. a pendant's smaller radius bumps the focused band to a 2nd
    // row (band only ~0.14 m wider than one diameter) and adds a bunched far-end column for the
    // last ~0.37 m of the coverage-box margin. Applied to both axes.
    const COVERAGE_SLIVER_TOL = 0.25;
    // Column/row counts cover to the box far edge (ceil + 1, minus the sliver tolerance) so the grid
    // reaches the far/top/bottom walls rather than stopping short. Ceiling AND pendant use this same
    // count — pendant no longer rounds down, so a large room fills instead of leaving a far-end gap.
    const nCols = Math.max(1, Math.ceil((farEdge - radius - x0) / pitch - COVERAGE_SLIVER_TOL) + 1);
    // Row count from the box width, then a focused-mode floor so speakers straddle the table over
    // both seating sides instead of pointing down the centreline gap (see expansionSpeakerMinRowsFocused).
    const geomRows = Math.max(1, Math.ceil((baseBox.width - 2 * radius) / pitch - COVERAGE_SLIVER_TOL) + 1);
    const nRows = isFocused ? Math.max(geomRows, minRowsFocused) : geomRows;

    // Over platform capacity — coverage can't be guaranteed within the cap, so degrade to the
    // same capped near-square grid the centered path uses.
    if (nCols * nRows > maxCount) {
      const colCount = Math.round(Math.sqrt(maxCount));
      const rowCount = Math.round(maxCount / colCount);
      const capSpacing = { x: expansionBox.length / colCount, y: expansionBox.width / rowCount };
      const capCoords = calculateCoordinates(model, modelName, expansionBox, { x: colCount, y: rowCount }, capSpacing);
      return { coords: capCoords, qty: capCoords.length, expansionBox };
    }

    // Grid builder. Rows straddle the band centre; columns are EVENLY distributed across
    // [x0 … lastColX] (endpoints pinned, then shifted by `xShift` and clamped inside the box).
    // Even distribution avoids the far-edge clamp that would otherwise bunch the last two columns.
    // Pairing (opt-in via enforcePairing): a SINGLE row may be an odd count; a MULTI-row grid must
    // be even — if odd, the last row gets one extra column re-spaced evenly (a denser row, no
    // bunching). mics/other callers never enforce pairing, so they're unaffected.
    //
    // Clamp to the box FAR EDGE (not `farEdge − radius`): forcing the whole coverage ring inside
    // the box pulled the last column inward, and the even distribution then compressed EVERY
    // in-row gap below `pitch` (bunching). Letting the last column's centre reach the box edge lets
    // its ring poke into the empty floor between the table end and the wall (harmless — coverage
    // clips at the wall), so when the true-pitch endpoint fits, the columns land at exact `pitch`.
    const bandCenter = baseBox.top + baseBox.width / 2;
    // Cap column centres at the TABLE end, not the box far edge. The box extends `farClampMargin`
    // (table2wall, ~0.7 m) past the table; without this cap the last column could float that far
    // past the table end (over empty floor toward the wall). Capping at the table end keeps the
    // last pair over the head-of-table seat. Count still targets the box edge (so the last column
    // is kept), so this only pulls that column in — the even distribution may compress the row
    // slightly below pitch, but never below the 0.4×radius minimum overlap. Full-room mode: 0.
    const colClampMax = farEdge - (isFocused ? farClampMargin : 0);
    const lastColX = Math.min(x0 + (nCols - 1) * pitch, colClampMax);
    const buildGrid = (rows, xShift) => {
      const a = x0 + xShift;
      const b = lastColX + xShift;
      const colX = (cols, i) => {
        const gap = cols > 1 ? (b - a) / (cols - 1) : 0;
        return Math.min(Math.max(a + i * gap, baseBox.left), colClampMax);
      };
      // Pairing (even multi-row count) applies to CEILING only — C-IC6 is 2 speakers/channel, so an
      // even grid is preferred. Pendant (P6-SM, 1 speaker/channel) skips pairing: densifying an odd
      // multi-row grid produced a staggered last row that looked bunched, and it isn't needed
      // electrically. So pendant fills to the walls with a clean, uniform grid.
      const odd = enforcePairing && !isPendant && rows > 1 && (rows * nCols) % 2 === 1;
      const out = [];
      for (let j = 0; j < rows; j += 1) {
        const cols = (odd && j === rows - 1) ? nCols + 1 : nCols;
        const y = bandCenter + (j - (rows - 1) / 2) * pitch;
        for (let i = 0; i < cols; i += 1) {
          out.push({ model, modelName: modelName ?? model, location: { x: colX(cols, i), y } });
        }
      }
      return out;
    };

    let coords = buildGrid(nRows, 0);

    // Mic-aware re-space (legacy-style, ≤ 4 tries). Only runs for speakers that pass `nearbyMics`
    // (the non-tile paths — ceiling-with-tiles resolves proximity via tile snapping instead). If a
    // speaker lands within MIN_MIC_SPKR_DISTANCE_M of a mic, first slide the whole grid along its
    // rows to interleave with the mics; if a SINGLE row still can't clear (mics on the speaker
    // line), add a second row so the speakers straddle the mic line — which clears them AND makes
    // the count even. Keep the arrangement with the fewest collisions.
    if (enforcePairing && nearbyMics.length > 0) {
      const countCollisions = (cds) => cds.reduce((n, s) => n + (nearbyMics.some((m) => {
        const dx = (m.location?.x ?? 0) - s.location.x;
        const dy = (m.location?.y ?? 0) - s.location.y;
        return Math.hypot(dx, dy) < MIN_MIC_SPKR_DISTANCE_M;
      }) ? 1 : 0), 0);
      let bestCollisions = countCollisions(coords);
      if (bestCollisions > 0) {
        const tryArrangement = (rows, xShift) => {
          if (bestCollisions === 0) return;
          const g = buildGrid(rows, xShift);
          const c = countCollisions(g);
          if (c < bestCollisions) { bestCollisions = c; coords = g; }
        };
        // 1) slide the current rows to interleave with the mics
        for (const d of [pitch * 0.25, -pitch * 0.25, pitch * 0.5, -pitch * 0.5]) tryArrangement(nRows, d);
        // 2) still stuck on a single row → add a second row (straddle the mic line, even count).
        //    Bundle caps the count: adding a row doubles the speakers (2 × nCols), so only add it
        //    when the doubled count still fits the amp/bundle budget (maxCount = maxExpansionSpeakers).
        //    If a second row would exceed the budget, keep the best single-row slide (which may still
        //    collide → the caller shows the mic-proximity toast) rather than over-provisioning.
        if (bestCollisions > 0 && nRows === 1 && nCols * 2 <= maxCount) {
          for (const d of [0, pitch * 0.25, -pitch * 0.25]) tryArrangement(2, d);
        }
      }
    }

    return { coords, qty: coords.length, expansionBox };
  }

  // 'centered' (mic path): count from the TRUE residual, position anchored on the bar.
  //
  // ── Position ─────────────────────────────────────────────────────────────────────────────
  // One rule for capped and uncapped alike (they used to differ, which is what let the counts and
  // anchors drift apart): pin the first column one radius past the bar's coverage edge — the
  // k = 1 tangency the expansion TABLE mic path uses — pin the last column one radius inside the
  // far wall, and distribute evenly between. Rows inset one radius from each side wall.
  //
  // DELIBERATE DEVIATION from spec §5.5's "rectangular centred grid" and from §5.7's published
  // coordinates, which this changes: the 10 × 6 m Good example goes from 4 mics
  // (x {3.960, 7.800} × y {1.080, 4.920}) to 2 at x 7.800, y {2.400, 3.600} — still fully
  // covering its 4.4 m residual, with neither mic stranded inside the bar's zone.
  // Approved by the design owner 2026-08-28.
  // See Documentation/specs/ceiling-expansion-even-spread-amendment.md.
  const farEdge = baseBox.left + baseBox.length;
  //   x: first column one radius past the bar's coverage edge (its near edge lands exactly ON
  //      that edge, so nothing is spent re-covering the bar's zone); last column one radius
  //      inside the far wall (its far edge lands exactly ON the wall). Clamped so a bar that
  //      never reaches the box still starts one radius inside the box's own near edge.
  //   y: no bar anchor on this axis — inset one radius from each side wall, same reasoning.
  //
  // `top` is OPTIONAL on a coverage box — `calculateCoordinates` has always defaulted it to 0,
  // and callers that only care about the depth axis omit it entirely. Reading it raw made
  // `yFirst`/`yLast` NaN, which propagated through `axisCount` into `Array.from({ length: NaN })`
  // and silently produced ZERO mics. Default it here the same way.
  const boxTop = baseBox.top ?? 0;
  // Clamp the RESULT, not the input. `min(barCoverageDepth, farEdge − radius) + radius` clamped
  // the bar depth and then added a radius on top, so when the bar overshoots the box the anchor
  // landed on `farEdge` itself — a mic centred ON the far wall, half its coverage outside the
  // room. Taking the min after the offset keeps the first column at the tangency point when the
  // bar leaves room for it, and one radius inside the far wall when it doesn't.
  const xFirst = Math.max(baseBox.left + radius, Math.min(barCoverageDepth + radius, farEdge - radius));
  const xLast = farEdge - radius;
  const yFirst = boxTop + radius;
  const yLast = boxTop + baseBox.width - radius;

  // ── Count ────────────────────────────────────────────────────────────────────────────────
  // Counted over the span available to mic CENTRES — the same span the placement below spreads
  // across — NOT over the raw depth/width.
  //
  // Two errors this replaces, both from measuring one thing and placing by another:
  //
  //  1. `round(expansionBox.length / pitch)` measured the PULLED-BACK box. Step 4 extends that
  //     box backwards into the bar's already-covered zone; as the Step 6 comment above already
  //     says for the minimum-residual check, the pull-back "is a positioning device, not a
  //     statement about how much area needs covering". 50' × 30' focused/Good asked for
  //     round(10.513 / 3.218) = 3 where the real residual wants 2, and the extra mic landed at
  //     x 4.42 — 1.18 m INSIDE the bar's own 5.6 m reach, its whole circle duplicating the bar.
  //
  //  2. Switching to `round(uncoveredDepth / pitch)` fixed that but still ignored the INSET:
  //     bar-anchoring costs one radius at each end, so a residual of depth D leaves only
  //     D − 2·radius of centre span. Default 20' × 14' room, focused, Poor — residual 1.772 m,
  //     radius 0.8, pitch 1.040: the depth says round(1.704) = 2 columns, but the centre span is
  //     4.300 → 4.472 = 0.172 m. Two columns were placed 0.172 m apart — 11% of a mic DIAMETER,
  //     rendering as one blob while the BOM charged for both.
  //
  // Counting over the centre span makes the two agree by construction. `COVERAGE_SLIVER_TOL`
  // matches the sibling 'anchored' branch above: don't spend a whole extra mic to cover less than
  // a quarter-pitch of leftover. Here that trades an 8.6 cm strip at the far edge of the default
  // room's focused box for not placing a redundant mic.
  const COVERAGE_SLIVER_TOL = 0.25;
  // Guarded against a non-finite span: NaN would survive `Math.max(1, …)` and then make
  // `Array.from({ length: NaN })` return an EMPTY array — zero mics, silently.
  const axisCount = (first, last, p) => {
    const span = last - first;
    if (!Number.isFinite(span) || !Number.isFinite(p) || p <= 0) return 1;
    return Math.max(1, 1 + Math.ceil(Math.max(0, span) / p - COVERAGE_SLIVER_TOL));
  };
  let spacing = { x: pitch, y: pitch };
  let gridDims = {
    x: axisCount(xFirst, xLast, spacing.x),
    y: axisCount(yFirst, yLast, spacing.y),
  };
  // ── Minimum separation ───────────────────────────────────────────────────────────────────
  // `axisCount` answers "how many devices does it take to COVER this centre span?". It has no
  // notion of how far apart the devices then END UP — and the spread below pins them to the span's
  // two endpoints, however close together those are. When a span only just clears
  // COVERAGE_SLIVER_TOL, the count ticks to 2 and the pair lands almost on top of each other.
  //
  // 40' × 30' room, rectangle table, Good acoustics, focused coverage — the reported bug:
  //   x centre span 0.817 m, pitch 3.218 → 0.817 / 3.218 = 0.2539
  //   0.2539 − 0.25 (tolerance) = 0.0039 → ceil = 1 → TWO columns
  // A whole second mic bought to cover a 1.25 cm sliver, and the pair then placed 0.817 m apart
  // against a 4.95 m coverage diameter — 16% of one diameter. It rendered as a single blob on the
  // canvas while the BOM charged for both. One mic at the span's midpoint (x 8.484) reaches
  // 10.96 m and the table ends at 10.668 m, so the table was already fully covered by one.
  //
  // The tolerance can't fix this on its own: it is a fraction of PITCH, and the failure is a
  // razor-thin miss against it (0.0039 of a pitch, ~1.25 cm). Any threshold tight enough to catch
  // this case is loose enough to drop columns that genuinely earn their place. So gate on the
  // SPREAD GAP instead — span / (n − 1), the distance the devices actually end up at — which is
  // the quantity the bug is about.
  //
  // Scope: this only ever REDUCES a count, only on an axis whose devices would be closer than
  // MIN_SPREAD_SEPARATION_RATIO × radius, and it leaves the anchors (`xFirst`/`xLast` etc.)
  // untouched — a collapsed axis falls to n = 1 and `spreadAxis` centres it between the same two
  // inset bounds. Verified not to move any of: Poor/Fair at either coverage mode (spacing there
  // runs 81–98% of pitch, far above the floor), the 40' × 30' Good/full_room 2 × 2 (x-pair at
  // 0.66 × radius — tight, but it is what makes the grid span bar-edge to far wall), or the
  // 50' × 30' grids pinned by `barExpansionCeilingMicEvenSpread` 8.20–8.25.
  //
  // What this does NOT address: the same endpoint pinning also produces sub-pitch spacing in
  // full_room at Good (x-gap 41% of pitch) and Great (y-gap 46%), which costs ~10 points of
  // coverage without ever tripping this floor. That is a placement-geometry change rather than a
  // count change, and is proposed separately in
  // Documentation/specs/bar-expansion-slice-centering-proposal.md.
  const minSeparation = radius * MIN_SPREAD_SEPARATION_RATIO;
  const collapseToMinSeparation = (first, last, n) => {
    const span = last - first;
    // Non-finite inputs: leave the count alone rather than collapsing on garbage. A CROSSED span
    // (last < first, i.e. a box narrower than one device diameter) is intentionally NOT excluded —
    // it clamps to 0 below and collapses to a single centred device, which is exactly what
    // `spreadAxis`/`xSpanOk` already do for that case.
    if (!Number.isFinite(span) || !Number.isFinite(minSeparation) || minSeparation <= 0) return n;
    let out = n;
    while (out > 1 && Math.max(0, span) / (out - 1) < minSeparation) out -= 1;
    return out;
  };
  gridDims = {
    x: collapseToMinSeparation(xFirst, xLast, gridDims.x),
    y: collapseToMinSeparation(yFirst, yLast, gridDims.y),
  };

  // `maxCount` (constants.maxExpansionMics, today 4) is DELIBERATELY not enforced here anymore.
  // `gridDims` is left at its full geometric size — the count the room's coverage need actually
  // asks for, uncapped.
  //
  // This used to trim `gridDims` down to `maxCount` right here (splitting the budget in
  // proportion to the grid's aspect — see git history for that logic if it's ever needed again).
  // That silently under-provisioned any room whose real need exceeded the cap: the design just
  // got fewer expansion mics than its coverage geometry called for, with no warning and no
  // recourse. `setExpansionMicLayout` (reduxModules/ducks/room.ts), the reducer this result flows
  // into via `computeExpansionCeilingMicCoords`/`calculateDevicesMiddleware`, already accepts
  // "the full placement-computed layout... as-is" and calls `revalidateUcxEligibility` right
  // after — the Devio UCX capacity fallback (spec §2.2/§2.3: attemptCeilingHeightMitigation →
  // attemptTableToCeilingSwitch → applyUcxCapacityFallback) is what's meant to react when that
  // count exceeds capacity, not a silent trim at the geometry layer. Capping here meant that
  // fallback could never see a real over-budget count from AUTO-placement in the first place —
  // only a hand-built/legacy state (or a manual "+" click, which enforces its own, separate,
  // port-budget-aware cap in `addExpansionMic` and is UNAFFECTED by this change) could ever
  // trigger it. Removed deliberately (2026-09) so auto-placement surfaces the true need and lets
  // the fallback cascade handle it, mirroring how manual add already gates its own count via
  // `maxExpansionMicCountFor` — this is the auto-placement counterpart, just reacting via the
  // fallback instead of refusing the add.

  // Even, bar-anchored spread across the spans counted over above — applied whether or not the
  // cap trimmed the grid.
  // n === 1 has no span to spread across — centre it between the two inset bounds. That midpoint
  // is the box's centre on that axis and stays correct even when the bounds CROSS, which happens
  // whenever the box is narrower/shallower than one mic diameter.
  const spreadAxis = (first, last, n) => (n <= 1
    ? [(first + last) / 2]
    : Array.from({ length: n }, (_, i) => first + (i * (last - first)) / (n - 1)));

  // Validate each axis INDEPENDENTLY, and only where it can actually be invalid — a single
  // row/column is always placeable (see above). Requiring BOTH spans to be valid regardless of
  // count silently disabled the spread for any box narrower than a mic diameter even though the
  // other axis was fine: focused coverage at Fair hit exactly that (box 2.62 m wide against a
  // 3.11 m diameter) and fell back to the centred grid, putting its first mic at x 3.66, inside
  // the bar's own 4.3 m reach, instead of anchoring at 5.855.
  const xSpanOk = gridDims.x === 1 || xLast >= xFirst;
  const ySpanOk = gridDims.y === 1 || yLast >= yFirst;

  if (xSpanOk && ySpanOk) {
    const xs = spreadAxis(xFirst, xLast, gridDims.x);
    const ys = spreadAxis(yFirst, yLast, gridDims.y);
    // Row-major (x varies fastest), matching `calculateCoordinates`' index order.
    const spreadCoords = ys.flatMap((y) => xs.map((x) => ({
      model,
      modelName: modelName ?? model,
      location: { x, y },
    })));
    return { coords: spreadCoords, qty: spreadCoords.length, expansionBox };
  }

  // Degenerate on a MULTI-position axis — the two extremes crossed over, so there is no span to
  // distribute along. Fall back to the legacy centred grid, which still produces something sane
  // for a box that small.
  spacing = {
    x: gridDims.x > 1 ? expansionBox.length / gridDims.x : spacing.x,
    y: gridDims.y > 1 ? expansionBox.width / gridDims.y : spacing.y,
  };
  const coords = calculateCoordinates(model, modelName, expansionBox, gridDims, spacing);

  return { coords, qty: coords.length, expansionBox };
};

export const calcExpansionCeilingMicPlacement = ({ room, baseBox, acousticIndex, isFocused, barModel, ceilingHeight = undefined, manualAdd = false }) => {
  const acousticIndexCapped = Math.max(0, Math.min(acousticIndex, 4));
  // Step 1 — bar coverage depth from the bar mic's acoustic pickup table
  const barMicData = constants.equipment.microphones[barModel]?.micData;
  if (!barMicData) return null;
  const barReach = barMicData[acousticIndexCapped];

  // Step 2 — Expansion ceiling mic is the DCM-X (same acoustic data as the primary TCM-X). The
  // coverage radius is the HORIZONTAL leg of the pickup cone: radius = sqrt(maxPickupDist^2 -
  // verticalDrop^2), verticalDrop = micHeight - talkerHeight. Matches the spec's ceilingMicRadius
  // input and the standalone computeMicRadius formula; the raw slant distance would overstate coverage.
  const maxPickupDist = constants.equipment.microphones.dcm_x.micData[acousticIndexCapped];
  const talkerHeight = constants.area?.talkerHeight ?? 1.2009;
  const micHeight = ceilingHeight ?? constants.area?.defaultCeilingHeight ?? 2.8956;
  let ceilingMicRadius = side(maxPickupDist, Math.max(0, micHeight - talkerHeight));
  if (!Number.isFinite(ceilingMicRadius) || ceilingMicRadius <= 0) {
    ceilingMicRadius = 1.0;
  } else if (acousticIndexCapped === 0 && ceilingMicRadius < 0.8) {
    ceilingMicRadius = 0.8;
  }

  // Steps 3–8 — shared core.
  //
  // Step 4 pull-back is ENABLED (spec §5.4, tree-frog-product-specifications.md). It was
  // previously disabled on the rationale that "expansion mics complement the bar's coverage
  // rather than duplicating it" — but the spec's pull-back is not duplication, it is what
  // guarantees the first expansion mic's coverage circle MEETS the bar's coverage boundary
  // instead of leaving a ring-shaped gap in the transition zone (spec Figure 3.1a).
  //
  // Disabling it also had a second, larger effect: without the pull-back the residual box is
  // often narrower than minResidualDepth, so Step 5 declined and expansion mics were never
  // auto-recommended in rooms that plainly need them (e.g. the default 20'x14' room with a
  // keystone table, where the residual collapsed to 0.43 m). See
  // Documentation/BAR_EXPANSION_CEILING_MIC_CHANGES_VS_SPEC.md §A1.
  return calcBarExpansionPlacement({
    room,
    baseBox,
    isFocused,
    radius: ceilingMicRadius,
    barReach,
    overlapMap: constants.defBarOverlap,
    pullBackEnabled: true,
    maxCount: constants.maxExpansionMics,
    model: 'dcm_x',
    modelName: constants.equipment.microphones.dcm_x.modelName,
    manualAdd,
  });
};

/**
 * Binary search along an SVG path to find the precise fraction where the path
 * crosses into or out of the bar coverage circle.
 * Converges to within ~0.5 cm of the true crossing after 26 iterations (spec: 0..25).
 *
 * @param {SVGPathElement} path
 * @param {number} tLow          - lower bound fraction [0, 1]
 * @param {number} tHigh         - upper bound fraction [0, 1]
 * @param {{x:number,y:number}} barPosition - bar position in canvas pixels
 * @param {number} barReachPx    - bar reach in canvas pixels
 * @param {'in'|'out'} direction - 'out' = inside→outside, 'in' = outside→inside
 * @param {number} totalLength   - path total length in canvas pixels
 * @param {number} [nIter=26]
 * @returns {number} fraction [0, 1] of the crossing point
 */
export const findCrossing = (path, tLow, tHigh, barPosition, barReachPx, direction, totalLength, nIter = 26, toViewport = (p) => p) => {
  tLow  = Math.max(0, tLow);
  tHigh = Math.min(1, tHigh);

  for (let i = 0; i < nIter; i++) {
    const tMid = (tLow + tHigh) / 2;
    const point = toViewport(path.getPointAtLength(tMid * totalLength));
    const inside = Math.hypot(point.x - barPosition.x, point.y - barPosition.y) <= barReachPx;
    if (direction === 'out') {
      if (inside) tLow = tMid;   // still inside — push lower bound up
      else tHigh = tMid;          // already outside — push upper bound down
    } else {
      if (!inside) tLow = tMid;  // still outside — push lower bound up
      else tHigh = tMid;          // already inside — push upper bound down
    }
  }
  return (tLow + tHigh) / 2;
};

/**
 * Calculates expansion table mic placement for bar + table mic configurations.
 *
 * @param {Object}  params
 * @param {number}  params.acousticIndex             - index into mic micData arrays (0=poor … 4=perfect)
 * @param {string}  params.barModel                  - bar mic model key, e.g. 'vbc_2500a'
 * @param {SVGPathElement|null} params.path          - the SVG mic path element from the canvas
 * @param {{x:number,y:number}} params.barPosition   - bar device position in canvas pixels
 * @param {number}  params.scale                     - pixels per meter (room → canvas conversion)
 * @param {{x0:number,y0:number}} [params.bbox]      - canvas bounding box origin; required for coord conversion
 * @param {number}  [params.N=400]                   - number of path samples (≥ 400 for adequate precision)
 * @param {number}  [params.scenarioCoverageThreshold=0.97] - fraction of samples that must be inside bar reach to trigger Scenario 1
 * @param {boolean} [params.isEndTable=false]        - true for open (end-table) paths: if no entry crossing exists, tEntry defaults to 1.0 (T.5)
 * @param {boolean} [params.isFlat=false]            - true when the mic path doubles back on itself:
 *   spec §4.1, `tableWidth − 2 × INSET ≤ 2 × micRadius` (the caller computes this from the table's
 *   own dimensions, with a zero-area path bbox as fallback — see `deriveTableExpansionInputs` in
 *   calculateDevicesMiddleware.ts). Consumed below to tile ONE centreline chain instead of two
 *   mirror-image ones.
 * @param {boolean} [params.returnLegRedundant=false] - true for keystone/bullet where the return leg mirrors the top edge; false for vshape/ushape where both arms carry distinct seating
 * @param {string|null} [params.shape=null] - table shape key (e.g. 'ushape'); when provided, isEndTable and returnLegRedundant are re-resolved from this module's own constants.tables[shape] and take precedence over the explicit flags (immune to a stale/undefined caller constants reference)
 * @param {boolean} [params.scenarioOnly=false] - stop after Step 2 and return `{ barReach, scenario, inCount }` without running the Scenario 3 tiling (see the inline note on the parameter)
 *
 * @returns {null}                           - unknown barModel
 * @returns {{ barReach, scenario: null }}   - path unavailable, scenario not yet determined
 * @returns {{ barReach, scenario: 1, inCount }} - bar covers entire path → expansion mics NOT
 *   REQUIRED (spec §7 Step 2). This is not "place 0 mics": per §2.2 row 2 a user who adds them
 *   anyway gets standard standalone placement on the full path. The caller decides — see
 *   `computeTableExpansionViaPath` in calculateDevicesMiddleware.ts.
 * @returns {{ barReach, scenario: 2, inCount }} - bar does not reach path → standard TTM placement
 * @returns {{ barReach, scenario: 3, inCount, tExit, tEntry, covFrac, arcFrac, qty, coords }} - partial overlap (tiling placement)
 */
export const calcExpansionTableMicPlacement = ({
  acousticIndex,
  barModel,
  path = null,
  barPosition = null,
  scale = 1,
  bbox = null,
  N = 400,
  scenarioCoverageThreshold = 0.97,
  isFlat = false,
  isEndTable = false,
  returnLegRedundant = false,
  shape = null,
  // Stop after Step 2 (scenario determination) and return `{ barReach, scenario, inCount }`
  // without running the Scenario 3 tiling. The acoustics-transition handling in
  // calculateDevicesMiddleware needs the scenario NUMBER on both sides of a tier change
  // (spec §7 Step 2) to tell "coverage required → not required" from "not required →
  // not required"; the scenario is not persisted anywhere in state, so the PRIOR side has
  // to be sampled against the pre-change path before React repaints it. Placement output
  // is discarded there, so skip the work.
  scenarioOnly = false,
}) => {
  const acousticIndexCapped = Math.max(0, Math.min(acousticIndex, 4));

  // When a table `shape` is supplied, resolve the open-path / redundant-leg flags from THIS
  // module's own constants import. That import is always loaded, so it is immune to the caller
  // passing a stale or undefined constants reference (e.g. `toolIndex.conferenceroomDesigner
  // .constants` not yet populated in the middleware, which silently yields isEndTable=false for
  // ushape/vshape). The explicit isEndTable/returnLegRedundant params remain the fallback,
  // used by unit tests that drive the algorithm directly without a shape.
  if (shape != null && constants.tables?.[shape]) {
    isEndTable = !!constants.tables[shape].endTable;
    // Only the bullet has a genuinely redundant return leg (parallel edges, collapses flat → a
    // single centreline chain suffices). The keystone DIVERGES, so like the V it needs BOTH arms
    // tiled — treating it as single-chain leaves one edge / the far end under-covered.
    returnLegRedundant = (shape === 'bullet');
  }

  const barMicData = constants.equipment.microphones[barModel]?.micData;
  if (!barMicData) return null;

  // bar reach in room coordinates (meters)
  const barReach = barMicData[acousticIndexCapped];

  // determine scenario by sampling the mic path
  if (!path || !barPosition) return { barReach, scenario: null };

  // barReach is in meters; convert to canvas pixels for distance comparisons against path points
  const barReachPx = barReach * scale;
  const totalLength = path.getTotalLength();

  // path.getPointAtLength() returns LOCAL coordinates (the table group's coordinate system).
  // Apply the element's CTM (current transformation matrix) to convert to SVG viewport coords,
  // which is the same space as barPosition and bbox. Gracefully falls back when getCTM is
  // unavailable (jsdom / test environments) so tests continue to work.
  const ctm = (path.getCTM && typeof path.getCTM === 'function') ? path.getCTM() : null;
  const toViewport = ctm
    ? (p) => ({ x: ctm.a * p.x + ctm.c * p.y + ctm.e, y: ctm.b * p.x + ctm.d * p.y + ctm.f })
    : (p) => p;

  // Sample at cell centres (i + 0.5) / N so the furthest sample reaches fraction (N-0.5)/N ≈ 1.
  // Sampling at i/N misses fraction 1.0 — a path that only exits bar coverage at its very end
  // would be misclassified as Scenario 1.
  let maxDistPx = -Infinity;
  let firstMaxDistIdx = 0;
  let lastMaxDistIdx = 0;
  const inside = [];
  for (let i = 0; i < N; i++) {
    const point = toViewport(path.getPointAtLength(((i + 0.5) / N) * totalLength));
    const dist = Math.hypot(point.x - barPosition.x, point.y - barPosition.y);
    inside.push(dist <= barReachPx);
    // Track the FIRST and LAST samples at peak distance. A symmetric tapered table (V/keystone)
    // reaches its max distance at the two far corners (which are equidistant from the centred
    // bar); the midpoint of those indices is the centreline tip. A single apex gives first==last.
    if (dist > maxDistPx + 1e-6) { maxDistPx = dist; firstMaxDistIdx = i; lastMaxDistIdx = i; }
    else if (dist >= maxDistPx - 1e-6) { lastMaxDistIdx = i; }
  }
  // tFarCentre: midpoint of the first/last peak-distance samples — the centreline tip of a
  // symmetric tapered table (V/keystone), whose two far corners are equidistant from the centred
  // bar. Used by the fixed 3-mic layout below to locate the far mic.
  const tFarCentre = (((firstMaxDistIdx + lastMaxDistIdx) / 2 + 0.5) / N + 1) % 1;
  // tFarCorner: keystone/bullet have a redundant return leg — stop placement at the far corner.
  // vshape/ushape have two distinct arms that both need coverage, so tFarCorner is 1.0 (no clamp).
  const tFarCorner = (isEndTable && returnLegRedundant) ? (lastMaxDistIdx + 0.5) / N : 1.0;
  const inCount = inside.filter(Boolean).length;

  let scenario;
  if (inCount >= N * scenarioCoverageThreshold) {
    scenario = 1; // bar covers entire path → 0 expansion mics needed
  } else if (inCount === 0) {
    scenario = 2; // bar does not reach path → standard TTM placement
  } else {
    scenario = 3; // partial overlap → reduced arc placement
  }

  // Scenario 2: bar does not reach the mic path at all — standard standalone
  // tabletop placement applies unchanged. The caller detects scenario: 2 and falls through
  // to the existing TTM-X path in roomCalculations.ts. No expansion logic runs here.
  if (scenarioOnly || scenario !== 3) return { barReach, scenario, inCount };

  // find precise crossing fractions (Scenario 3 only)
  const eps = 1 / N;
  const exits   = []; // inside→outside transition fractions
  const entries = []; // outside→inside transition fractions

  for (let i = 0; i < N; i++) {
    const curr = inside[i];
    const next = inside[(i + 1) % N];
    if (curr && !next) exits.push(i / N);   // last-inside index per spec
    if (!curr && next) entries.push(i / N); // last-outside index per spec
  }

  const tExit  = findCrossing(path, exits[0] - eps, exits[0] + eps, barPosition, barReachPx, 'out', totalLength, 26, toViewport);

  // T.5: for open paths (endTable=true), if the path end is outside bar coverage
  // there is no entry crossing — the uncovered arc runs to the physical end of the path.
  let tEntry;
  if (entries.length > 0) {
    tEntry = findCrossing(path, entries[entries.length - 1] - eps, entries[entries.length - 1] + eps, barPosition, barReachPx, 'in', totalLength, 26, toViewport);
    // For endTable paths the return leg re-enters bar coverage — don't place mics there.
    if (isEndTable) tEntry = Math.min(tEntry, tFarCorner);
  } else if (isEndTable) {
    tEntry = tFarCorner; // Stop at far corner, not end of path
  } else {
    // Closed path with no detected entry: degenerate — treat as full coverage
    return { barReach, scenario: 1, inCount };
  }

  const covFrac = ((tExit  - tEntry) + 1) % 1; // covered / near-bar arc
  const arcFrac = ((tEntry - tExit)  + 1) % 1; // uncovered / far arc

  // micData is in metres; multiply by scale to match totalLength (pixels).
  const micCoverageRadius = constants.equipment.microphones.ttm_x.micData[acousticIndexCapped] * scale;

  // Bar → first tabletop mic offset. Measured in units of the mic radius (k × r), the first
  // mic's coverage spans [offset − r, offset + r] relative to the bar's coverage edge, so its
  // NEAR edge sits at (k − 1)·r past that edge:
  //   k > 1  → a white band of (k − 1)·r (the old 1.6 × r left ≈0.6 r ≈ 1 m of white).
  //   k < 1  → the mic laps (1 − k)·r back inside bar coverage and hugs the bar.
  //   k = 1  → the mic's near edge lands EXACTLY on the bar's coverage edge: no band, and the
  //            mic sits as far down-table as geometry allows without opening a gap.
  // k = 1 is a derived tangency condition, not a tuned constant, so the offset is simply one
  // mic radius and defBarTableOverlap drops out of the bar→mic interface. defTableOverlap still
  // drives the mic-to-mic step (same device type). Neither shrinks the arc — the count is purely
  // how many tiles fit.
  const overlapDistance = micCoverageRadius; // k = 1: first-mic coverage edge meets the bar edge
  const overlapFrac     = overlapDistance / totalLength;
  const spacingFrac     = (constants.defTableOverlap * micCoverageRadius) / totalLength;

  const modelName = constants.expansionMicPlaceholders.table;
  const makeMicAt = (t) => {
    const pt = toViewport(path.getPointAtLength(t * totalLength));
    const x = bbox ? (pt.x - bbox.x0) / scale : pt.x / scale;
    const y = bbox ? (pt.y - bbox.y0) / scale : pt.y / scale;
    return { model: 'ttm_x', modelName, location: { x, y, z: 0.91 } };
  };

  // Each chain tiles half of the uncovered arc, so the two meet at the arc's far midpoint
  // (the point furthest from the bar). Using arcFrac/2 directly avoids the wrap-around trap of
  // a plateau-centre estimate (on a closed/flat path the far point sits at the t=0 seam, so
  // averaging the two plateau indices would land back at the near-bar turnaround).
  const halfArcFrac = arcFrac / 2;

  // Steps 4–7 (tiling): walk inward from each bar transition toward the far midpoint, first
  // mic `overlapDistance` past the bar edge, then steps of `spacing`, stopping at the table's
  // far edge. Count = whatever fits. "Anchor from both ends" → tile from tExit and tEntry; on
  // a flat (narrow) table the two chains coincide on the centreline, so we de-dupe.
  const radiusFrac = micCoverageRadius / totalLength;
  // Tags the LAST tile of each chain (the boundary/far-edge pull-in point, closest to the table's
  // far end / apex) as `isChainFarEnd` — needed below so the cap step can guarantee it a slot
  // instead of risking it being sampled away.
  const tileFrom = (startT, dir, limit) => {
    const ds = [];
    for (let d = overlapFrac; d <= limit + 1e-9; d += spacingFrac) ds.push(d);
    // Boundary pull-in: if at least one mic was placed but the last one's coverage stops
    // short of the far edge (limit), the next step overshoots and would normally be dropped —
    // leaving the far end uncovered. Instead pull that mic IN to the edge so the far end is
    // covered (this is what lets a rectangle fit the 2nd mic that a racetrack's longer arc
    // already accommodates). Skipped when nothing fit (first mic already past the edge).
    if (ds.length > 0 && (ds[ds.length - 1] + radiusFrac) < limit) ds.push(limit);
    return ds.map((d, i) => ({ t: ((startT + dir * d) + 1) % 1, isChainFarEnd: i === ds.length - 1 }));
  };
  // vshape/keystone TAPER toward the far end (the two edges meet at an apex, or a single narrow
  // "head"). Per spec §6 these get a fixed 3-mic layout: one mic per arm just past the bar's
  // coverage edge, plus ONE mic at the centre of the far end — regardless of how wide that far
  // end is. This avoids the two arms' far mics landing almost on top of each other (the V) or
  // double-covering a tapered head (the keystone).
  //
  // HISTORY — this block was removed by 290cbaaf (squashed into e3e6f339, "PIG-794: fixed the
  // expansion mic height") in favour of falling through to the two-chain tiling below, on the
  // rationale that "mic count must scale with actual coverage need instead of being pinned at 3"
  // for large keystone/V tables. That change also had two unintended effects on the DEFAULT
  // keystone, which is why it is reverted here:
  //   1. It halved the count at Poor (3 → 1), because the default table's uncovered arc only
  //      fits one tile per chain before `mergeConverging` collapses them.
  //   2. It exposed the tiling path's minimum-viable check to these shapes for the first time.
  //      That check lives implicitly in `tileFrom`'s loop, which this block returns BEFORE
  //      reaching — so with the block gone, Fair (uncovered arc 1.259 m against a 2 × 1.3 m
  //      threshold) placed ZERO mics and offered no way to recover.
  // The "scale with coverage need" concern is real for LARGE keystone/V tables and is unresolved
  // with the spec owner; if it is reinstated it needs to be a scaling layout, not a fall-through
  // to tiling, or defect 2 returns.
  const singleFarMic = isEndTable && (shape === 'vshape' || shape === 'keystone');
  // A FLAT path (spec §4.1: `tableWidth − 2 × INSET ≤ 2 × micRadius`, supplied by the caller as
  // `isFlat`) doubles back on itself: its two long legs are the same physical stretch of table,
  // close enough that one row of mics covers both. Tiling them as two independent chains
  // generates a mirror-image duplicate of every tile, which is wrong twice over:
  //   1. `mergeConverging` only collapses the duplicates AFTER the cap has already been split
  //      `floor(cap / 2)` per arm — so a cap of 4 buys 4 slots spread over 2 physical positions
  //      and the design ends up with HALF the mics it should have.
  //   2. The count stops being monotonic in acoustics. On the default 4 ft-wide rectangle /
  //      racetrack (path legs 0.4192 m apart) Poor placed 2 mics while Fair placed 4 and Good 3 —
  //      the WORST acoustics, with the largest uncovered arc, got the FEWEST mics. Fair and above
  //      were only correct by accident: at r ≥ 1.3 `devicePathProps` zeroes devicePathWidth
  //      (tableConstants.ts), the two chains land on identical points, and the tight dedupe below
  //      collapses them before the cap ever sees them. Poor is the one tier where the path stays
  //      wider than `dedupDistM` but far narrower than `mergeDistM`. (PIG: 50×30 room, racetrack
  //      /rectangle, Poor, bar + expansion tabletop mic → 2 mics.)
  // `isFlat` was already computed and threaded in for exactly this decision but never read here.
  //
  // The bullet has a genuinely redundant return leg (parallel edges, collapses flat → one
  // centreline chain) and is single-chain for the same reason.
  const isFlatClosedPath = isFlat && !isEndTable;
  const singleChain = (isEndTable && returnLegRedundant) || isFlatClosedPath;
  // Limit differs between the two single-chain cases, so it can NOT be folded into `singleChain`:
  //   • bullet (open path): the chain must cover the FULL arc (tExit → far corner), NOT
  //     halfArcFrac, or the far end is left bare.
  //   • flat CLOSED path: halfArcFrac is already the whole table — the arc runs down one leg and
  //     back along its mirror, so the second half re-traverses the same x range. Tiling arcFrac
  //     here would walk the return leg and double every mic.
  const singleChainLimit = isFlatClosedPath ? halfArcFrac : arcFrac;

  if (singleFarMic) {
    // Fixed 3-mic layout: one near mic per arm (one radius past the bar edge) + one far mic.
    // The far mic is forced ONTO THE CENTRELINE: its y is the midpoint of the two near mics,
    // which are symmetric about the table centreline (so this is exact even when the path's
    // farthest sample lands on an off-centre corner of the far end).
    const nearTop = makeMicAt((tExit + overlapFrac + 1) % 1);
    const nearBot = makeMicAt((tEntry - overlapFrac + 1) % 1);
    const apex    = makeMicAt(tFarCentre);
    const centreY = (nearTop.location.y + nearBot.location.y) / 2;
    // Far mic forced onto the centreline (y = centreY). Its x by shape:
    //   keystone — the far extent (seated head).
    //   vshape   — narrow the mid-arm band by moving the mic toward the bar, but stop while it is
    //              still ON the table: walk back from the apex along an arm and stop where the
    //              converging neck has only just opened to a small gap (half-width = 0.2·radius ≈
    //              0.34 m, i.e. a ~0.7 m neck). Past that the centreline runs through the open
    //              mouth between the arms (off the table).
    const nearX = (nearTop.location.x + nearBot.location.x) / 2;
    const towardBar = Math.sign(nearX - apex.location.x) || -1; // bar side direction in x
    let farX = apex.location.x;
    if (shape === 'vshape') {
      const neckHalfWidthM = (micCoverageRadius / scale) * 0.2;
      const STEPS = 48;
      for (let s = 1; s <= STEPS; s++) {
        const t = ((tFarCentre + (tExit - tFarCentre) * (s / STEPS)) % 1 + 1) % 1;
        const p = makeMicAt(t);
        if (Math.abs(p.location.y - centreY) >= neckHalfWidthM) { farX = p.location.x; break; }
      }
      // Design tweak: nudge ~3 ft 3 in back toward the apex (right) from the neck point, clamped
      // so it never passes the apex.
      farX = Math.min(apex.location.x, farX + (3 * 0.3048 + 3 * 0.0254));
    } else if (shape === 'keystone') {
      // Design tweak: pull the end mic ~2 ft off the head toward the bar (into the converging
      // neck, still on the table) so its coverage reaches back toward the near mics.
      farX = apex.location.x + towardBar * (2 * 0.3048);
    }
    const farMic = { model: 'ttm_x', modelName, location: { x: farX, y: centreY, z: 0.91 } };

    // §6 places three mics unconditionally, on the assumption that the uncovered arc is long
    // enough to hold three distinct ones. It often isn't. The near mics step one `overlapDistance`
    // IN from each END of the arc, so once the arc is shorter than about 2 × overlapDistance +
    // a mic spacing they converge on its midpoint — which is exactly where the far mic already
    // sits. Measured on the default keystone at Poor: a 2.892 m arc (bar covering 63% of the path)
    // puts all three within 0.30 m of each other at the table head. One TTM-X there covers the
    // entire uncovered region on its own, so three is not merely ugly, it is wrong.
    //
    // Collapse converging mics using the SAME threshold the tiling path uses further down
    // (`defTableOverlap × micCoverageRadius × 0.5` — see `mergeConverging`), so both placement
    // paths agree on what "too close to be two mics" means. The merged mic goes on the CENTRELINE
    // at the mean x: consistent with how the far mic's own y is constructed, and correct for a
    // tapered table, where a single mic belongs down the middle rather than out on either arm.
    //
    // DEPARTS FROM SPEC §6, which states "The two near mics are not merged." That sentence holds
    // for a long arc; on a short one it yields three coincident mics. Raise with the spec owner.
    const sfMergeDistM = (constants.defTableOverlap * micCoverageRadius) / scale * 0.5;
    const collapseConverging = (list) => {
      const out = list.map((m) => ({ ...m, location: { ...m.location } }));
      for (let i = 0; i < out.length; i++) {
        for (let j = i + 1; j < out.length; j++) {
          const d = Math.hypot(out[i].location.x - out[j].location.x, out[i].location.y - out[j].location.y);
          if (d < sfMergeDistM) {
            out[i].location = { x: (out[i].location.x + out[j].location.x) / 2, y: centreY, z: 0.91 };
            out.splice(j, 1);
            j = i; // re-scan the merged point against the rest
          }
        }
      }
      return out;
    };
    // CAVEAT: the §6 layout assumes the near mics sit BETWEEN the bar and the far mic, but nothing
    // enforces it. `tExit ± overlapFrac` steps a fixed arc length from wherever the bar's coverage
    // ends, so when the bar covers most of the path (weak acoustics → small mic radius → small
    // overlapFrac, but a bar reach that still swallows the near half) the near mics can land at or
    // PAST the far mic, bunching all three at the table head. `collapseConverging` then merges
    // them, which is why this can return fewer than 3 coords.
    const sfCoords = collapseConverging([nearTop, nearBot, farMic])
      .sort((a, b) => (a.location.x - b.location.x) || (a.location.y - b.location.y));

    return { barReach, scenario, inCount, tExit, tEntry, covFrac, arcFrac, qty: sfCoords.length, coords: sfCoords };
  }

  // Hoisted: the per-chain tiling below now splits this budget between the arms up front, so it
  // has to be known before the chains are built rather than only at the trim step.
  const cap = constants.maxExpansionMics;

  // Each chain's OWN far end: walk outward from the bar transition and take the point at peak
  // distance from the bar. That is where the path turns back on itself — a U/boat arm's far
  // corner, or a flat path's far cap — so it is the "end of the table" as seen from THIS arm,
  // not the shared far midpoint both chains would otherwise aim at.
  //
  // The walk is bounded by halfArcFrac (each chain owns half the uncovered arc) so an asymmetric
  // table can't have one chain's peak land on the OTHER arm. On a flat path halfArcFrac IS the
  // far cap, so the same rule reproduces the previous limit exactly — one rule, both shapes.
  const distFromBarAt = (t) => {
    const p = toViewport(path.getPointAtLength((((t % 1) + 1) % 1) * totalLength));
    return Math.hypot(p.x - barPosition.x, p.y - barPosition.y);
  };
  const armFarEndFrom = (startT, dir, limit) => {
    const step = 1 / N;
    let bestD = limit;
    let bestDist = -Infinity;
    for (let d = 0; d <= limit + 1e-9; d += step) {
      const dist = distFromBarAt(startT + dir * d);
      // Strict `>` keeps the FIRST peak, so a symmetric pair of far corners (equidistant from a
      // centred bar) resolves to this chain's own corner rather than the other chain's.
      if (dist > bestDist + 1e-6) { bestDist = dist; bestD = d; }
    }
    return bestD;
  };

  // Cap handling for a chain that cannot cover its arm: spread its budget EVENLY from the first
  // mic (the bar-tangency anchor) to the arm's far end, instead of keeping the natural pitch and
  // letting the entire shortfall fall at the far end.
  //
  // The natural pitch (`defTableOverlap × radius`) is the spacing at which consecutive coverage
  // circles overlap, so a prefix of the natural tiling gives one CONTIGUOUS covered stretch and
  // then abandons the rest of the table — on the 50×30 rectangle/racetrack at Poor that left the
  // last ~2.1 m (three seats) with no mic at all. Respacing trades that single large hole for
  // several small even ones (0.46 m each there) and reaches the end of the table. Per the design
  // owner: when full coverage is unreachable, an even spread to the table's end is preferred over
  // contiguous-then-nothing.
  //
  // Only engaged when the budget is genuinely short. If the natural tiling fits inside the
  // budget it already reaches the arm's far end contiguously, so there is nothing to trade.
  const respaceEvenly = (startT, dir, limit, n) => {
    if (n <= 1) return [{ t: ((startT + dir * overlapFrac) + 1) % 1, isChainFarEnd: true }];
    const pitch = (limit - overlapFrac) / (n - 1);
    return Array.from({ length: n }, (_, i) => ({
      t: ((startT + dir * (overlapFrac + i * pitch)) + 1) % 1,
      isChainFarEnd: i === n - 1,
    }));
  };
  const tileChain = (startT, dir, chain, budget) => {
    const natural = tileFrom(startT, dir, singleChain ? singleChainLimit : halfArcFrac);
    if (natural.length <= budget || natural.length === 0) {
      return natural.map((p) => ({ ...p, chain }));
    }
    // A flat path's far end is EXACTLY halfArcFrac — the arc's self-mirror point, where the path
    // doubles back. Take it directly instead of letting the peak walk rediscover it: the walk is
    // a discrete 1/N sample, and its nearest grid point to the true turn lands slightly to one
    // side, so `resolveMicAt` averages a leg point against a cap point and the last mic comes out
    // ~1 cm off the centreline. Exact here, and `resolveMicAt` maps the self-mirror point to
    // itself, so the mic is on the centreline by construction.
    const farEnd = isFlatClosedPath ? halfArcFrac : armFarEndFrom(startT, dir, halfArcFrac);
    // Guard the degenerate case where the peak sits at (or inside) the first mic's own anchor —
    // respacing across a non-positive span would stack every mic on the anchor. Fall back to the
    // natural prefix, which at least spaces them correctly.
    if (farEnd <= overlapFrac) {
      return natural.slice(0, budget).map((p) => ({ ...p, chain }));
    }
    return respaceEvenly(startT, dir, farEnd, budget).map((p) => ({ ...p, chain }));
  };

  // Tag each tile with which chain/arm generated it ('A' = tExit chain, 'B' = tEntry chain) so
  // the cap step below can balance the two arms independently instead of sampling a single
  // flattened, arc-position-sorted list — which for a symmetric table (equal-length arms) still
  // ends up handing an uneven split to each arm purely from where indices land after rounding.
  //
  // Split the cap between the chains UP FRONT so each arm respaces against its own budget. When
  // both chains come in at budget the total equals the cap exactly, so the cap step below has
  // nothing left to trim — which is what stops the post-selection merge from quietly spending a
  // slot (the U-shape-at-Fair 4 → 3 collapse: both chains previously tiled to the shared base
  // midpoint, so their far picks landed a tiling remainder apart, 0.70 m, inside the 0.871 m
  // merge threshold. Aiming each chain at its own base CORNER puts them the base's full width
  // apart instead.)
  const budgetA = singleChain ? cap : Math.ceil(cap / 2);
  let micTs = tileChain(tExit, +1, 'A', budgetA);
  // Two-armed shapes (U/boat/non-flat closed loops) tile BOTH chains. A flat path has no genuine
  // second arm — see `isFlatClosedPath` above.
  if (!singleChain) micTs.push(...tileChain(tEntry, -1, 'B', cap - budgetA));

  // De-dupe only TRULY coincident mics: the flat table's out/return legs and the two chains'
  // shared far-edge pull-in land on the exact same point (distance ≈ 0). The threshold must NOT
  // be large enough to cull DISTINCT cross-arm mics where a U/V/boat's two arms pass close
  // together — that greedily keeps the first chain, starves the second, and (worse) drops
  // placedCount below maxExpansionMics so the balancing sampler below never runs. Distinct mics
  // are ≥ one mic-spacing apart, so a small epsilon (≈0.17 m at the TTM-X radius) is safe and
  // still collapses the genuine coincidences.
  const dedupDistM = (micCoverageRadius / scale) * 0.1;
  // Arc position = distance from tExit along the uncovered arc in the +dir (0 at the bar edge,
  // arcFrac at the far bar edge / second arm). Used to cap fairly across both arms.
  const arcPosOf = (t) => (((t - tExit) % 1) + 1) % 1;

  // A flat closed path's single chain (see `isFlatClosedPath` above) tiles only ONE of the two
  // mirror-image legs, so sampling it directly would render every mic hugging one edge of the
  // table (0.4 m in from it, ~0.21 m off centre on the default 4 ft-wide rectangle/racetrack).
  // Resolve each tile to the MIDPOINT of itself and its mirror on the return leg instead — the
  // same centreline resolution `placeStandaloneTableMics` applies to §4.1 flat paths, and the
  // same centreline the OLD two-chain behaviour happened to land on when `mergeConverging`
  // averaged each mirror pair. So this is not a positional change for the user; it is what keeps
  // the corrected COUNT from arriving with a regressed position.
  //
  // The mirror of `t` is `(tExit + tEntry) − t`: tExit and tEntry are themselves a mirror pair
  // (equal arc distance from the bar on opposite legs), which makes the arc's far midpoint map to
  // itself. Derived from the crossings rather than assuming a `1 − t` seam, so it holds wherever
  // the path builder happens to start.
  const mirrorT = (t) => (((tExit + tEntry - t) % 1) + 1) % 1;
  const resolveMicAt = !isFlatClosedPath ? makeMicAt : (t) => {
    const a = makeMicAt(t);
    const b = makeMicAt(mirrorT(t));
    return {
      ...a,
      location: { x: (a.location.x + b.location.x) / 2, y: (a.location.y + b.location.y) / 2, z: 0.91 },
    };
  };
  const placed = [];
  for (const { t, isChainFarEnd, chain } of micTs) {
    const mic = resolveMicAt(t);
    const dupIdx = placed.findIndex(p => Math.hypot(p.location.x - mic.location.x, p.location.y - mic.location.y) < dedupDistM);
    if (dupIdx === -1) {
      placed.push({ ...mic, _arcPos: arcPosOf(t), _isFarEnd: isChainFarEnd, _t: t, _chain: chain });
    } else {
      if (isChainFarEnd) placed[dupIdx]._isFarEnd = true; // keep the tag even if this exact point already existed from the other chain
      // A dedup collapse across DIFFERENT chains means both arms converge on the same physical
      // point (e.g. a flat table's centreline) — mark it shared so the cap step below treats it
      // as belonging to neither arm's exclusive budget.
      if (placed[dupIdx]._chain !== chain) placed[dupIdx]._chain = 'AB';
    }
  }

  // Collapse mics that converge too close together — the two arms' far mics at a V/keystone
  // apex land almost on top of each other; the far end should get ONE mic at their midpoint, not
  // two stacked. Wide far ends (U/boat) keep both far mics (they sit well beyond this distance).
  // Runs BEFORE the cap below (not after) so the merged far-end point is a single, cap-eligible
  // entry instead of two near-duplicate entries the cap's arc-position sampling could split apart.
  //
  // Merge in PATH-LENGTH (t) space, not raw (x, y): two points can be within `mergeDistM` of each
  // other in straight-line distance while sitting on DIFFERENT segments of the path around a
  // corner (e.g. a U-shape's two arms, close together across its opening). Averaging their (x, y)
  // directly cuts a straight line across the corner, landing the "merged" mic in the empty
  // opening — off the table entirely. Averaging their t (path-length fraction) instead and
  // re-sampling via `makeMicAt` guarantees the merged mic is always an actual point ON the path.
  const mergeDistM = (constants.defTableOverlap * micCoverageRadius) / scale * 0.5;
  const circularMeanT = (t1, t2) => {
    let diff = t2 - t1;
    if (diff > 0.5) diff -= 1;
    if (diff < -0.5) diff += 1;
    return ((t1 + diff / 2) + 1) % 1;
  };
  const mergeConverging = (list) => {
    const out = list.map((m) => ({ ...m, location: { ...m.location } }));
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const d = Math.hypot(out[i].location.x - out[j].location.x, out[i].location.y - out[j].location.y);
        if (d < mergeDistM) {
          const mergedT = circularMeanT(out[i]._t, out[j]._t);
          const resampled = resolveMicAt(mergedT);
          out[i].location = resampled.location;
          out[i]._t = mergedT;
          out[i]._arcPos = arcPosOf(mergedT);
          out[i]._isFarEnd = out[i]._isFarEnd || out[j]._isFarEnd;
          // Merging across different chains (the usual V/keystone apex case) produces a shared
          // point belonging to neither arm's exclusive budget — see the cap step below.
          if (out[i]._chain !== out[j]._chain) out[i]._chain = 'AB';
          out.splice(j, 1);
          j = i; // re-scan against the merged midpoint
        }
      }
    }
    return out;
  };
  const merged = mergeConverging(placed);

  // Cap to maxExpansionMics. NOT slice(0, N): the mics are generated chain-by-chain (tExit arm
  // first, then tEntry arm), so slicing the first N dumps the whole budget onto one arm and
  // starves the other (the U/V "one arm fully miked, the other bare" bug). A flat arc-position
  // sort-then-evenly-sample has a subtler version of the same problem: for a SYMMETRIC table
  // (equal-length arms), both arms' points interleave along the single arc-position axis, so
  // evenly-spaced indices over the flattened list can still land 3-and-1 or 2-and-2-off-centre
  // across the two arms purely from index rounding — the tapered table then shows mics bunched
  // toward one side (the same "flat index sample ignores structure" bug fixed for the ceiling
  // expansion mic grid — see capExpansionGridLayout in reduxModules/ducks/room.ts). Splitting by
  // chain and capping each arm's OWN budget independently guarantees a symmetric table keeps a
  // symmetric result.
  // A single pick (n===1) is centred across the candidate range rather than pinned to index 0.
  // Index 0 is the near-bar extreme (each chain's candidates run near-bar → far), so for the
  // common case of one mic per arm (e.g. a symmetric keystone capped to 1-shared + 1-per-arm —
  // see the two-armed cap branch below) always returning [0] hugs the mic right up against the
  // bar's own coverage edge — visually right on the uncovered region's near boundary, with a
  // coverage gap opening across the rest of the arm before the far/shared mic picks up again.
  // n>=2 still spans the full range (kept extremes) since those already terminate at both ends.
  const sampleEvenlyIndices = (count, n) => {
    if (n <= 0 || count === 0) return [];
    if (n === 1) return [Math.round((count - 1) / 2)];
    return Array.from({ length: n }, (_, i) => Math.round((i * (count - 1)) / (n - 1)));
  };
  let capped = merged;
  if (merged.length > cap) {
    if (isFlatClosedPath) {
      // SAFETY NET, not the normal route. `tileChain` above already builds a flat path's single
      // chain at no more than `cap` tiles (respacing them evenly to the arm's far end when the
      // natural pitch would need more), so `merged.length > cap` should not be reachable here —
      // it only survives a merge that somehow ADDS entries, which nothing currently does.
      //
      // If it ever does trigger, take the tiles nearest the bar at their natural pitch: a prefix
      // of `tileFrom`'s output keeps uniform spacing and one contiguous covered stretch, which is
      // the least surprising degradation. NOT the general `slice(0, cap)` the comment further
      // down warns against — that warning is about the TWO-chain case, where slicing spends the
      // whole budget on the tExit arm and starves the tEntry arm. A flat path has exactly one
      // chain (its second leg is this chain's mirror, already handled by `resolveMicAt`), so
      // there is no arm to starve.
      merged.sort((a, b) => a._arcPos - b._arcPos);
      capped = merged.slice(0, cap);
    } else if (singleChain) {
      // Bullet's redundant return leg — an OPEN path whose far corner is a distinct physical end
      // of the table (not the mirror of the near end), so it keeps its reserved far-end slot.
      merged.sort((a, b) => a._arcPos - b._arcPos);
      const lastIdx = merged.length - 1;
      const mandatorySet = new Set([0, lastIdx]);
      merged.forEach((m, i) => { if (m._isFarEnd) mandatorySet.add(i); });
      const mandatory = [...mandatorySet].sort((a, b) => a - b);
      if (mandatory.length >= cap) {
        const pick = sampleEvenlyIndices(mandatory.length, cap).map(i => mandatory[i]);
        capped = [...new Set(pick)].sort((a, b) => a - b).map(idx => merged[idx]);
      } else {
        // Sample evenly over the FULL candidate list, NOT over the complement of `mandatory`.
        // `sampleEvenlyIndices` is an INCLUSIVE-endpoint sampler: for any n >= 2 it always
        // returns the first and last index of whatever array it is handed. Handing it the
        // mandatory-removed complement therefore forced the two filler mics onto
        // `remaining[0]` and `remaining[last]` — the immediate NEIGHBOURS of the two extremes
        // that were just reserved — clustering the whole budget into two pairs at the ends with
        // a hole in the middle. Measured on the 50×30 room, 4 ft-wide rectangle/racetrack at
        // Poor (6 candidates 1.34 m apart, cap 4): it picked indices [0, 1, 4, 5], i.e. gaps of
        // 1.34 / 4.02 / 1.34 m — a 4.02 m hole between two mics whose coverage spans only
        // 1.0 m each, leaving ~2 m of table (and the chairs on it) unmiked. An even sample over
        // the full list picks [0, 2, 3, 5] → gaps 2.68 / 1.34 / 2.68.
        //
        // That same inclusive-endpoint property is what makes the extremes safe to drop from the
        // selection step: an even sample over the full list already contains index 0 and
        // `lastIdx`, so the near-bar and far-end slots are still guaranteed. `mandatory` is only
        // consulted below for an `_isFarEnd` tile that landed INTERIOR (possible on the bullet,
        // where the boundary pull-in can tag a tile that is not the arc extreme); a flat path's
        // single chain always tags `lastIdx`, which the sample holds already.
        const evenPick = sampleEvenlyIndices(merged.length, cap);
        const picked = new Set(evenPick);
        for (const m of mandatory) {
          if (picked.has(m)) continue;
          // Evict the picked index closest to `m` that is not itself an extreme, so guaranteeing
          // an interior far-end tag costs an interior mic rather than collapsing an endpoint.
          const evictable = [...picked].filter(i => i !== 0 && i !== lastIdx);
          if (evictable.length === 0) break;
          const evict = evictable.reduce((best, i) => (Math.abs(i - m) < Math.abs(best - m) ? i : best));
          picked.delete(evict);
          picked.add(m);
        }
        capped = [...picked].sort((a, b) => a - b).map(idx => merged[idx]);
      }
    } else {
      // Two-armed shapes: split into each arm's OWN points, sourced from the PRE-merge `placed`
      // array — NOT `merged` — sorted near-bar → far, i.e. by arc position.
      //
      // Why pre-merge: `mergeConverging`'s nested re-scan chains together any tiles within
      // `mergeDistM` of EACH OTHER, including same-chain neighbours, then re-checks the merged
      // (shifted) point against everything else. A dense tiling (weak-acoustics tables generate
      // many closely-spaced candidates near a U-shape's corner) can bridge a whole series of
      // sub-threshold gaps into one cumulative collapse spanning a couple of METRES — even though
      // the two arms' actual endpoints were never genuinely close enough to justify sharing a
      // single mic. That cascade consumes one of the two arms' cap-budget picks before the split
      // below ever runs, so a wide U-shape ends up "spending" a slot on a merge it didn't need
      // (fewer total mics than a sparser tiling of the exact same shape, purely from how many
      // intermediate tiles happened to bridge the gap — see the Fair-vs-Poor qty mismatch this
      // fixes). Selecting each arm's own near+far extremes FIRST, from the full unmerged pool,
      // then merging only the small selected result (below) still correctly collapses a TRUE tight
      // convergence (e.g. a narrow keystone's apex) into one shared mic, but no longer wastes a
      // slot on a merge that was only an artifact of over-dense pre-cap tiling.
      //
      // `_arcPos` measures distance from tExit in the +dir direction — meaningful as "near-bar →
      // far" ordering for chain A (which tiles forward FROM tExit), but INVERTED for chain B
      // (which tiles backward FROM tEntry): chain B's near-bar point has the LARGEST _arcPos
      // (closest to tEntry) and its apex-side point has the SMALLEST. Sorting both chains
      // ascending by _arcPos therefore puts chain A's near-bar point at index 0 but chain B's
      // APEX-side point at index 0 — sampleEvenlyIndices then hands chain B's "near-bar" budget
      // slot to an apex-side mic instead, bunching mics near the apex on one side while the
      // other arm's own near-bar stretch goes unmiked. Sort chain B DESCENDING so index 0 is
      // consistently "near-bar" and the last index is consistently "apex-side" for BOTH chains.
      const pureA = placed.filter(m => m._chain === 'A').sort((a, b) => a._arcPos - b._arcPos);
      const pureB = placed.filter(m => m._chain === 'B').sort((a, b) => b._arcPos - a._arcPos);
      // Any point already tagged 'AB' by the tight initial DEDUPE (a true, near-exact coincidence
      // between the two chains — e.g. a narrow keystone's literal apex tip) is excluded from both
      // arms' pools above. It does NOT get a pre-reserved cap slot of its own: each arm's own
      // remaining "far" candidate already sits close to that same convergence point, so giving
      // each arm its full, even share of the cap (near-bar + own far/near-apex pick) reaches the
      // far end from BOTH sides — covering the WHOLE arm — instead of reserving one slot for a
      // single point at the exact tip and leaving each arm only one (necessarily mid-arm) pick.
      //
      // This is what the earlier "reserve 1 slot for the shared point, floor-split the rest"
      // version got wrong: it treated that reserved slot as mandatory even when the two arms had
      // plenty of their OWN distinct near-corner candidates to spend the full cap on instead —
      // wasting a slot (e.g. cap=4 → only 3 mics used) and leaving each arm with just one
      // centred mic that can't reach both its own near-bar and far ends, opening large coverage
      // gaps along the arm's length. Splitting the FULL cap evenly between the arms' own pools
      // uses every slot and reaches both ends of each arm.
      //
      // The post-selection `mergeConverging` call below still collapses the two arms' final picks
      // back into one mic if they land close enough together — so a TRUE tight convergence (a
      // narrow keystone apex) still correctly ends up as one shared mic (3 total, symmetric); a
      // WIDE convergence (a U-shape's corner) keeps both arms' far picks distinct, using the full
      // cap for better coverage.
      const equalShare = Math.floor(cap / 2);
      let budgetA = Math.min(pureA.length, equalShare);
      let budgetB = Math.min(pureB.length, equalShare);
      // Reclaim unused capacity ONLY when an arm's own candidate pool is the limiter (a genuinely
      // shorter/asymmetric arm) — hand its slack to the other arm so lopsided tables don't
      // under-fill. This must NOT fire when both arms simply have plenty of candidates and are
      // already at their intended equal share.
      const slack = (equalShare - budgetA) + (equalShare - budgetB);
      if (slack > 0) {
        if (pureA.length > budgetA) budgetA = Math.min(pureA.length, budgetA + slack);
        else if (pureB.length > budgetB) budgetB = Math.min(pureB.length, budgetB + slack);
      }
      const pickA = sampleEvenlyIndices(pureA.length, budgetA).map(i => pureA[i]);
      const pickB = sampleEvenlyIndices(pureB.length, budgetB).map(i => pureB[i]);
      // Merge the SELECTED subset (not the full pool) — collapses a genuine tight convergence
      // (narrow keystone apex) into one shared mic, but leaves a wide corner's independently-
      // chosen far picks distinct since they're no longer bridged by intermediate tiles that were
      // never part of the final selection.
      capped = mergeConverging([...pickA, ...pickB]);
    }
  }

  // Strip internal tags; sort for stable, position-ordered output.
  const coords = capped
    .map(({ _arcPos, _isFarEnd, _t, _chain, ...mic }) => mic)
    .sort((a, b) => (a.location.x - b.location.x) || (a.location.y - b.location.y));

  // U-shape design tweak: for SHORT U-shapes, the tiled far mics land in the middle of the U's
  // base (right edge of the outer rectangle) — inside the empty opening. Pull each far mic to its
  // arm's near mic (matched by y) + 2·radius so their coverage just touches and both mics sit on
  // the same arm's outer edge.
  //
  // Skip this tweak when the tiled far mic is ALREADY on an arm. For LONG U-shapes
  // (e.g. 25'-length with modest bar coverage), the tiling anchors the far mic on the top or
  // bottom edge — already on-arm and inside the table. Applying `near.x + 2r` to a mic whose
  // near-mate sits far from the bar (past bar coverage boundary) pushes the far mic OFF the
  // table's far edge. Detect "already on arm" by y-proximity to a near mic (both would sit on
  // the same outer edge).
  if (shape === 'ushape' && coords.length >= 4) {
    const twoR = 2 * (micCoverageRadius / scale);
    const armProximityM = micCoverageRadius / scale; // ~1 mic radius in metres
    const byX = [...coords].sort((a, b) => a.location.x - b.location.x);
    const near = byX.slice(0, 2);          // nearer the bar (smaller x)
    const far  = byX.slice(-2);            // toward the far end (larger x)
    // Decide "already on arm" for BOTH far mics TOGETHER, not independently. A symmetric
    // U-shape's two far mics sit at almost — but, from floating-point noise in the upstream
    // tiling, not always EXACTLY — equal distance from their own arm's row. Checking each mic's
    // gap against `armProximityM` independently lets one side's gap fall just under the
    // threshold while the mirror-image other side's nearly-identical gap falls just over it —
    // sliding only ONE far mic onto its row and leaving the other on the corner segment, a
    // visibly lopsided result for what should be a symmetric table. Only skip the slide-back
    // for a far mic when its MATE is also being skipped, so a symmetric shape's two far mics are
    // always treated the same way (both slide, or both stay put).
    const withMate = far.map((f) => {
      const mate = Math.abs(near[0].location.y - f.location.y) <= Math.abs(near[1].location.y - f.location.y)
        ? near[0] : near[1];
      return { f, mate, gap: Math.abs(f.location.y - mate.location.y) };
    });
    const bothAlreadyOnArm = withMate.every(({ gap }) => gap < armProximityM);
    if (!bothAlreadyOnArm) {
      withMate.forEach(({ f, mate }) => {
        f.location.x = mate.location.x + twoR;
        f.location.y = mate.location.y;
      });
    }
    coords.sort((a, b) => (a.location.x - b.location.x) || (a.location.y - b.location.y));
  }

  // NB: the U-shape arm-centerline shift lives in calculateDevicesMiddleware's
  // computeTableExpansionViaPath so it applies to BOTH scenario 3 (reduced-arc,
  // via this function) AND scenarios 1/2 (standalone full-path placement).

  // `requestedQty` (pre-cap candidate count) vs `qty` (post-cap, what's actually returned) — kept
  // for callers that want to know how much maxExpansionMics trimmed the geometrically-ideal
  // count. No toast is surfaced for this trim (removed per product request).
  return { barReach, scenario, inCount, tExit, tEntry, covFrac, arcFrac, qty: coords.length, requestedQty: merged.length, coords };

};

// Exported so the middleware's proximity warning can reuse the same 0.86 m feedback threshold.
export const MIN_MIC_SPKR_DISTANCE_M = 0.86;

/**
 * Minimum centre-to-centre separation between two auto-placed expansion devices on the same axis,
 * as a fraction of the device's own coverage RADIUS. Applied by `calcBarExpansionPlacement`'s
 * 'centered' branch (see `collapseToMinSeparation` there).
 *
 * Why a fraction of radius and not an absolute distance: the whole point is "are these two devices
 * so close that the second one is buying nothing?", and that question scales with the coverage
 * circle. 0.4 × radius means the pair's circles overlap by ≥ 80% of a diameter before we call the
 * second one redundant — deliberately permissive, because dropping a device that IS pulling its
 * weight costs far more coverage than keeping a somewhat-redundant one (measured: dropping the
 * legitimately-tight 40' × 30' Good/full_room x-pair takes coverage from 86.3% to 61.6%).
 *
 * Calibration, for anyone tempted to raise it: the tightest spacing this must NOT collapse is the
 * 50' × 30' Great/full_room grid pinned by `barExpansionCeilingMicEvenSpread` 8.25 (qty 4), whose
 * x-pair sits at 0.463 × radius. There is only 0.063 of headroom above 0.4, so raising this ratio
 * without re-deriving that expectation will change a pinned count.
 */
export const MIN_SPREAD_SEPARATION_RATIO = 0.4;

/**
 * Resolves the residual coverage box for bar-expansion ceiling/pendant speaker placement, or
 * `null` when no expansion speakers are warranted:
 *
 *  - Case A: the bar's own SPL-decay reach already covers the whole base coverage box — no
 *    residual area exists, so no expansion speakers are needed at all.
 *  - Case B: the residual band beyond the bar's reach is narrower than one speaker's coverage
 *    diameter (`radius * sqrt(2)`) — too shallow for meaningful additional coverage. Suppresses
 *    only the AUTOMATIC recommendation; an explicit manual "+ Add" (`manualAdd`) bypasses it.
 *
 * Shared by the placement pass (`computeExpansionSpeakerCoords` in roomCalculations.ts) and the
 * recommendation-dialog trigger (`calculateBarSpeakerCoverageGap` below) so they can never
 * disagree about when zero expansion speakers would actually be placed.
 *
 * Deliberately duplicates (rather than calls into) the Case A/B logic inside
 * `calcBarExpansionPlacement` — that function's anchored-grid math is retired for speakers (the
 * shared `calculateSpeakerPlacement` core now handles the grid + shape dispatch), but
 * `calcBarExpansionPlacement` is still the live implementation for the expansion CEILING MIC path
 * (`calcExpansionCeilingMicPlacement`), so it's left untouched rather than risking its mic
 * behaviour to share a ~15-line box calculation.
 *
 * @param {Object} params
 * @param {Object} params.room       - { left: number } room left wall position (meters)
 * @param {Object} params.baseBox    - { left, length, width, spkrAdj } available speaker area
 * @param {boolean} params.isFocused - true for focused coverage, false for full-room. Currently
 *                                     unused by the box math itself (see note below) but kept in
 *                                     the signature since every caller already passes it and it
 *                                     may matter again if a future revision needs to vary the box
 *                                     by coverage mode.
 * @param {number} params.radius     - effective coverage radius of the expansion speaker
 * @param {number} params.refDistance - listener distance from bar wall (meters)
 * @param {boolean} [params.manualAdd=false] - bypass the Case B suppression
 * @returns {Object|null} the residual coverage box, or null if no expansion speakers are warranted
 */
export const resolveExpansionSpeakerBox = ({
  room,
  baseBox,
  isFocused,
  radius,
  refDistance,
  manualAdd = false,
}) => {
  const barReach = refDistance * Math.pow(10, constants.barSpeakerDecayThreshold / 20);
  const barCoverageDepth = room.left + barReach;

  // Case A: bar covers the entire expansion area.
  if (barCoverageDepth >= baseBox.left + baseBox.length) {
    return null;
  }

  // Box starts exactly at the bar's own coverage reach — no pull-back subtracted. An earlier
  // version pulled the box back by radius × targetOverlap (up to a full coverage diameter),
  // inherited from the old anchored-grid algorithm where that pulled-back number was ONLY ever
  // used for the Case B feasibility check below, never to position a speaker — the anchored
  // grid's actual first speaker was independently anchored at barCoverageDepth (+ radius for
  // ceiling models), bypassing this box entirely. Reusing that oversized number as the literal box
  // boundary for the new centered-grid core collapsed the box back to baseBox in most rooms
  // (confirmed via the debug overlay: PIG task "Fix expansion speaker coverage box start
  // position"). With a CENTERED grid the first speaker sits half a grid-spacing in from this edge
  // and its own coverage radius already reaches back to meet the bar's edge, so no extra margin
  // is needed here.
  const expansionLeft = Math.max(baseBox.left, barCoverageDepth);
  const expansionBox = { ...baseBox, left: expansionLeft, length: (baseBox.left + baseBox.length) - expansionLeft };

  // Case B: expansion box too narrow for meaningful coverage beyond the bar.
  const minResidualDepth = radius * Math.SQRT2;
  if (!manualAdd && expansionBox.length < minResidualDepth) {
    return null;
  }

  return expansionBox;
};

/*
 * REMOVED 2026-08-13: `resolveExpansionMicBox`.
 *
 * It was a SECOND implementation of the expansion ceiling mic box (spec Steps 2–5), used only by
 * `computeExpansionCeilingMicCoords` in roomCalculations.ts while the recommendation dialog and
 * the MuiExpansionBox visualiser used `calcExpansionCeilingMicPlacement`. The two had drifted:
 * this one omitted the Step 4 overlap pull-back entirely and relaxed the Step 5 threshold from
 * `radius × √2` to `radius`, so the box that got mics and the box that got drawn/recommended were
 * different boxes. roomCalculations.ts now delegates to `calcExpansionCeilingMicPlacement` like
 * everything else. See Documentation/BAR_EXPANSION_CEILING_MIC_CHANGES_VS_SPEC.md §A1/§D1.
 */

/**
 * Resolves the focused/full_room mode used for expansion ceiling mic geometry.
 *
 * Source of truth: `area.expansionMicCoverage` when set (reflects the user's toggle and
 * the reducer's table-aware default), otherwise the spec default (table present → focused,
 * no table → full_room). The bar's own `microphones.coverage` is intentionally NOT consulted:
 * it's forced to focused for bars regardless of room state and does not represent the
 * expansion mic's mode. Shared by `calculateBarCoverageGap`, the placement pass in
 * `roomCalculations.ts`, and the `MuiExpansionBox` visualizer so they can't disagree.
 */
export const getExpansionCeilingIsFocused = (area) => {
  return area?.expansionMicCoverage
    ? !!area.expansionMicCoverage.focused
    : Boolean(area?.table?.shape);
};

/**
 * Speaker counterpart of `getExpansionCeilingIsFocused`: resolves the focused/full_room mode for
 * expansion SPEAKER geometry (both ceiling and pendant expansion speakers use the toggle).
 *
 * Source of truth: `area.expansionSpeakerCoverage` when set (the user's toggle + the reducer's
 * table-aware seed), otherwise the table default (table → focused, no table → full_room). The bar's
 * own `speakers.coverage` is NOT consulted — for a bar the primary speaker is the bar itself, which
 * has no coverage toggle. Shared by the placement pass in `roomCalculations.ts` and the PDF report
 * so they can't disagree.
 */
export const getExpansionSpeakerIsFocused = (area) => {
  return area?.expansionSpeakerCoverage
    ? !!area.expansionSpeakerCoverage.focused
    : Boolean(area?.table?.shape);
};

/**
 * Returns true when a non-CBC bar leaves a SPEAKER coverage gap that warrants
 * expansion ceiling speakers (per WDT-136). Delegates to `resolveExpansionSpeakerBox`
 * so the dialog trigger uses the same edge-case logic (bar SPL decay reach,
 * expansion box derivation, minimum residual depth) as the placement pass — preventing
 * the dialog from prompting in cases where zero expansion speakers would actually be placed.
 *
 * Inputs mirror the bar speaker calc inside `roomCalculations.ts`:
 *   - `coverageBoxes`: the focused / full_room base boxes
 *   - `area.expansionMicCoverage`: drives focused vs full-room mode for the speaker calc
 *     (kept consistent with the expansion ceiling mic mode resolver)
 *   - `area.table`: drives `refDistance` (distance from bar wall to nearest listener)
 *   - `expSpkrRadius`, `expSpkrModel`: the candidate expansion speaker's effective radius
 *     and equipment key (defaults to ceiling speaker, the WDT-87 default)
 */
export const calculateBarSpeakerCoverageGap = (area, coverageBoxes, constants) => {
  const { BAR, CBC } = constants.equipment.micCategories;
  const micModel = area.microphones?.model;
  if (!micModel || !BAR.has(micModel) || CBC.has(micModel)) return false;

  const isFocused = getExpansionCeilingIsFocused(area);
  const baseBox = isFocused ? coverageBoxes?.focused : coverageBoxes?.full_room;
  if (!baseBox) return false;

  // Default expansion type for the pre-calc is 'ceiling' per WDT-136 — the recommendation
  // dialog shows users the ceiling option first; pendant is offered later via the menu.
  const expSpkrModel = 'ceilingspkr';
  const expSpkrEquipment = constants.equipment.speakers[expSpkrModel];
  if (!expSpkrEquipment) return false;

  // Compute the candidate speaker's effective radius the same way roomCalculations does:
  //   (speakerHeight − talkerHeight) × tan(coverageAngle × π/180)
  // Speaker height defaults to ceiling height; talker height comes from constants.
  const ceilingHeight = area.ceilingHeight?.min ?? area.ceilingHeight ?? 2.8956;
  const talkerHeight = constants.area?.talkerHeight ?? 1.2009;
  const coverageAngle = expSpkrEquipment.coverageAngle ?? 39;
  const expSpkrRadius = (ceilingHeight - talkerHeight) * Math.tan((coverageAngle * Math.PI) / 180);
  if (!Number.isFinite(expSpkrRadius) || expSpkrRadius <= 0) return false;

  // refDistance — same formula as `roomCalculations.ts`: distance from the bar wall to the
  // nearest listener (spec Step 1a). When a table is present we use its near-edge position
  // (table.location.left - room.left); per spec there is NO 1.524 m floor in that case —
  // barSpeakerNoTableRefDistance is the no-table default only. A small epsilon guards
  // against degenerate geometry when the table abuts the bar wall.
  const hasTable = Boolean(area.table?.shape);
  const refDistance = hasTable
    ? Math.max(
        0.01,
        ((area.dimensions?.length ?? 0) - (area.table?.size?.length ?? 0)) / 2
          + (area.table?.transform?.x ?? 0),
      )
    : constants.barSpeakerNoTableRefDistance;

  // Same Case A/B box logic used by the actual placement pass (resolveExpansionSpeakerBox), so
  // this dialog trigger can't disagree with what the placement pass would actually place.
  const expansionBox = resolveExpansionSpeakerBox({
    room: { left: area.location.left },
    baseBox,
    isFocused,
    radius: expSpkrRadius,
    refDistance,
  });
  return expansionBox !== null;
};

/**
 * Returns true when a non-CBC bar mic leaves a coverage gap that warrants an expansion ceiling mic.
 * Delegates to calcExpansionCeilingMicPlacement so the dialog trigger uses the same Steps 1, 2, 4, 5
 * (bar reach, full-coverage check, expansion box, minimum residual depth) as the placement pass —
 * preventing the dialog from prompting in cases where zero mics would actually be placed.
 */
export const calculateBarCoverageGap = (area, coverageBoxes, constants) => {
  const { BAR, CBC } = constants.equipment.micCategories;
  const model = area.microphones?.model;
  if (!model || !BAR.has(model) || CBC.has(model)) return false;

  const acousticIndex = constants.acoustics.indexOf(area.acoustics);
  if (acousticIndex === -1) return false;

  const isFocused = getExpansionCeilingIsFocused(area);
  const baseBox = isFocused ? coverageBoxes?.focused : coverageBoxes?.full_room;
  if (!baseBox) return false;

  // Ceiling expansion gap: would a DCM-X ceiling expansion mic be placed beyond the bar?
  // The recommendation is CEILING-BASIS ONLY — the dialog's "Add" adds the default ceiling
  // expansion, so the prompt must mirror exactly what that ceiling placement would do (same bar
  // reach, full-coverage check, expansion box, and minimum-residual-depth edge case). If the
  // ceiling placement would place 0 mics, do not prompt. (Tabletop expansion is reached via the
  // expansion-mic-type dropdown, not this auto-recommendation.)
  // Runs before `expansionMicrophones` exists (deciding whether to recommend it in the first
  // place), so there's no expansion-mic height yet to read — fall back to raw ceiling height,
  // matching the default the expansion mic will actually be created with. NOT
  // `area.microphones.height` — the primary here is always a non-CBC bar (table/wall mounted),
  // so its height has nothing to do with the ceiling mount.
  const result = calcExpansionCeilingMicPlacement({
    room: { left: area.location.left },
    baseBox,
    acousticIndex,
    isFocused,
    barModel: model,
    ceilingHeight: area.ceilingHeight?.min ?? area.ceilingHeight,
  });
  return !!result && result.qty !== 0;
};
